// The 3D table: draws the mat, cards, piles and tokens, and turns mouse
// input into actions (drag, tap, draw, right-click menus).
//
// Everything here works in "view" coordinates: your seat is always at +z.
// For the player in seat 2 the whole table is turned around.
import * as THREE from 'three';
import { pinchable } from '../touch.ts';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { CONFIG } from '../../config.ts';
import { countsAgainstHealth, darkgaleMultiplier, handOf, onCard, seatPoint, sideOf, teamOf, tokensOnCard, playWindowRefusal, slotAt, slotKind, type Slot, slotRefusal, slotsOpenTo, triggerPayout, type Placed, type Action, type CardView, type FxEvent, type GameView, type PileName, type Seat, type Token, type Where } from '../../shared/engine.ts';
import type { TokenDef } from '../../shared/tokens.ts';
import { CARD_DB, TOKEN_DB } from '../data.ts';
import { formOf, formWords, isEmperor, roman, tieredDamage, tieredRemoval, type CardDef } from '../../shared/cards.ts';
import { describeFormula, priceOf } from '../../shared/costs.ts';
import { barSrc, cardBackSrc, cardFrontSrc, dieColor, drawDieFace, loadImageEl, matSrc, tokenSrc } from '../images.ts';
import { CameraRig } from './cameraRig.ts';
import { AttackFx } from './attackFx.ts';
import { BEAM_FADE, Effects, OrbitAura, SACRIFICE_BURST, fxTextures } from './effects.ts';
/**
 * Undead Legion's Spirit Soldier rising out of its fallen Undead Soldier (seconds, cm): it waits for the shards to
 * burst (`after`), rises small (`up`, to `height`, at `small` of full size), hangs a moment (`hang`), then floats back
 * down into its lane, growing to full size (`down`).
 */
const SPIRIT_RISE = { after: SACRIFICE_BURST, up: 0.9, hang: 0.35, down: 1.15, height: 7, small: 0.35 };
/** …and fading away when its General falls: it shrinks as it drifts up and off (seconds, cm). */
const SPIRIT_AWAY = { seconds: 1.8, height: 9 };
/** Glace Glass: how long the sheen wipes over the discard pile before the card lifts off it (seconds). */
const PILE_SHEEN = 0.9;
import { FieldFog } from './fieldFog.ts';
import { TimerClocks } from './timerClock.ts';
import { PileLooks } from './pileLook.ts';
import { chosenBackground } from '../prefs.ts';
import { LinkLines, TokenFx } from './tokenFx.ts';
import { TOKEN_RECIPES } from './tokenRecipes.ts';
import { Markers } from './markers.ts';
import { AxeMarks } from './axeMarks.ts';
import { SpellScenes } from './spellScenes.ts';
import { cardBlock, cardFlat, cardPlane } from './cardShape.ts';

/** How far outside a card's edge the red outline of a destroyed card reaches, glow included (cm). */
const OUTLINE_PAD = 0.9;

/** Seconds a card takes to fly from one pile to the other. */
const PILE_FLIGHT = 0.75;
const DECK_OUT_FLIGHT = 0.9; // seconds Deck-Out's mote takes from the empty deck to its Emperor

export interface TableCallbacks {
  send(action: Action): void;
  cardMenu(card: CardView, clientX: number, clientY: number, expired: boolean): void;
  /**
   * A card on the table was left-clicked. Returns true if that opened something of its own (a Biome's
   * "pay to remove", a run-down Tome's "pay to restart"), false for the usual click (tap it).
   */
  cardAct?(card: CardView, expired: boolean, clientX: number, clientY: number): boolean;
  /**
   * One of your units was left-clicked (a card the table lists in view.combat): start its attack, or ask
   * whether it attacks or uses its Tap ability. Returns false if there was nothing to offer.
   */
  unitClick?(card: CardView, clientX: number, clientY: number): boolean;
  /**
   * A Biome is being played where another already lies: the old one's removal cost has to be paid first.
   * Resolves to true if the player agrees to pay it.
   */
  /**
   * A Lock or a turn timer was just put on an Emperor by this player: which ability is it for?
   * (kind 'lock' = it locks that ability; 'cooldown' = it puts that ability on cooldown.)
   */
  askAbility?(tokenId: string, card: CardView, kind: 'lock' | 'cooldown', clientX: number, clientY: number): void;
  /** where on the screen the middle of your hand bar is (cards drawn fly there) */
  handPoint?(): { x: number; y: number } | null;
  askReplace?(old: CardView, expired: boolean, def: CardDef, clientX: number, clientY: number, how: { free: boolean; dark: boolean }): Promise<boolean>;
  tokenMenu(token: Token, clientX: number, clientY: number): void;
  pileMenu(seat: Seat, pile: PileName, clientX: number, clientY: number): void;
  askWhere(pile: PileName, clientX: number, clientY: number): Promise<Where | null>;
  /**
   * A card whose cost is settled as it is played (two costs to choose from, a number to give, a Unit to
   * pick): ask the player. Resolves to what they chose, or null if they backed out.
   */
  askCost(cardId: string, def: CardDef, clientX: number, clientY: number, how: { free: boolean; dark: boolean; minion?: boolean }): Promise<{ form?: number; choice?: number; unit?: string; spread?: boolean } | null>;
  /** your own deck was clicked (draw, or the Overdraw / Deck-Out choices) */
  deckClick(clientX: number, clientY: number): void;
  /** your own discard pile was right-clicked (its top card: to your hand, or straight to your deck) */
  discardClick(clientX: number, clientY: number): void;
  /** the label over your opponent's hand was clicked while a card keeps both hands in plain view */
  showOpenHand?(seat?: Seat): void;
  /** the small chip floating over a card was clicked (a face-up Trigger's payout) */
  cardChip(card: CardView, clientX: number, clientY: number): void;
  /** something the player should be told in words ("Burn on Card 9: roll to cure it…") */
  notice(text: string): void;
  /** A player showed everyone one of their cards (hold R and click it): `seat` did it, `num` is the card. */
  revealed?(seat: Seat, num: number, text: string): void;
  /** The Remaining is flying to this player's own hand (the Calling Pillars called it): the hand bar glows red. */
  remainingToHand?(): void;
  isOverHand(clientX: number, clientY: number): boolean;
  setHandHighlight(on: boolean): void;
  /** a card in your hand is selected, and the table was clicked: place it there */
  placeClick?(clientX: number, clientY: number, shift: boolean, free: boolean): void;
  /** …and the mouse is moving over the table with that card selected */
  placeHover?(clientX: number, clientY: number, shift: boolean, free: boolean): void;
  /** …or something other than the board was clicked (a pile, the room around the table): let go of the card */
  placeCancel?(): void;
}

type Pick =
  | { kind: 'card'; id: string }
  | { kind: 'token'; id: string }
  | { kind: 'pile'; seat: Seat; pile: PileName };

interface CardObj {
  data: CardView;
  root: THREE.Group;
  flipper: THREE.Group;
  frontMat: THREE.MeshLambertMaterial;
  backMat: THREE.MeshLambertMaterial;
  num: number | null | undefined;
  form?: number; // the form its face shows (a two-cost card with an art for each: Forgotten Monarch)
  darkFace?: boolean; // shown with the dark red cost glow: it was summoned with Dark Gerzite (Darkgale)
  cloneTint?: boolean; // tinted light blue: it is a clone
  x: number;
  z: number;
  y: number;
  yaw: number;
  dragging: boolean;
  under: THREE.Mesh[]; // the merged copies peeking out from underneath a tiered card
  aura: OrbitAura | null; // discard-pile aura, for cards tagged discard_aura
  flying: boolean; // movement = flying: hovers above the table
  hover: number; // 0 = on the table, 1 = fully up in the air
  phase: number; // so flying cards don't all bob in step
  shade: THREE.Mesh | null; // soft shadow under a flying card
  tokenFly: boolean; // a Flying token is on it
  tokenGround: boolean; // a Ground token is on it
  passiveNext: number; // when its next Passive pulse is due (0 = not counting)
  fxUntil: number; // when the one-off effects playing around this card are over (seconds, the frame clock)
  fxQueue: string[]; // effects waiting their turn: each starts once the one before it has finished
  armedNext: number; // a face-down Trigger: when the next round of its standing beam starts (0 = no beam)
  ambientNext: number; // an active Biome or Tome: when its next little affiliation scene plays (0 = not counting)
  ambientUntil: number; // …and when the one playing now is over (it doesn't hold back the next scene, only a Passive pulse)
  expired: boolean; // a Destroyed token is on it: its turns are over, so it shows no aura
  wasExpired: boolean | null; // `expired` as of the last frame (null = not looked at yet), to catch the moment its turns run out
  slam: { t0: number; y0: number; rise: number; hit: boolean } | null;
  guardNext?: number; // a Troop a Grave Intercessor has chosen: when its ward plays again
  laneNext?: number; // a Spell with a scene over each of its lanes (CONFIG.looks.laneScenes): when the next round starts
  bellNext?: number; // The River Keeper: when the light next runs out to its Bell tokens (CONFIG.looks.bellEvery)
  spiritRise?: { t0: number }; // a Spirit Soldier rising out of its fallen Undead Soldier's shards, then settling into its lane (SPIRIT_RISE)
  emerge?: { t0: number; look?: 'sand' | 'water'; waiting?: boolean }; // rising up out of the table (CONFIG.combat.emerge) // an Entrance in progress: the card is dropping onto the table
  damage: number; // everything it has lost: the damage counters on it, plus (a Building) its SP decay
  decay: number; // …the part of that which is SP decay
  overheal: number; // the Overheal on it
  gained: number; // the health it has gained (Health counters on it): its full health is that much higher
  doom: { group: THREE.Group; ring: THREE.MeshBasicMaterial; shade: THREE.Mesh } | null; // the "destroyed" look: cracks and a red outline
  passiveOff: boolean; // a status that stops Passives is on it (CONFIG.passiveStoppedBy), a Lock it put on itself included: its Passive pulse turns deep red
  quiet: boolean; // a Frozen, Stunned, Comatose, Entrenched or Locked token is on it: its own effects stay off
}

interface TokenArt {
  img: HTMLImageElement | null;
  alpha: Uint8ClampedArray | null; // 64x64 transparency map, for clicking only the visible shape
}

interface TokenObj {
  data: Token;
  flight?: { from: THREE.Vector3; t0: number; dur: number; trail: string; last: number }; // Gerzite flying to the bar from a card (Gerzite Mine), trailing light
  def: TokenDef | undefined;
  shape: 'round' | 'cutout';
  mesh: THREE.Object3D; // what moves around
  face: THREE.Mesh; // the picture; also what the mouse hits
  shadow: THREE.Mesh | null; // cutouts get a shadow in their own shape
  counter: { canvas: HTMLCanvasElement; tex: THREE.CanvasTexture; value: number } | null;
  art: TokenArt | null;
  height: number;
  x: number;
  z: number;
  y: number;
  dragging: boolean;
  offTable: boolean; // being dragged past the table edge: letting go deletes it
  fx: TokenFx | null; // its ambient effect (burn, frozen, halo…)
  on: CardObj | null; // the card it is sitting on right now
  die: DieState | null; // set for six-sided dice
  wait: number; // hidden until this time (Dark Gerzite waiting for a sacrificed card to burst); 0 = not waiting
  pulse: number; // when its effect last happened (it swells for a moment); 0 = never
  // the little clock resting on a counter that also counts turns (Weakness 2 for 3 turns); null = no clock
  clock: { mesh: THREE.Mesh; canvas: HTMLCanvasElement; tex: THREE.CanvasTexture; shown: number } | null;
  // the little wear-off badge on a counter part of whose number is temporary (Thorns 3, 2 of it wearing off)
  drainBadge: { mesh: THREE.Mesh; canvas: HTMLCanvasElement; tex: THREE.CanvasTexture; shown: number } | null;
}

interface DieState {
  cube: THREE.Mesh;
  shown: number; // the face that is up
  yaw: number; // how the die is turned on the table (just for looks)
  target: THREE.Quaternion; // the resting position that shows that face
  /** a roll in progress: the die hops from where it was to where it lands, tumbling */
  anim: { t0: number; dur: number; from: THREE.Vector3; axis: THREE.Vector3; turns: number; height: number } | null;
  // a cure die (Burn) fades away by itself once its roll has been seen: when it starts to (0 = it doesn't), and
  // whether it is gone from sight (it comes back the next time it is thrown)
  fadeAt: number;
  faded: boolean;
}

// Which number is on each side of the cube (order: right, left, top, bottom, front, back).
// Opposite sides add up to 7, like a real die.
const DIE_SIDES = [3, 4, 1, 6, 2, 5];
const DIE_TURN: Record<number, [number, number, number]> = {
  1: [0, 0, 0],
  6: [Math.PI, 0, 0],
  2: [-Math.PI / 2, 0, 0],
  5: [Math.PI / 2, 0, 0],
  3: [0, 0, Math.PI / 2],
  4: [0, 0, -Math.PI / 2],
};
const UP = new THREE.Vector3(0, 1, 0);
const LINK_TRAVEL = 0.45; // seconds the pulse takes to run between an Emperor and its Minion
const CHARGE_SECONDS = 1.1; // Gerzite flying off the bar into Gerzite Dagger / Crystalline Discharge
const GUST_TRAVEL = 1.1; // seconds Summit Tarragon's wind and snow take to blow from it to the card played for less
const LOOK_TRAVEL = 0.55; // seconds a card's own light takes to run from one card to another (Fleshstealer's blood, Occult Sorcerer's beam)
/** How to turn the cube so `value` faces up, then spin it by `yaw` on the table. */
function dieQuat(value: number, yaw: number) {
  const [x, y, z] = DIE_TURN[value] ?? DIE_TURN[1];
  const face = new THREE.Quaternion().setFromEuler(new THREE.Euler(x, y, z));
  return new THREE.Quaternion().setFromAxisAngle(UP, yaw).multiply(face);
}

/** How many cards one copy of the pile-edge stripes covers (one row each). */
const PILE_EDGE_ROWS = 64;
let pileEdge: THREE.CanvasTexture | null = null;
/**
 * The side of a deck or discard pile: near-black card edges, each card a slightly different dark grey,
 * with a lighter grey line here and there, so the stack reads as many thin cards with depth.
 */
function pileEdgeTexture() {
  if (pileEdge) return pileEdge;
  const P = CONFIG.colors.pileEdge;
  const c = document.createElement('canvas');
  c.width = 4;
  c.height = PILE_EDGE_ROWS * 4;
  const ctx = c.getContext('2d')!;
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  for (let row = 0; row < PILE_EDGE_ROWS; row++) {
    const r = rnd();
    // each card: its edge, then a hairline gap to the next card
    ctx.fillStyle = r < 0.14 ? P.light : r < 0.5 ? P.mid : P.base;
    ctx.fillRect(0, row * 4, 4, 3);
    ctx.fillStyle = P.gap;
    ctx.fillRect(0, row * 4 + 3, 4, 1);
  }
  pileEdge = new THREE.CanvasTexture(c);
  pileEdge.wrapS = pileEdge.wrapT = THREE.RepeatWrapping;
  pileEdge.colorSpace = THREE.SRGBColorSpace;
  return pileEdge;
}

interface PileObj {
  seat: Seat;
  pile: PileName;
  group: THREE.Group;
  box: THREE.Mesh;
  edges: THREE.Texture; // the striped sides of the stack (one stripe row per card)
  top: THREE.Mesh;
  topMat: THREE.MeshLambertMaterial;
  topNum: number | null | undefined;
  outline: THREE.LineLoop;
  glow: THREE.Mesh;
  label: HTMLElement;
  shuffles: number;
  shuffleT: number;
  jumps: number; // how many times The Remaining has moved within this pile (when it goes up, the pile shudders in deep red)
  jumpT: number;
  searching: boolean;
  hover: boolean;
  holdUntil: number; // a sacrificed card is about to land here: the pile waits for it to burst before showing it
}

const CW = CONFIG.card.width;
const CH = CONFIG.card.height;
const TOKEN_H = 0.35; // thickness of a round chip
const CLOCK_H = 0.12; // thickness of the little turn clock that rests on a counter
const CURE_LAND = 1.15; // seconds from a cure roll being thrown to the die lying still: its result is told then
const RECALL_SECONDS = 1.6; // Recall Rift: how long its swirl winds up round the card it returns (the recall-swirl look in effects.ts) before the card is gone
const ALPHA_PX = 64;

function cardGeometries() {
  // rounded corners, like a real playing card (CONFIG.card.cornerRadius)
  const front = cardPlane(CW, CH);
  front.rotateX(-Math.PI / 2);
  front.translate(0, 0.012, 0);
  const back = cardPlane(CW, CH);
  back.rotateY(Math.PI);
  back.rotateX(-Math.PI / 2);
  back.translate(0, -0.012, 0);
  return { front, back };
}

export class TableView {
  private renderer: THREE.WebGLRenderer;
  private labels: CSS2DRenderer;
  private scene = new THREE.Scene();
  private rig: CameraRig;
  private raycaster = new THREE.Raycaster();
  private ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private geo = cardGeometries();
  private tokenArt = new Map<string, Promise<TokenArt>>();
  private tokenFaces = new Map<string, THREE.CanvasTexture>();
  private textures = new Map<string, THREE.Texture>();
  private cards = new Map<string, CardObj>();
  private tokens = new Map<string, TokenObj>();
  private piles: PileObj[] = [];
  private oppHands: { seat: Seat; group: THREE.Group; label: HTMLElement; key: string }[] = []; // your opponents' hands, as rows of card backs
  private pickables: THREE.Object3D[] = [];
  private view: GameView | null = null;
  private flip = 1;
  private raf = 0;
  private last = performance.now();
  private resizeObs: ResizeObserver;
  private hovered: Pick | null = null;
  private pointer: {
    id: number;
    button: number;
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    pick: Pick | null;
    mode: 'pending' | 'drag' | 'look' | 'pan' | 'group' | 'marquee' | 'aim';
    alt?: boolean; // Alt was held: a drag points an attack arrow, a click pings
    aimAt?: { card: string | null; x: number; z: number } | null; // where the arrow being drawn points right now
    aimFrom?: string; // the card the arrow starts on
    offset?: THREE.Vector3;
    carry?: { obj: TokenObj; dx: number; dz: number }[];
    trail?: { t: number; x: number; z: number }[]; // recent positions of a dragged die, to tell a throw from a drop
    select?: boolean; // Ctrl/Cmd was held: this press is about selecting, not acting
    group?: { obj: CardObj | TokenObj; dx: number; dz: number; token: boolean; selected: boolean }[];
    groupOff?: boolean; // the selection is being thrown off the table
  } | null = null;
  /** ids of the cards and tokens picked with Ctrl+click or a Ctrl+drag box */
  private selected = new Set<string>();
  private selMarks = new Map<string, THREE.Mesh>();
  // an attack being aimed: the attacker, with a red ring on each card it may hit (the next click picks one)
  private striking: string | null = null;
  // a choice a card effect is waiting for: the cards that may be chosen are ringed, and a click on one answers it
  private picking: { id: number; options: string[] } | null = null;
  private strikeMarks = new Map<string, THREE.Mesh>();
  private linkLines = new LinkLines(this.scene, TOKEN_RECIPES.lanespanner?.color ?? '#5fe0d6');
  private pairLines = new LinkLines(this.scene, TOKEN_RECIPES[CONFIG.tokenRules.linkToken]?.color ?? '#2f9e4f'); // Link counters, pair by pair
  private marqueeEl: HTMLElement | null = null;
  private disposed = false;
  private effects: Effects;
  private markers = new Markers(this.scene);
  private looks = new PileLooks(this.scene); // a player looking at the top cards of a pile
  private attackFx = new AttackFx(this.scene); // what flies from an attacker to its target
  private clocks = new TimerClocks(this.scene); // the little clock over a turn timer as it counts down
  private spellScenes = new SpellScenes(this.scene); // Light Screen's screen over its side, Arcane Dome's dome over its card
  private axes = new AxeMarks(this.scene); // Executioner's axe, hanging over the lane it comes back to
  private fog = new FieldFog(this.scene); // Fog and Smog: a card's owner's half of the mat, clouded over for the other player
  private mood = 0; // 0 = normal, 1 = Overtime (the room glows like embers)
  private rimMat: THREE.MeshLambertMaterial | null = null;
  private emberBands: THREE.Mesh[] = [];
  private host: HTMLElement;
  private cb: TableCallbacks;

  /** true while watching a replay: you can look around and inspect things, but not move or change them */
  private readOnly: boolean;
  private skipFx = false;
  /** The next frame takes note of which cards are spent (a Destroyed token on them) without playing their ending. */
  private settleExpiry = false;

  constructor(host: HTMLElement, cb: TableCallbacks, seat: Seat, readOnly = false) {
    this.host = host;
    this.cb = cb;
    this.readOnly = readOnly;
    this.flip = sideOf(seat); // the far side of the mat (seat 1, and 3 in 2v2) sees the table turned round
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.domElement.className = 'table-canvas';
    host.append(this.renderer.domElement);

    this.labels = new CSS2DRenderer();
    this.labels.domElement.className = 'table-labels';
    host.append(this.labels.domElement);

    this.rig = new CameraRig(1);
    this.effects = new Effects(this.scene, (strength, duration) => this.rig.shake(strength, duration));
    this.buildRoom();

    this.resizeObs = new ResizeObserver(() => this.resize());
    this.resizeObs.observe(host);
    this.resize();
    this.bindInput();
    this.loop();
    // add ?debug to the page address to poke at the table from the browser console
    if (location.search.includes('debug')) (window as unknown as { table: TableView }).table = this;
  }

  /**
   * The spot on the table in the middle of what you are looking at, in the game's own coordinates:
   * new counters and summoned cards appear near it. `bottom` is where the visible table ends on screen
   * (the top of the hand bar). undefined = you are looking past the table (at the horizon).
   */
  lookPoint(bottom?: number) {
    const r = this.renderer.domElement.getBoundingClientRect();
    const low = Math.min(r.bottom, Math.max(r.top + 1, bottom ?? r.bottom));
    const gp = this.groundPoint(r.left + r.width / 2, (r.top + low) / 2);
    return gp ? this.toWorld(gp.x, gp.z) : undefined;
  }

  /** Where a table spot (in your view) appears on screen — used by tests and debugging. */
  screenPoint(x: number, z: number, y = 0) {
    const v = new THREE.Vector3(x, y, z).project(this.rig.camera);
    const r = this.renderer.domElement.getBoundingClientRect();
    return { x: r.left + ((v.x + 1) / 2) * r.width, y: r.top + ((1 - v.y) / 2) * r.height };
  }

  /** Screen position of a card or token by id (for tests). */
  screenOf(id: string) {
    const c = this.cards.get(id);
    if (c) return this.screenPoint(c.root.position.x, c.root.position.z, c.root.position.y);
    const t = this.tokens.get(id);
    if (t) return this.screenPoint(t.mesh.position.x, t.mesh.position.z, t.mesh.position.y + t.height);
    return null;
  }

  debugState() {
    return this.view;
  }

  /** The next update is a jump to another moment of a replay: show where things are, without replaying effects. */
  forgetEffects() {
    this.skipFx = true;
  }

  // ======================= setup =======================

  private texture(key: string, src: Promise<string>): THREE.Texture {
    let tex = this.textures.get(key);
    if (tex) return tex;
    const t = new THREE.Texture();
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    src
      .then((s) => loadImageEl(s))
      .then((img) => {
        t.image = img;
        t.needsUpdate = true;
      })
      .catch(() => {});
    this.textures.set(key, t);
    return t;
  }
  private backTexture() {
    return this.texture('back', cardBackSrc());
  }
  private frontTexture(num: number | null, dark = false, form = 0) {
    return num === null ? this.backTexture() : this.texture(`card:${num}${dark ? ':dark' : ''}${form ? `:f${form}` : ''}`, cardFrontSrc(num, dark, form));
  }
  /** Was this card summoned with Dark Gerzite (it carries the Darkgale token)? Its cost gem then glows dark red. */
  private summonedDark(c: CardView) {
    const mark = CONFIG.turn.darkgale.token;
    if (!mark || !this.view) return false;
    return tokensOnCard(c, this.view.tokens).some((t) => TOKEN_DB.byId.get(t.tokenId)?.effect === mark);
  }

  private buildRoom() {
    const s = this.scene;
    // the room: this player's own choice of background (Game Menu → Background). Only the air and the floor
    // change with it; the table, its legs and the mat stay as they are.
    s.background = new THREE.Color(CONFIG.colors.background);
    s.fog = new THREE.Fog(CONFIG.colors.background, 260, 700);

    this.hemi = new THREE.HemisphereLight(0xf6f3ec, 0x3a3d44, 2.6);
    s.add(this.hemi);
    const sun = new THREE.DirectionalLight(0xffffff, 1.6);
    this.sun = sun;
    sun.position.set(30, 120, 60);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const sc = sun.shadow.camera;
    sc.left = -100;
    sc.right = 100;
    sc.top = 80;
    sc.bottom = -80;
    sc.near = 10;
    sc.far = 300;
    sun.shadow.bias = -0.0004;
    s.add(sun);

    // table top with a darker rim
    const { width, depth } = CONFIG.table;
    const top = new THREE.Mesh(
      new THREE.BoxGeometry(width, 3, depth),
      new THREE.MeshLambertMaterial({ color: CONFIG.colors.table }),
    );
    top.position.y = -1.5;
    top.receiveShadow = true;
    s.add(top);
    this.rimMat = new THREE.MeshLambertMaterial({ color: CONFIG.colors.tableEdge });
    const rim = new THREE.Mesh(new THREE.BoxGeometry(width + 4, 4, depth + 4), this.rimMat);
    rim.position.y = -2.1;
    s.add(rim);
    this.buildEmberBands();
    const legGeo = new THREE.BoxGeometry(6, 70, 6);
    const legMat = new THREE.MeshLambertMaterial({ color: CONFIG.colors.tableEdge });
    for (const [lx, lz] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) {
      const leg = new THREE.Mesh(legGeo, legMat);
      leg.position.set(lx * (width / 2 - 8), -39, lz * (depth / 2 - 8));
      s.add(leg);
    }
    this.floorMat = new THREE.MeshLambertMaterial({ color: new THREE.Color(CONFIG.colors.background).multiplyScalar(0.8) });
    const floor = new THREE.Mesh(new THREE.CircleGeometry(600, 64), this.floorMat);
    this.floor = floor;
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -74;
    floor.receiveShadow = true;
    s.add(floor);

    // the playing mat — one image for the whole table; turned around for seat 2
    const matGeo = new THREE.PlaneGeometry(CONFIG.mat.width, CONFIG.mat.depth);
    matGeo.rotateX(-Math.PI / 2);
    const mat = new THREE.Mesh(matGeo, new THREE.MeshLambertMaterial({ map: this.texture('mat', matSrc()) }));
    mat.position.y = 0.01;
    mat.rotation.y = this.flip === 1 ? 0 : Math.PI;
    mat.receiveShadow = true;
    s.add(mat);
    this.buildSlotGlows();
    this.setBackground(chosenBackground());
  }

  private roomColor = new THREE.Color(CONFIG.colors.background);
  private floorMat: THREE.MeshLambertMaterial | null = null;
  private floor: THREE.Mesh | null = null;
  private hemi: THREE.HemisphereLight | null = null;
  private sun: THREE.DirectionalLight | null = null;
  private sky: THREE.Mesh | null = null; // a special environment's 360° panorama around the table
  private motes: { points: THREE.Points; base: Float32Array; phase: Float32Array } | null = null;
  /** A special environment's panorama: a big sphere seen from inside, centered on the table. */
  private setSky(src: string | undefined, turn = 0) {
    if (this.sky) {
      this.scene.remove(this.sky);
      (this.sky.material as THREE.MeshBasicMaterial).dispose();
      this.sky.geometry.dispose();
      this.sky = null;
    }
    if (this.floor) this.floor.visible = !src; // the panorama has its own floor
    if (!src) return;
    const geo = new THREE.SphereGeometry(900, 64, 32);
    geo.scale(-1, 1, 1); // seen from inside
    // (the picture as named, or, if that file isn't there, the same name as .png, .jpg, .jpeg or .webp: a panorama
    // saved as a .png while config.ts says .jpg still shows)
    const tries = [...new Set([src, ...['png', 'jpg', 'jpeg', 'webp'].map((ext) => src.replace(/\.\w+$/, `.${ext}`))])];
    const found = tries.reduce<Promise<string>>((prev, next) => prev.catch(() => loadImageEl(next).then(() => next)), Promise.reject(new Error('none')));
    found.catch(() => console.warn(`Background panorama not found: ${src} (put it in public/${src.replace(/\.\w+$/, '')}.png or .jpg)`));
    const map = this.texture(`sky:${src}`, found);
    map.colorSpace = THREE.SRGBColorSpace;
    const sky = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map, fog: false, depthWrite: false }));
    // the camera sits a little above the table: the panorama's horizon is lifted to about eye height
    sky.position.y = 60;
    sky.rotation.y = (turn * Math.PI) / 180; // the view is the same from either seat: so is the room
    sky.renderOrder = -10;
    this.scene.add(sky);
    this.sky = sky;
  }
  /** Floating specks in the air around the table (dust in candlelight), or none. */
  private setMotes(color: string | undefined) {
    if (this.motes) {
      this.scene.remove(this.motes.points);
      this.motes.points.geometry.dispose();
      (this.motes.points.material as THREE.PointsMaterial).dispose();
      this.motes = null;
    }
    if (!color) return;
    const n = 260;
    const base = new Float32Array(n * 3);
    const phase = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 40 + Math.random() * 260;
      base[i * 3] = Math.cos(a) * r;
      base[i * 3 + 1] = -30 + Math.random() * 170;
      base[i * 3 + 2] = Math.sin(a) * r;
      phase[i] = Math.random() * Math.PI * 2;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(base.slice(), 3));
    const c = document.createElement('canvas');
    c.width = c.height = 32;
    const g = c.getContext('2d')!;
    const grad = g.createRadialGradient(16, 16, 0, 16, 16, 16);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.4, 'rgba(255,255,255,0.35)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 32, 32);
    const mat = new THREE.PointsMaterial({ color, size: 1.6, map: new THREE.CanvasTexture(c), transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
    const points = new THREE.Points(geo, mat);
    this.scene.add(points);
    this.motes = { points, base, phase };
  }
  /** The specks drift slowly and twinkle. */
  private animateMotes(time: number) {
    const m = this.motes;
    if (!m) return;
    const pos = m.points.geometry.getAttribute('position') as THREE.BufferAttribute;
    const arr = pos.array as Float32Array;
    for (let i = 0; i < m.phase.length; i++) {
      const ph = m.phase[i];
      arr[i * 3] = m.base[i * 3] + Math.sin(time * 0.13 + ph) * 6;
      arr[i * 3 + 1] = m.base[i * 3 + 1] + Math.sin(time * 0.21 + ph * 1.7) * 4;
      arr[i * 3 + 2] = m.base[i * 3 + 2] + Math.cos(time * 0.11 + ph) * 6;
    }
    pos.needsUpdate = true;
    (m.points.material as THREE.PointsMaterial).opacity = 0.45 + 0.1 * Math.sin(time * 0.8);
  }
  /** Changes the room around the table to one of the backgrounds in the settings. Only this player's view changes. */
  setBackground(id: string) {
    const B = CONFIG.backgrounds;
    const bg = B.list.find((b) => b.id === id) ?? B.list.find((b) => b.id === B.default);
    if (!bg) return;
    this.roomColor.set(bg.color);
    if (bg.floor) this.floorMat?.color.set(bg.floor);
    else this.floorMat?.color.copy(this.roomColor).multiplyScalar(0.8);
    // a special environment: its panorama, the color of its light, and specks in the air
    this.setSky(bg.sky, bg.skyTurn ?? 0);
    const light = new THREE.Color(bg.light ?? '#ffffff');
    this.hemi?.color.set(0xf6f3ec).multiply(light);
    this.sun?.color.copy(light);
    this.setMotes(bg.motes);
    // Overtime warms the room from whatever color it is; outside Overtime it is simply that color
    if (this.mood <= 0) {
      (this.scene.background as THREE.Color).copy(this.roomColor);
      this.scene.fog?.color.copy(this.roomColor);
    }
  }

  // ======================= where a card may go =======================
  // While you hold a card (dragged from your hand or across the table, or selected in your hand), the
  // slots on your side that it is allowed in light up. Which those are is slotsFor in src/config.ts,
  // plus the card's own extra_slots in cards.csv.

  private slotGlows: { mesh: THREE.Mesh; kind: string }[] = [];
  private openKinds: Set<string> | null = null; // the kinds of slot lit up right now

  private buildSlotGlows() {
    // one soft frame, stretched over each slot: a bright rounded outline with a faint wash inside
    const c = document.createElement('canvas');
    c.width = 128;
    c.height = 128;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = 'rgba(255,255,255,0.28)';
    ctx.beginPath();
    ctx.roundRect(8, 8, 112, 112, 12);
    ctx.fill();
    ctx.lineWidth = 7;
    ctx.strokeStyle = '#fff';
    ctx.shadowColor = '#fff';
    ctx.shadowBlur = 8;
    ctx.stroke();
    const tex = new THREE.CanvasTexture(c);
    for (const slot of CONFIG.slots) {
      const geo = new THREE.PlaneGeometry(slot.width + 1.6, slot.depth + 1.6);
      geo.rotateX(-Math.PI / 2);
      const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: tex, color: CONFIG.colors.highlight, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
      mesh.position.set(slot.x, 0.03, slot.z); // your own side: in your view your slots are always the near ones
      mesh.visible = false;
      this.scene.add(mesh);
      this.slotGlows.push({ mesh, kind: slotKind(slot.name) });
    }
    // the same frame over every lane, Minion and Emperor slot on both sides, for showing who a unit can reach
    for (const slot of CONFIG.slots) {
      if (!['Lane', 'Minion', 'Emperor'].includes(slotKind(slot.name))) continue;
      for (const side of [1, -1] as const) {
        const geo = new THREE.PlaneGeometry(slot.width + 1.6, slot.depth + 1.6);
        geo.rotateX(-Math.PI / 2);
        // painted over the slot's own outline (not added to it as light), so the red and green read true against it
        const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0, depthWrite: false }));
        mesh.position.set(slot.x * side, 0.032, slot.z * side);
        mesh.visible = false;
        this.scene.add(mesh);
        this.reachGlows.push({ mesh, key: `${slot.name}|${slot.hand ?? ''}|${side}` });
      }
    }
  }

  // ======================= who a unit can reach =======================
  // Hover over a unit on the table: the opposing lanes it can attack light up red, and its own side's lanes it
  // can heal or boost light up green. The rules are `reach` in src/config.ts.

  private reachGlows: { mesh: THREE.Mesh; key: string }[] = [];

  /** A choice of lanes being pointed at (a Spell's "Lanes 1 to 3"): the middle of each of those lanes, to light up. */
  private lanePreview: { x: number; z: number }[] | null = null;
  /** Light up these lanes while a lane choice is pointed at (null = none): red on the opposing side, green on yours. */
  previewLanes(spots: { x: number; z: number }[] | null) {
    this.lanePreview = spots?.length ? spots : null;
  }
  /** The slots the hovered unit reaches right now, as "Lane 3|-1" (side 1 = yours, -1 = your opponent's). */
  private reachTargets(): Map<string, 'attack' | 'support'> {
    const out = new Map<string, 'attack' | 'support'>();
    if (this.lanePreview) {
      const you = this.view?.you ?? 0;
      for (const sp of this.lanePreview) {
        const at = slotAt(sp);
        if (at) out.set(`${at.slot.name}|${at.slot.hand ?? ''}|${at.side}`, at.side === sideOf(you) ? 'support' : 'attack');
      }
      return out;
    }
    const R = CONFIG.reach;
    if (!R.enabled || this.pointer || this.openKinds || this.hovered?.kind !== 'card') return out;
    const obj = this.cards.get(this.hovered.id);
    const def = obj && obj.data.num !== null && obj.data.faceUp ? CARD_DB.byNumber.get(obj.data.num) : undefined;
    if (!obj || !def || obj.dragging || R.notLanes.includes(def.number)) return out;
    const at = slotAt(obj);
    if (!at) return out;
    const kind = slotKind(at.slot.name);
    const foe = -at.side as 1 | -1;
    const key = (slot: Slot, side: number) => `${slot.name}|${slot.hand ?? ''}|${side}`;
    const lanes = CONFIG.slots.filter((sl) => slotKind(sl.name) === 'Lane');
    const inOrder = (side: number) => [...lanes].sort((a, b) => a.x * side - b.x * side); // left to right as you see them
    const holds = (slot: Slot, side: number) =>
      [...this.cards.values()].filter((c) => {
        if (c === obj || Math.abs(c.x - slot.x * side) > slot.width / 2 || Math.abs(c.z - slot.z * side) > slot.depth / 2) return false;
        const d = c.data.num !== null && c.data.faceUp ? CARD_DB.byNumber.get(c.data.num) : undefined;
        return !d || d.types.some((t) => t === 'troop' || t === 'building' || t === 'emperor'); // a face-down card in a lane counts as a unit
      });
    const flies = (c: CardObj) => (c.flying || c.tokenFly) && !c.tokenGround && !c.data.grounded;
    const emperors = CONFIG.slots.filter((sl) => slotKind(sl.name) === 'Emperor');
    const minions = CONFIG.slots.filter((sl) => slotKind(sl.name) === 'Minion');
    // the opposing Emperor, and its Minion if there is one (2v2: both opponents')
    const leader = () => {
      for (const emperor of emperors) out.set(key(emperor, foe), 'attack');
      for (const minion of minions) if (holds(minion, foe).length) out.set(key(minion, foe), 'attack');
    };
    // a range written "1/0" (Sunken Soldier) is the first number with its Special Hit and the second without, and
    // the Range token it brought is that same number (not counted twice); Firelance's first attack has 1 range
    const mine = [...this.tokens.values()].filter((t) => t.on === obj);
    const special = mine.some((t) => t.def?.effect === 'special-hit');
    const twoRanges = (def.rangeText.match(/\d+(\.\d+)?/g) ?? []).map(Number);
    const two = twoRanges.length > 1;
    let range = two ? (special ? twoRanges[0] : twoRanges[twoRanges.length - 1]) : (def.range ?? 0);
    const firstAttack = special ? /first attack deals \d+[^.]*?has (\d+) range/i.exec(def.text) : null;
    if (firstAttack) range = Math.max(range, Number(firstAttack[1]));
    let ranged = def.range !== null || !!firstAttack;
    for (const t of mine) {
      if (t.def?.effect === R.rangeToken && !(two && t.data.from === obj.data.id)) {
        range += t.data.value;
        ranged = true;
      }
    }
    // range from another card's aura (Watch Tower: +2 to your Units within its range), as the table works it out
    if (obj.data.rangeAura) {
      range += obj.data.rangeAura;
      ranged = true;
    }
    range = Math.max(0, Math.floor(range));
    const damage = Number.parseFloat(def.damage);
    const attacks = def.types.includes('emperor') || (Number.isFinite(damage) && damage > 0);

    if (attacks && (kind === 'Emperor' || kind === 'Minion')) {
      // Emperors and Minions reach every opposing lane, and the Emperor and Minion behind them once one lane is open
      for (const lane of lanes) out.set(key(lane, foe), 'attack');
      if (lanes.some((lane) => !holds(lane, foe).length)) leader();
      return out;
    }
    if (kind !== 'Lane') return out;
    const i = inOrder(at.side).indexOf(at.slot);
    if (attacks) {
      const hitsFlying = flies(obj) || (range > 0 && !new RegExp(R.noFlying, 'i').test(def.text));
      inOrder(foe).forEach((lane, j) => {
        if (Math.abs(j - i) > range) return;
        const units = holds(lane, foe);
        const cards = units.filter((c) => c.data.num === null || CARD_DB.byNumber.get(c.data.num)?.types.some((t) => t === 'troop' || t === 'building'));
        if (cards.length && !hitsFlying && cards.every(flies)) return; // only flying units there, out of its reach
        out.set(key(lane, foe), 'attack');
      });
      const direct = def.traits.some((t) => t.split('/').map((s) => s.trim()).includes(R.direct));
      const front = inOrder(foe)[i];
      if (direct && front && !holds(front, foe).length) leader();
    }
    if (def.traits.some((t) => R.supportTraits.includes(t))) {
      inOrder(at.side).forEach((lane, j) => {
        if (!ranged || Math.abs(j - i) <= range) out.set(key(lane, at.side), 'support');
      });
    }
    return out;
  }

  private updateReachGlows(time: number, dt: number) {
    const k = Math.min(1, dt * 10);
    const want = this.reachTargets();
    for (const g of this.reachGlows) {
      const m = g.mesh.material as THREE.MeshBasicMaterial;
      const what = want.get(g.key);
      if (what) m.color.set(what === 'attack' ? CONFIG.reach.attack : CONFIG.reach.support);
      const target = what ? 0.85 + 0.15 * Math.sin(time * 4) : 0;
      m.opacity += (target - m.opacity) * k;
      g.mesh.visible = m.opacity > 0.01;
    }
  }

  /** Light up the slots this card may be placed in (null = none). */
  private showSlots(num: number | null) {
    const def = num !== null ? CARD_DB.byNumber.get(num) : undefined;
    const open = def ? slotsOpenTo(def) : null;
    this.openKinds = open ? new Set(open) : null;
  }

  private updateSlotGlows(time: number, dt: number) {
    const k = Math.min(1, dt * 10);
    for (const g of this.slotGlows) {
      const m = g.mesh.material as THREE.MeshBasicMaterial;
      const target = this.openKinds?.has(g.kind) ? 0.5 + 0.18 * Math.sin(time * 4) : 0;
      m.opacity += (target - m.opacity) * k;
      g.mesh.visible = m.opacity > 0.01;
    }
  }

  /** Overtime: a low ember glow along the left and right edges of the table (dark until Overtime begins). */
  private buildEmberBands() {
    const { width, depth } = CONFIG.table;
    const c = document.createElement('canvas');
    c.width = 8;
    c.height = 128;
    const ctx = c.getContext('2d')!;
    const g = ctx.createLinearGradient(0, 0, 0, 128);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.14)');
    g.addColorStop(1, 'rgba(255,255,255,1)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 8, 128);
    const tex = new THREE.CanvasTexture(c);
    const band = 18;
    for (const side of [1, -1]) {
      const geo = new THREE.PlaneGeometry(depth, band);
      geo.rotateX(-Math.PI / 2);
      const mat = new THREE.MeshBasicMaterial({ map: tex, color: CONFIG.overtime.ember, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending });
      const m = new THREE.Mesh(geo, mat);
      m.position.set(side * (width / 2 - band / 2), 0.005, 0);
      m.rotation.y = side === 1 ? Math.PI / 2 : -Math.PI / 2;
      m.visible = false;
      this.emberBands.push(m);
      this.scene.add(m);
    }
  }

  /** Undead Legion's battle banner in its owner's color: the cloth a shade deeper than the side's blue or red, the
   *  pale gold trim and skull as they are, the pole nearly black with a hint of the color. */
  private bannerTint(owner: Seat): string[] {
    const side = new THREE.Color(this.seatColor(owner));
    const trim = CONFIG.playEffects.colors['legion-banner']?.[1] ?? '#d8c78f';
    return ['#' + side.clone().multiplyScalar(0.72).getHexString(), trim, '#' + side.clone().multiplyScalar(0.22).getHexString()];
  }

  /** Whose side a card shows as. The solo table has only you, so a card put on the far side of the table plays
   *  the opponent's part: it turns to face that side and takes the red side's color. Everywhere else: its owner. */
  private sideSeat(c: CardView): Seat {
    return this.view?.mode === 'solo' && c.z < 0 ? (1 as Seat) : c.owner;
  }

  /** Blue for whoever goes first, red for the other player. */
  private seatColor(seat: Seat) {
    return teamOf(seat) === teamOf(this.view?.first ?? 0) ? CONFIG.playerColors.first : CONFIG.playerColors.second;
  }
  /** Is this seat on your side of the table (you, or your teammate in 2v2)? */
  private nearSeat(seat: Seat) {
    return !!this.view && sideOf(seat) === sideOf(this.view.you);
  }
  /** Are these two seats on the same team? (In 1v1, only a seat with itself.) */
  private allied(a: Seat, b: Seat) {
    return this.view?.teams ? teamOf(a) === teamOf(b) : a === b;
  }

  /** A soft band of the player's color along their edge of the table, and a bright line on the rim. */
  private buildSideAccent(side: number, color: string) {
    const { width, depth } = CONFIG.table;
    const strength = CONFIG.playerColors.tableGlow;
    if (strength <= 0) return;
    const c = document.createElement('canvas');
    c.width = 8;
    c.height = 128;
    const ctx = c.getContext('2d')!;
    const g = ctx.createLinearGradient(0, 0, 0, 128);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.55, 'rgba(255,255,255,0.12)');
    g.addColorStop(1, 'rgba(255,255,255,1)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 8, 128);
    const tex = new THREE.CanvasTexture(c);
    const band = 22; // how far the glow reaches in from the edge, in cm
    const geo = new THREE.PlaneGeometry(width, band);
    geo.rotateX(-Math.PI / 2);
    const glow = new THREE.Mesh(
      geo,
      new THREE.MeshBasicMaterial({ map: tex, color, transparent: true, opacity: strength, depthWrite: false, blending: THREE.AdditiveBlending }),
    );
    glow.position.set(0, 0.004, side * (depth / 2 - band / 2));
    glow.rotation.y = side === 1 ? 0 : Math.PI;
    const line = new THREE.Mesh(new THREE.BoxGeometry(width + 4, 0.5, 1.1), new THREE.MeshBasicMaterial({ color }));
    line.position.set(0, 0.05, side * (depth / 2 + 1.45));
    this.scene.add(glow, line);
  }

  /** Bars and piles for each seat at the table. */
  private buildSeats(view: GameView) {
    for (let i = 0; i < view.players.length; i++) {
      const seat = i as Seat;
      const mine = sideOf(seat) === sideOf(view.you); // on your side of the table
      const side = mine ? 1 : -1;
      for (const pile of ['deck', 'discard'] as const) this.piles.push(this.makePile(seat, pile, mine));
      // 2v2: teammates share one side, one colored edge and one pair of bars (built for the team's left seat)
      if (handOf(seat) !== 1) continue;
      this.buildSideAccent(side, this.seatColor(seat));
      for (const bar of ['gerzite', 'dark'] as const) {
        const b = bar === 'gerzite' ? CONFIG.zones.gerziteBar : CONFIG.zones.darkGerziteBar;
        const g = new THREE.PlaneGeometry(b.width, b.depth);
        g.rotateX(-Math.PI / 2);
        const m = new THREE.Mesh(
          g,
          new THREE.MeshLambertMaterial({ map: this.texture(`bar:${bar}`, barSrc(bar)), transparent: true }),
        );
        const at = this.toView(seatPoint(seat, b).x, seatPoint(seat, b).z);
        m.position.set(at.x, 0.006, at.z);
        m.rotation.y = mine ? 0 : Math.PI;
        m.receiveShadow = true;
        this.scene.add(m);
        // an outline round the bar that flashes when the bar is full (nothing more fits in it)
        const pad = 1.1;
        const fg = new THREE.PlaneGeometry(b.width + pad * 2, b.depth + pad * 2);
        fg.rotateX(-Math.PI / 2);
        const color = new THREE.Color(bar === 'gerzite' ? CONFIG.colors.gerziteBar : CONFIG.colors.darkGerziteBar).lerp(new THREE.Color('#ffffff'), 0.35);
        const mat = new THREE.MeshBasicMaterial({ map: this.frameTexture(b.width, b.depth, pad), color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending });
        const frame = new THREE.Mesh(fg, mat);
        frame.position.set(at.x, 0.03, at.z);
        frame.visible = false;
        frame.renderOrder = 5;
        this.scene.add(frame);
        this.barFrames.push({ seat, bar, frame, mat, t0: -1, holds: b.slots, had: -1 });
      }
    }
    if (view.mode === 'versus') {
      // a row of card backs on the far side for each opponent (2v2: one behind each half)
      for (let i = 0; i < view.players.length; i++) {
        const seat = i as Seat;
        if (this.nearSeat(seat)) continue;
        const group = new THREE.Group();
        const at = this.toView(seatPoint(seat, { x: view.teams ? CONFIG.zones.emperor.x : 0, z: CONFIG.zones.opponentHandRow.z }).x, seatPoint(seat, { x: view.teams ? CONFIG.zones.emperor.x : 0, z: CONFIG.zones.opponentHandRow.z }).z);
        group.position.set(at.x, 0, 0);
        this.scene.add(group);
        const label = document.createElement('div');
        label.className = 'table-label';
        label.addEventListener('click', () => this.cb.showOpenHand?.(seat));
        label.style.borderColor = this.seatColor(seat);
        const lbl = new CSS2DObject(label);
        lbl.position.set(at.x, 0.5, -CONFIG.zones.opponentHandRow.z + CH / 2 + 2);
        this.scene.add(lbl);
        this.oppHands.push({ seat, group, label, key: '' });
      }
    }
  }

  private makePile(seat: Seat, pile: PileName, mine: boolean): PileObj {
    const zone = seatPoint(seat, CONFIG.zones[pile]);
    const at = this.toView(zone.x, zone.z);
    const group = new THREE.Group();
    group.position.set(at.x, 0, at.z);
    group.rotation.y = mine ? 0 : Math.PI;

    // the sides: dark like the edge of a card back, with darker and lighter grey lines between the cards
    const edges = pileEdgeTexture().clone();
    edges.needsUpdate = true;
    const box = new THREE.Mesh(cardBlock(CW, CH), new THREE.MeshLambertMaterial({ map: edges }));
    box.castShadow = true;
    const topMat = new THREE.MeshLambertMaterial({ map: this.backTexture() });
    const top = new THREE.Mesh(this.geo.front, topMat);
    const pick: Pick = { kind: 'pile', seat, pile };
    box.userData.pick = pick;
    top.userData.pick = pick;
    this.pickables.push(box, top);

    const pad = 0.8;
    const pts = [
      new THREE.Vector3(-CW / 2 - pad, 0.02, -CH / 2 - pad),
      new THREE.Vector3(CW / 2 + pad, 0.02, -CH / 2 - pad),
      new THREE.Vector3(CW / 2 + pad, 0.02, CH / 2 + pad),
      new THREE.Vector3(-CW / 2 - pad, 0.02, CH / 2 + pad),
    ];
    const outline = new THREE.LineLoop(
      new THREE.BufferGeometry().setFromPoints(pts),
      new THREE.LineBasicMaterial({ color: this.seatColor(seat), transparent: true, opacity: 0.85 }),
    );
    const glowGeo = new THREE.PlaneGeometry(CW + 4, CH + 4);
    glowGeo.rotateX(-Math.PI / 2);
    const glow = new THREE.Mesh(
      glowGeo,
      new THREE.MeshBasicMaterial({ color: CONFIG.colors.highlight, transparent: true, opacity: 0, depthWrite: false }),
    );
    glow.position.y = 0.015;
    group.add(glow, outline, box, top);

    const label = document.createElement('div');
    label.className = 'table-label';
    label.style.borderColor = this.seatColor(seat);
    const lbl = new CSS2DObject(label);
    lbl.position.set(0, 0.5, CH / 2 + 2.2);
    if (!mine) lbl.position.z = -(CH / 2 + 2.2); // group is turned; keep the label on the near side
    group.add(lbl);

    this.scene.add(group);
    return { seat, pile, group, box, edges, top, topMat, topNum: undefined, outline, glow, label, shuffles: -1, shuffleT: 0, jumps: -1, jumpT: 0, searching: false, hover: false, holdUntil: 0 };
  }

  // ======================= syncing with the game =======================

  update(view: GameView) {
    const first = !this.view;
    this.view = view;
    if (first) this.buildSeats(view);
    this.holdPulled(view, first);

    // cards on the table
    const sorted = [...view.field].sort((a, b) => a.layer - b.layer);
    const seen = new Set<string>();
    sorted.forEach((c, rank) => {
      seen.add(c.id);
      let obj = this.cards.get(c.id);
      if (!obj) {
        obj = this.makeCard(c);
        this.cards.set(c.id, obj);
      }
      this.syncCard(obj, c, rank, first);
      if (first && c.num !== null && this.isEmperorCard(c.num)) {
        const target = obj;
        setTimeout(() => !this.disposed && this.playEffect(target), 700);
      }
    });
    this.syncAxes(view);
    // effects for things that just happened (a card played, flipped face-up, merged…)
    if (first || this.skipFx) {
      this.lastFx = view.fx.at(-1)?.id ?? 0;
      this.settleExpiry = true; // a card found already spent (a loaded game, a jump in a replay) doesn't go off again
    }
    this.skipFx = false;
    const fresh = view.fx.filter((f) => f.id > this.lastFx);
    this.freshNow = fresh;
    // a sacrificed card has already left the game, but it stays on the table a moment longer to crack and burst
    const burst: { at: THREE.Vector3; seat: Seat }[] = [];
    const departures: { num: number; owner: Seat; from: THREE.Vector3; delay: number }[] = []; // (see discardFlights)
    // (a Spell that blows apart as it goes off — Arcane Explosion — hurts what it hits as it bursts, not before)
    const blasting = fresh.some((f) => f.kind === 'sacrifice' && !!f.effect?.startsWith('blast-'));
    // …and what it sends at its targets (its affiliation's look) sets off as it bursts
    this.spellLead = blasting ? SACRIFICE_BURST : CONFIG.combat.spellFx.delay;
    for (const f of fresh) {
      let obj = f.kind === 'sacrifice' ? this.cards.get(f.card) : undefined;
      // a Spell that went off the moment it was played (Arcane Explosion) was never drawn: it is laid down now, to go out its own way
      if (!obj && f.kind === 'sacrifice' && f.num !== undefined && f.from && !seen.has(f.card)) {
        const data: CardView = { id: f.card, num: f.num, owner: f.seat ?? 0, x: f.from.x, z: f.from.z, rot: f.value ?? 0, faceUp: true, spawned: false, layer: 0, tier: 1 };
        obj = this.makeCard(data);
        this.cards.set(f.card, obj);
        this.syncCard(obj, data, 0, true);
      }
      // a Spell that went off and was discarded in one go (played and done at once) is laid down too, to be seen doing it
      if (f.kind === 'spent' && !first && !this.cards.has(f.card) && !seen.has(f.card) && f.num !== undefined && f.from && !fresh.some((x) => x.kind === 'sacrifice' && x.card === f.card)) {
        const data: CardView = { id: f.card, num: f.num, owner: f.seat ?? 0, x: f.from.x, z: f.from.z, rot: f.value ?? 0, faceUp: true, spawned: false, layer: 0, tier: 1 };
        const laid = this.makeCard(data);
        this.cards.set(f.card, laid);
        this.syncCard(laid, data, 0, true);
        continue;
      }
      if (f.kind !== 'sacrifice') continue;
      if (!obj || seen.has(f.card)) continue;
      burst.push({ at: obj.root.position.clone(), seat: f.seat ?? obj.data.owner });
      // one destroyed by an attack the table dealt stands until that attack has landed
      const hitBy = CONFIG.combat.attackFx.enabled && fresh.some((a) => a.kind === 'attack' && a.effect === 'strike' && a.target === f.card);
      const spellBy = CONFIG.combat.spellFx.enabled && fresh.some((a) => a.kind === 'spell' && a.target === f.card);
      const bounced = this.bounceDelay(fresh, f.card) || (blasting && !spellBy && !f.effect?.startsWith('blast-') ? SACRIFICE_BURST : 0);
      let wait = bounced || (hitBy ? CONFIG.combat.attackFx.arrowSeconds + CONFIG.combat.attackFx.flightSeconds : spellBy ? this.spellLandsOn(fresh, f.card) : 0);
      // All Stop: each Spell bursts as the pulse running out from the caster reaches it
      if (f.effect === 'allstop-burst' && f.at) wait = Math.max(wait, this.pulseReaches(f.at, obj));
      // a coin that lost its flip shatters once the coin has landed and shown its side
      if (f.effect === 'coin-shatter' && fresh.some((c) => c.kind === 'cure' && c.card === f.card)) wait = Math.max(wait, CURE_LAND + 0.2);
      this.beginSacrifice(obj, wait, f.effect);
      if (obj.data.num !== null && obj.data.faceUp) departures.push({ num: obj.data.num, owner: obj.data.owner, from: new THREE.Vector3(obj.x, 1, obj.z), delay: wait + SACRIFICE_BURST });
    }
    // cards that simply left the table (a Spell that has done what it does, a card discarded): they may be flying to a discard pile
    for (const [id, obj] of this.cards) if (!seen.has(id)) {
      if (this.lingerCards.has(id)) continue; // (already staying on until what it set off has played out)
      // a Spell that has done what it does stays on the table until what it set off in this update has played out
      // (its light reaching its targets, Gerzite flying into it, its own look), then goes to the discard pile
      const hold = first ? 0 : this.holdFor(id, obj, fresh);
      if (!first && obj.data.num !== null && obj.data.faceUp) departures.push({ num: obj.data.num, owner: obj.data.owner, from: new THREE.Vector3(obj.x, obj.root.position.y + 0.5, obj.z), delay: hold });
      if (hold > 0) {
        this.lingerCards.add(id);
        this.pickables = this.pickables.filter((pk) => pk.userData.pick?.id !== id); // (it can't be clicked on any more)
        setTimeout(() => {
          this.lingerCards.delete(id);
          const still = this.cards.get(id);
          if (!this.disposed && still && !this.view?.field.some((c) => c.id === id)) this.removeCard(id, still);
        }, hold * 1000);
        continue;
      }
      this.removeCard(id, obj);
    }

    // (selection is refreshed further down, once tokens are synced too)
    const rolls: FxEvent[] = []; // dice rolls wait until the tokens below are up to date
    // damage copied across the Emperor–Minion link shows up when the pulse arrives, not before
    const linked = new Set(fresh.filter((f) => f.kind === 'link').map((f) => f.target));
    // …and the hit of an attack the table dealt shows when the attack lands, not while its arrow is still up
    const A = CONFIG.combat.attackFx;
    const struck = new Set(A.enabled ? fresh.filter((f) => f.kind === 'attack' && f.effect === 'strike').map((f) => f.target) : []);
    // …and a Spell's hit shows when what it sent gets there
    const spelled = new Set(CONFIG.combat.spellFx.enabled ? fresh.filter((f) => f.kind === 'spell').map((f) => f.target) : []);
    const looked = new Set<string>(); // (the same look on the same card twice in one go plays once: an attack's several hits)
    for (const f of fresh) {
      this.lastFx = f.id;
      if (f.kind === 'look' && !f.target) {
        const key = `${f.effect}|${f.card}|${f.pile ?? ''}|${f.at ? `${f.at.x},${f.at.z}` : ''}`;
        if (looked.has(key)) continue;
        looked.add(key);
      }
      if (f.kind === 'roll') rolls.push(f);
      else if (f.kind === 'damage' && blasting && !spelled.has(f.card)) setTimeout(() => !this.disposed && this.playFx(f), SACRIFICE_BURST * 1000);
      else if (f.kind === 'damage' && this.bounceDelay(fresh, f.card)) setTimeout(() => !this.disposed && this.playFx(f), this.bounceDelay(fresh, f.card) * 1000);
      else if (f.kind === 'damage' && linked.has(f.card)) setTimeout(() => !this.disposed && this.playFx(f), LINK_TRAVEL * 1000);
      // a card's own effect on a card being hit (Highcard Assassin's X, the Hay Golem throwing it back) waits for the hit
      else if (f.kind === 'status' && f.card && struck.has(f.card)) setTimeout(() => !this.disposed && this.playFx(f), (A.arrowSeconds + A.flightSeconds) * 1000);
      else if (f.kind === 'status' && f.card && spelled.has(f.card)) setTimeout(() => !this.disposed && this.playFx(f), this.spellLandsOn(fresh, f.card) * 1000);
      else if (f.kind === 'look' && f.card && struck.has(f.card)) setTimeout(() => !this.disposed && this.playFx(f), (A.arrowSeconds + A.flightSeconds) * 1000);
      else if (f.kind === 'look' && f.card && spelled.has(f.card)) setTimeout(() => !this.disposed && this.playFx(f), this.spellLandsOn(fresh, f.card) * 1000);
      else if (f.kind === 'damage' && struck.has(f.card)) setTimeout(() => !this.disposed && this.playFx(f), (A.arrowSeconds + A.flightSeconds) * 1000);
      else if (f.kind === 'damage' && spelled.has(f.card)) setTimeout(() => !this.disposed && this.playFx(f), this.spellLandsOn(fresh, f.card) * 1000);
      else this.playFx(f);
    }

    // tokens
    // a status cured by a die stays where it is until the die has landed, then goes
    for (const f of fresh) {
      if (f.kind !== 'cure' || f.effect !== 'cured') continue;
      for (const id of [f.token, f.target]) {
        if (!id || !this.tokens.has(id)) continue;
        this.lingering.add(id);
        setTimeout(() => {
          this.lingering.delete(id);
          const obj = this.tokens.get(id);
          if (!this.disposed && obj && !this.view?.tokens.some((t) => t.id === id)) this.removeToken(id, obj);
        }, CURE_LAND * 1000);
      }
    }
    // Gerzite flying to a bar from a card or the other bar (Gerzite Mine, Devil's Coin), with its own look round it
    // what an attack or Spell does to a card shows once it has landed there: new counters (damage, a status) stay out
    // of sight until then, changed ones keep their old number, and ones it takes away (a Shield) stay until then
    const hitDelay = this.hitDelays(fresh, struck, spelled, blasting);
    const delayAt = (x: number, z: number, bar: boolean) => {
      if (bar || !hitDelay.length || first) return 0;
      const p = this.toView(x, z);
      let d = 0;
      for (const h of hitDelay) if (Math.abs(p.x - h.x) < CW * 0.75 && Math.abs(p.z - h.z) < CH * 0.75) d = Math.max(d, h.delay);
      return d;
    };
    const gzFly = fresh.filter((f) => f.kind === 'look' && !!f.effect?.endsWith('-gz') && !!f.at).map((f) => ({ f, left: f.count ?? 1 }));
    const tsorted = [...view.tokens].sort((a, b) => a.layer - b.layer);
    const tseen = new Set<string>();
    const made = new Set<string>(); // tokens that are new in this update
    tsorted.forEach((t, rank) => {
      tseen.add(t.id);
      let obj = this.tokens.get(t.id);
      // Gerzite moved between a bar (a resource) and the table (a counter with a number): drawn afresh
      if (obj && obj.def?.bar && !!obj.counter !== !!t.asCounter && !obj.dragging) {
        this.removeToken(t.id, obj);
        obj = undefined;
      }
      if (!obj) {
        obj = this.makeToken(t);
        this.tokens.set(t.id, obj);
        made.add(t.id);
        // Dark Gerzite earned by a sacrifice flies out of the burst and over to its owner's bar
        const from = obj.def?.bar === 'dark' ? burst.find((b) => b.seat === t.by) : undefined;
        if (from) {
          obj.mesh.position.set(from.at.x, 1.2, from.at.z);
          obj.mesh.visible = false;
          obj.wait = performance.now() / 1000 + SACRIFICE_BURST;
        }
        const late = !from ? delayAt(t.x, t.z, !!obj.def?.bar) : 0;
        if (late > 0) {
          obj.mesh.visible = false;
          obj.wait = performance.now() / 1000 + late;
        }
        const fly = !from && obj.def?.bar === 'gerzite' ? gzFly.find((g) => g.left > 0 && g.f.seat === t.by) : undefined;
        if (fly && fly.f.at) {
          fly.left--;
          const p = this.toView(fly.f.at.x, fly.f.at.z);
          obj.mesh.position.set(p.x, 3, p.z);
          this.effects.play(fly.f.effect!, obj.mesh);
          // it travels at its own pace, in an arc, leaving a trail (CONFIG.looks.gainFlight)
          const G = CONFIG.looks.gainFlight;
          const order = fly.f.count ? (fly.f.count - fly.left - 1) : 0;
          obj.flight = { from: new THREE.Vector3(p.x, 3, p.z), t0: performance.now() / 1000 + order * 0.25, dur: G.seconds, trail: G.trail[fly.f.effect!] ?? '', last: 0 };
        }
      }
      // a turn timer just counted down: a little clock turns above it
      if (!first && this.isTimer(obj) && (this.countsUp(obj) ? t.value > obj.data.value : t.value < obj.data.value)) this.clocks.play(obj.x, obj.z);
      // …and so does the turn clock on a counter that holds an amount
      if (!first && (t.turns ?? 0) < (obj.data.turns ?? 0) && (t.turns ?? 0) > 0) this.clocks.play(obj.x, obj.z);
      // …and a counter wearing down (its wear-off timer took 1 off it) swells for a moment
      if (!first && (t.drain ?? 0) < (obj.data.drain ?? 0) && t.value < obj.data.value) obj.pulse = performance.now() / 1000;
      obj.data = t;
      this.syncClock(obj);
      this.syncDrain(obj);
      if (!obj.dragging) {
        const p = this.toView(t.x, t.z);
        const held = this.heldAt.get(t.id);
        obj.x = held && performance.now() / 1000 < held.until ? held.x : p.x;
        obj.z = held && performance.now() / 1000 < held.until ? held.z : p.z;
      }
      obj.y = 0.34 + rank * 0.002;
      if (obj.counter) {
        // (a counter going up or down under a hit keeps its old number until the hit lands)
        const late = !made.has(t.id) ? delayAt(t.x, t.z, !!obj.def?.bar) : 0;
        if (late > 0) {
          const o = obj;
          setTimeout(() => !this.disposed && this.paintCounter(o), late * 1000);
        } else this.paintCounter(obj);
      }
      if (obj.die && obj.die.shown !== t.value) {
        obj.die.shown = t.value;
        obj.die.target = dieQuat(t.value, obj.die.yaw);
      }
    });
    for (const [id, obj] of this.tokens) {
      // (a counter a hit takes away — a Shield breaking — stays until the hit lands)
      const late = !tseen.has(id) && !this.lingering.has(id) ? delayAt(obj.data.x, obj.data.z, !!obj.def?.bar) : 0;
      if (late > 0) {
        this.lingering.add(id);
        setTimeout(() => {
          this.lingering.delete(id);
          const o = this.tokens.get(id);
          if (!this.disposed && o && !this.view?.tokens.some((t) => t.id === id)) this.removeToken(id, o);
        }, late * 1000);
        continue;
      }
      if (!tseen.has(id) && !this.lingering.has(id)) {
        // a timer on its last turn that is gone while its card is still here: that was its final count
        if (this.isTimer(obj) && !this.countsUp(obj) && obj.data.value === 1 && obj.on && seen.has(obj.on.data.id)) this.clocks.play(obj.x, obj.z);
        if (obj.data.turns === 1 && obj.on && seen.has(obj.on.data.id) && !obj.dragging) this.clocks.play(obj.x, obj.z); // a turn clock's last turn
        this.removeToken(id, obj);
      }
    }
    for (const f of rolls) this.rollFx(f, !!f.token && made.has(f.token));

    this.refreshSelection();
    this.refreshStrike();
    if (this.tipEl) this.hoverTip();

    // piles
    for (const b of burst) {
      const pile = this.piles.find((p) => p.seat === b.seat && p.pile === 'discard');
      if (pile) pile.holdUntil = performance.now() / 1000 + SACRIFICE_BURST;
    }
    this.discardFlights(view, departures, first);
    // a card on its way from one pile to the other: the pile it is flying to waits for it to land
    for (const f of fresh) {
      if (f.kind !== 'pileMove') continue;
      const sheen = f.tint ? PILE_SHEEN : 0; // (Glace Glass: the card waits on its pile while the sheen wipes over it)
      const pile = this.piles.find((p) => p.seat === (f.seat ?? 0) && p.pile === (f.effect === 'discard' ? 'deck' : 'discard'));
      if (pile) pile.holdUntil = performance.now() / 1000 + PILE_FLIGHT + sheen;
      const out = sheen ? this.piles.find((p) => p.seat === (f.seat ?? 0) && p.pile === (f.effect === 'discard' ? 'discard' : 'deck')) : undefined;
      if (out) out.holdUntil = performance.now() / 1000 + sheen;
    }
    for (const p of this.piles) {
      if (p.holdUntil > performance.now() / 1000) continue; // shows the card once it has arrived (or, for a sacrifice, burst)
      const pv = view.players[p.seat];
      const count = p.pile === 'deck' ? pv.deckCount : pv.discard.length;
      const h = Math.max(0.08, count * 0.03);
      p.box.visible = p.top.visible = count > 0;
      p.box.scale.y = h;
      p.box.position.y = h / 2;
      p.edges.repeat.set(1, Math.max(1, count) / PILE_EDGE_ROWS); // one stripe row per card, however tall the pile
      p.top.position.y = h;
      if (p.pile === 'discard') {
        const topNum = pv.discard[0]?.num ?? null;
        if (topNum !== p.topNum) {
          p.topNum = topNum;
          p.topMat.map = topNum === null ? this.backTexture() : this.frontTexture(topNum);
          p.topMat.needsUpdate = true;
        }
      }
      const shuffles = pv.shuffles[p.pile];
      if (p.shuffles >= 0 && shuffles !== p.shuffles) p.shuffleT = 1.1;
      p.shuffles = shuffles;
      // The Remaining moved within this pile: a slower, heavier shudder, with deep red spirits circling and rising off it
      const jumps = pv.jumps?.[p.pile] ?? 0;
      if (p.jumps >= 0 && jumps !== p.jumps) {
        p.shuffleT = Math.max(p.shuffleT, 2.0);
        p.jumpT = 2.0;
        this.effects.play('remainingJump', p.group);
      }
      p.jumps = jumps;
      p.searching = pv.searching === p.pile;
      this.pileLabel(p, count);
    }

    // a bar that has just filled up flashes once, so you see there is no room left in it
    for (const f of this.barFrames) {
      const pv = view.players[f.seat];
      const now = pv ? (f.bar === 'gerzite' ? pv.gerzite : pv.darkGerzite) : 0;
      if (f.had >= 0 && now >= f.holds && f.had < f.holds) this.flashBar(f.seat, f.bar);
      f.had = now;
    }

    // opponents' hands (card backs on their side)
    for (const row of this.oppHands) {
      const opp = view.players[row.seat];
      const n = opp?.handCount ?? 0;
      // a card in play can keep both hands in plain view (Lexicon of Truth): then the row shows the cards' faces, turned to read from your seat
      const open = opp?.openHand ?? null;
      const key = open ? `open:${open.map((c) => c.num).join(',')}` : `backs:${n}`;
      const room = view.teams ? 0.55 : 1; // 2v2: two rows side by side
      if (key !== row.key) {
        row.key = key;
        row.group.clear();
        const spacing = open ? Math.min(CW * 1.05, (70 * room) / Math.max(1, n)) : Math.min(CW * 0.8, (60 * room) / Math.max(1, n));
        for (let i = 0; i < n; i++) {
          const m = new THREE.Mesh(this.geo.front, new THREE.MeshLambertMaterial({ map: open ? this.frontTexture(open[i].num) : this.backTexture() }));
          m.position.set((i - (n - 1) / 2) * spacing, 0.03 + i * 0.004, -CONFIG.zones.opponentHandRow.z);
          m.rotation.y = open ? 0 : Math.PI;
          m.castShadow = true;
          row.group.add(m);
        }
      }
      row.label.textContent = (opp?.out ? `${opp?.name ?? 'Opponent'} · out · ` : '') + (open ? `${opp?.name ?? 'Opponent'}’s hand · ${n} · in plain view (${view.handsOpen}) — click to look closer` : `${opp?.name ?? 'Opponent'}’s hand · ${n}`);
      row.label.classList.toggle('is-open', !!open);
    }
  }

  private isEmperorCard(num: number) {
    const def = CARD_DB.byNumber.get(num);
    return !!def && isEmperor(def);
  }

  private isEmperorData(c: CardView) {
    return c.num !== null && this.isEmperorCard(c.num);
  }

  private playEffect(obj: CardObj) {
    const num = obj.data.num;
    if (num === null) return;
    const def = CARD_DB.byNumber.get(num);
    if (!def) return;
    this.playOn(obj, isEmperor(def) ? 'emperor' : def.rarity);
  }

  /**
   * Entrance: the card drops onto the table from above and hits it hard. A card just played starts at the
   * top of the drop; one flipped face-up where it lies rises first. The impact effect plays as it lands.
   */
  private beginSlam(obj: CardObj, rises: boolean) {
    const E = CONFIG.abilityEffects.entrance;
    const now = performance.now() / 1000;
    if (!E.enabled) {
      this.playOn(obj, 'entrance');
      return;
    }
    obj.slam = { t0: now, y0: obj.root.position.y, rise: rises ? E.rise : 0, hit: false };
    if (!rises) obj.root.position.y = obj.y + E.height;
    obj.root.userData.fxY = obj.y; // its effects stay on the table while it is in the air
    // nothing else of the card's own (a Passive pulse, an aura) starts until the landing is over
    obj.fxUntil = Math.max(obj.fxUntil, now + obj.slam.rise + E.fall + this.effects.lengthOf('entrance'));
  }

  /**
   * Plays a one-off effect around a card right now, and notes when it will be over. Effects put in the card's
   * queue (Entrance, a Trigger's reveal pulse) and its Passive pulse wait for that moment, so they never
   * play over the card's rarity effect or over each other.
   */
  private playOn(obj: CardObj, kind: string) {
    this.effects.play(kind, obj.root);
    obj.fxUntil = Math.max(obj.fxUntil, performance.now() / 1000 + this.effects.lengthOf(kind));
  }

  /** Where the attacks and Spells just dealt land, and how long until they get there (see the token sync). */
  private hitDelays(fresh: FxEvent[], struck: Set<string | undefined>, spelled: Set<string | undefined>, blasting: boolean) {
    const A = CONFIG.combat.attackFx;
    const out: { x: number; z: number; delay: number }[] = [];
    const at = (id: string) => { const o = this.cards.get(id); return o ? { x: o.x, z: o.z } : this.goneAt.get(id) ?? null; };
    const add = (id: string | undefined, delay: number) => { const p = id ? at(id) : null; if (p && delay > 0) out.push({ x: p.x, z: p.z, delay }); };
    for (const id of struck) add(id, A.arrowSeconds + A.flightSeconds);
    for (const id of spelled) add(id, this.spellLandsOn(fresh, id));
    for (const f of fresh) if ((f.kind === 'attack' || f.kind === 'spell') && (f.effect === 'bounce' || f.effect === 'relay') && f.target) add(f.target, this.bounceDelay(fresh, f.target));
    // (a unit coming up out of the table: the counters it arrives with appear once it is up — Body Segments, Sand Crawlers)
    for (const f of fresh) if (f.kind === 'emerge') add(f.card, CONFIG.combat.emerge.seconds * 0.9);
    // (Deck-Out: the damage on the Emperor shows once what flew off the deck reaches it)
    for (const f of fresh) if (f.kind === 'look' && f.effect === 'deck-out') add(f.card, DECK_OUT_FLIGHT);
    // (a Spell that blows apart as it goes off: everything it hurts, as it bursts)
    if (blasting) for (const f of fresh) if (f.kind === 'damage' && !spelled.has(f.card)) add(f.card, SACRIFICE_BURST);
    return out;
  }
  /** How long until an attack or Spell the Great Hay Golem threw back reaches this card (0: none is on its way). */
  private bounceDelay(fresh: FxEvent[], id: string): number {
    const A = CONFIG.combat.attackFx;
    const S = CONFIG.combat.spellFx;
    if (A.enabled && fresh.some((a) => a.kind === 'attack' && (a.effect === 'bounce' || a.effect === 'relay') && a.target === id)) return A.arrowSeconds + A.flightSeconds * 2;
    if (S.enabled && fresh.some((a) => a.kind === 'spell' && a.effect === 'bounce' && a.target === id)) return this.spellLands() + S.flightSeconds;
    return 0;
  }

  /** A spot on the table for an effect that isn't on a card (a pile, where a token was). */
  private spotAt(x: number, y: number, z: number) {
    const o = new THREE.Object3D();
    o.position.set(x, y, z);
    o.userData.fxY = y;
    return o;
  }

  /**
   * A card's own effect (an Ancient's, CONFIG.looks): on the card, running from one card to another, on a pile,
   * or where a token was. The recipes are in effects.ts.
   */
  private playLook(f: FxEvent, afterCoin = false) {
    const name = f.effect;
    if (!name || name.endsWith('-gz')) return; // (Gerzite flying to a bar: the token itself carries it, see sync)
    if (name === 'tornado' && f.at) {
      // Tornado: the funnel over its lanes as its damage lands, a ring of wind round every lane it reaches
      const p = this.toView(f.at.x, f.at.z);
      const spot = this.spotAt(p.x, 0.1, p.z);
      spot.userData.ringW = (f.value ?? 0) + CW * 0.95;
      spot.userData.ringD = CH * 0.85;
      setTimeout(() => !this.disposed && this.effects.play(name, spot), Math.max(0, this.spellLands() - 0.5) * 1000);
      return;
    }
    // a coin's win shows once the coin has landed heads up
    if (!afterCoin && name.startsWith('coin-win') && this.freshNow.some((c) => c.kind === 'cure' && c.card === f.card)) {
      setTimeout(() => !this.disposed && this.playLook(f, true), CURE_LAND * 1000);
      return;
    }
    if (name === 'deck-out') {
      // Deck-Out: it flies off the empty deck into its own Emperor (the damage shows as it lands: hitDelays)
      const pile = this.piles.find((p) => p.seat === (f.seat ?? 0) && p.pile === 'deck');
      const emp = this.cards.get(f.card);
      if (!pile || !emp) return;
      this.attackFx.launch(pile.group.position.clone().setY(1.5), new THREE.Vector3(emp.x, emp.root.position.y, emp.z), 'deckOut', 0, DECK_OUT_FLIGHT, false, () => !this.disposed && this.rig.shake(0.25, 0.4));
        return;
    }
    if (name === 'gz-charge') {
      // Gerzite paid into the card flies off its player's bar, one after another, and the card glows brighter the
      // more it took (Gerzite Dagger, Crystalline Discharge)
      const obj = this.cards.get(f.card);
      const bar = this.barFrames.find((b) => b.bar === 'gerzite' && (this.view?.teams ? teamOf(b.seat) === teamOf(f.seat ?? 0) : b.seat === (f.seat ?? 0)));
      if (!obj) return;
      const n = Math.max(1, f.value ?? 1);
      if (bar) this.markers.stream(bar.frame.position.clone().setY(1), new THREE.Vector3(obj.x, 1, obj.z), CONFIG.playEffects.colors['gz-charge'] ?? ['#8fe3ff'], Math.min(n, 16), CHARGE_SECONDS);
      const tier = n >= 8 ? 3 : n >= 4 ? 2 : 1;
      setTimeout(() => !this.disposed && this.cards.get(f.card) && this.effects.play(`gz-charge-${tier}`, this.cards.get(f.card)!.root), CHARGE_SECONDS * 600);
      return;
    }
    if (name === 'seastorm') {
      // Primordial Seastorm: a great wave crashes across the whole table from one side, red lightning striking
      this.attackFx.seastorm(f.value ?? 0, CONFIG.table.width, CONFIG.table.depth, CONFIG.playEffects.colors.seastorm ?? ['#2a7fd6', '#d8f4ff', '#ff2a2a', '#ffb0a0']);
      this.rig.shake(0.6, 1.6);
      return;
    }
    if (f.pile && f.target) {
      // a card flying from a pile to a card on the table, where it shows once it lands (Soul Collector taking a Troop)
      const pile = this.piles.find((p) => p.seat === (f.seat ?? 0) && p.pile === f.pile);
      const t = this.cards.get(f.target);
      if (!pile || !t) return;
      t.root.visible = false;
      this.launch(pile.group.position.clone().setY(1.5), new THREE.Vector3(t.x, 1, t.z), { into: null, out: pile, arc: 10, fade: false, delay: 0, glow: CONFIG.playEffects.colors[name]?.[0] });
      setTimeout(() => {
        if (this.disposed) return;
        t.root.visible = true;
        this.effects.play(name, t.root);
      }, PILE_FLIGHT * 1000);
      return;
    }
    if (f.pile && f.count) {
      // cards flying from a hand into its discard pile all at once, then the pile plays the effect (Banishment)
      const pile = this.piles.find((p) => p.seat === (f.seat ?? 0) && p.pile === f.pile);
      if (!pile || !this.view) return;
      const mine = f.seat === this.view.you && !this.readOnly;
      const row = this.oppHands.find((r) => r.seat === f.seat);
      const from = mine ? this.handTarget() : new THREE.Vector3(row?.group.position.x ?? 0, 0.6, (this.nearSeat(f.seat ?? 0) ? 1 : -1) * CONFIG.zones.opponentHandRow.z);
      const to = pile.group.position.clone().setY(1.5);
      for (let i = 0; i < Math.min(10, f.count); i++) this.launch(from.clone().add(new THREE.Vector3((i - (f.count - 1) / 2) * 2.5, 0, 0)), to.clone(), { into: pile, out: null, arc: 8, fade: false, delay: i * 0.04, glow: CONFIG.playEffects.colors[name]?.[0] });
      setTimeout(() => !this.disposed && this.effects.play(name, pile.group), (PILE_FLIGHT + 0.04 * f.count) * 1000);
      return;
    }
    const obj = f.card ? this.cards.get(f.card) : undefined;
    const target = f.target ? this.cards.get(f.target) : undefined;
    if (f.target) {
      // light running across: from the card (or where it was: a sacrificed Troop is already gone) to the other
      const at = obj ? { x: obj.x, z: obj.z } : f.from ? this.toView(f.from.x, f.from.z) : null;
      const travel = name.endsWith('-gust') ? GUST_TRAVEL : LOOK_TRAVEL;
      // (the card it reaches may already be gone: a Spell that went off at once and was discarded. Then it goes
      // to where that card landed)
      const end = target ? { x: target.x, z: target.z } : f.at ? this.toView(f.at.x, f.at.z) : null;
      if (!at || !end) return;
      if (!target) {
        const spot = this.spotAt(end.x, 0.4, end.z);
        if (name.endsWith('-gust')) this.markers.gust(new THREE.Vector3(at.x, 0, at.z), new THREE.Vector3(end.x, 0, end.z), CONFIG.playEffects.colors[name] ?? ['#ffffff'], travel);
        else this.markers.link(new THREE.Vector3(at.x, 0, at.z), new THREE.Vector3(end.x, 0, end.z), CONFIG.playEffects.colors[name] ?? ['#ffffff'], travel);
        if (obj && (name.endsWith('-beam') || name.endsWith('-gust'))) this.effects.play(name, obj.root);
        setTimeout(() => !this.disposed && this.effects.play(name, spot), travel * 1000);
        return;
      }
      // (a gust: wind and snow blowing across instead of a beam: Summit Tarragon's discount)
      if (name.endsWith('-gust')) this.markers.gust(new THREE.Vector3(at.x, 0, at.z), new THREE.Vector3(target.x, 0, target.z), CONFIG.playEffects.colors[name] ?? ['#ffffff'], travel);
      else this.markers.link(new THREE.Vector3(at.x, 0, at.z), new THREE.Vector3(target.x, 0, target.z), CONFIG.playEffects.colors[name] ?? ['#ffffff'], travel);
      if (obj && (name.endsWith('-beam') || name.endsWith('-gust'))) this.effects.play(name, obj.root); // the card flares as the beam leaves it (Occult Sorcerer, Voodoo Magic)
      const id = f.target;
      setTimeout(() => {
        const t = this.cards.get(id);
        if (!this.disposed && t) this.effects.play(name, t.root);
      }, travel * 1000);
      return;
    }
    if (f.pile) {
      const pile = this.piles.find((p) => p.seat === (f.seat ?? 0) && p.pile === f.pile);
      if (pile) this.effects.play(name, this.spotAt(pile.group.position.x, pile.group.position.y + pile.top.position.y, pile.group.position.z));
      return;
    }
    if (f.at) {
      // at a spot: where a token was (a Bell, a Shield breaking), a lane
      const p = this.toView(f.at.x, f.at.z);
      // (a lane blast that goes off as the Gerzite charging the Spell arrives waits for it: Crystalline Discharge)
      const wait = this.freshNow.some((g) => g.kind === 'look' && g.effect === 'gz-charge') && !f.card ? CHARGE_SECONDS * 1000 : 0;
      setTimeout(() => !this.disposed && this.effects.play(name, this.spotAt(p.x, 0.4, p.z)), wait);
      return;
    }
    if (obj) this.effects.play(name, obj.root, name === 'legion-banner' ? this.bannerTint(this.sideSeat(obj.data)) : undefined);
    else {
      // the card has just been destroyed (a Spell or attack that finished it): it plays where the card stood
      const gone = f.card ? this.goneAt.get(f.card) : undefined;
      if (gone) this.effects.play(name, this.spotAt(gone.x, 0.1, gone.z));
    }
  }

  /**
   * The River Keeper: every so often a flash of black and pale green light runs from it to each of its Bell
   * tokens in turn, and the Bell pulses as it arrives (CONFIG.looks.bellEvery).
   */
  /**
   * Grave Intercessor: over the Troop it chose, a ward of pale souls lingers, round after round, for as long as the
   * Intercessor stands ready to be destroyed in its place.
   */
  private guardScene(obj: CardObj, time: number) {
    if (!CONFIG.looks.enabled || !obj.data.guarded || obj.dragging) { obj.guardNext = 0; return; }
    if (obj.guardNext && time < obj.guardNext) return;
    const length = this.effects.lengthOf('intercede-ward');
    if (length <= 0) return;
    this.effects.play('intercede-ward', obj.root);
    obj.guardNext = time + Math.max(0.5, length - 0.6);
  }
  /** A Spell with a scene standing over each lane chosen for it (CONFIG.looks.laneScenes: Spatial Rift's black holes). */
  private laneScenes(obj: CardObj, time: number) {
    const c = obj.data;
    const cardName = c.num !== null ? CARD_DB.byNumber.get(c.num)?.name ?? '' : '';
    const scene = CONFIG.looks.enabled && c.num !== null && c.faceUp && !obj.expired && c.laneSpots?.length ? CONFIG.looks.laneScenes[cardName] : undefined;
    if (!scene) { obj.laneNext = 0; return; }
    if (obj.laneNext && time < obj.laneNext) return;
    const length = this.effects.lengthOf(scene);
    if (length <= 0) return;
    const tint = CONFIG.looks.laneTinted.includes(cardName) ? [this.seatColor(this.sideSeat(c)), '#ffffff'] : undefined;
    for (const sp of c.laneSpots!) { const p = this.toView(sp.x, sp.z); this.effects.play(scene, this.spotAt(p.x, 0.12, p.z), tint); }
    // (and over the Biome slot, for a card that forbids Biomes: Tectonic Shift)
    const biome = CONFIG.looks.biomeScenes[cardName];
    const slot = biome ? CONFIG.slots.find((sl) => sl.name === 'Biome') : undefined;
    if (biome && slot) { const p = this.toView(slot.x, slot.z); this.effects.play(biome, this.spotAt(p.x, 0.12, p.z)); }
    obj.laneNext = time + Math.max(0.5, length - 0.8); // (each round fades in as the last fades out: it never goes still)
  }
  /** Cagurn's Thorny Snares lying in wait: thorny vines and blowing sand over each snared lane, round after round. */
  private snareNext = 0;
  private snareScenes(time: number) {
    const list = this.view?.snares;
    if (!CONFIG.looks.enabled || !list?.length) { this.snareNext = 0; return; }
    if (this.snareNext && time < this.snareNext) return;
    const length = this.effects.lengthOf('lane-snare');
    if (length <= 0) return;
    for (const sp of list) { const p = this.toView(sp.x, sp.z); this.effects.play('lane-snare', this.spotAt(p.x, 0.12, p.z)); }
    this.snareNext = time + Math.max(0.5, length - 1.6); // (each round of vines grows in before the last has faded)
  }
  /** One fading speck of light left behind by something flying (Gerzite Mine's Gerzite on its way to the bar). */
  private trails: { sp: THREE.Sprite; t0: number }[] = [];
  private trailDot(at: THREE.Vector3, color: string) {
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: fxTextures().dot, color, transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending }));
    sp.position.copy(at);
    sp.scale.set(2.6, 2.6, 1);
    sp.renderOrder = 19;
    this.scene.add(sp);
    this.trails.push({ sp, t0: performance.now() / 1000 });
  }
  private animateTrails(time: number) {
    if (!this.trails.length) return;
    const life = CONFIG.looks.gainFlight.trailSeconds;
    this.trails = this.trails.filter((tr) => {
      const q = (time - tr.t0) / life;
      if (q >= 1) { this.scene.remove(tr.sp); tr.sp.material.dispose(); return false; }
      tr.sp.material.opacity = 0.9 * (1 - q);
      const sc = 2.6 * (1 - 0.6 * q);
      tr.sp.scale.set(sc, sc, 1);
      return true;
    });
  }
  private bellFlashes(obj: CardObj, time: number) {
    const L = CONFIG.looks;
    if (!L.enabled || obj.data.num === null || !obj.data.faceUp || CARD_DB.byNumber.get(obj.data.num)?.name !== 'The River Keeper') return;
    if (!obj.bellNext) { obj.bellNext = time + 1.5 + Math.random() * 2; return; }
    if (time < obj.bellNext) return;
    obj.bellNext = time + L.bellEvery[0] + Math.random() * (L.bellEvery[1] - L.bellEvery[0]);
    const bells = [...this.tokens.values()].filter((t) => t.def?.effect === 'bell' && t.data.by === obj.data.owner);
    const colors = CONFIG.playEffects.colors['bell-flash'] ?? ['#0a0c0a', '#b9f5c4'];
    bells.forEach((b, i) => {
      setTimeout(() => {
        if (this.disposed || !this.tokens.has(b.data.id) || !this.cards.has(obj.data.id)) return;
        this.markers.link(new THREE.Vector3(obj.x, 0, obj.z), new THREE.Vector3(b.x, 0, b.z), [colors[1], colors[0]], LOOK_TRAVEL);
        setTimeout(() => !this.disposed && this.tokens.has(b.data.id) && this.effects.play('bell-flash', b.mesh), LOOK_TRAVEL * 1000);
      }, i * 900);
    });
  }

  /**
   * The fogs that should be over the mat right now, as "kind|side" (side 1 = your half in your view, -1 = the
   * far half). A card with a field_aura covers its owner's half while it is face-up in play and its turns
   * aren't over, and only the other player is shown it (unless fieldAuras.showToCaster says otherwise).
   */
  private fogsWanted(): Set<string> {
    const out = new Set<string>();
    const you = this.view?.you;
    for (const obj of this.cards.values()) {
      const c = obj.data;
      if (c.num === null || !c.faceUp || obj.expired) continue;
      const kind = CARD_DB.byNumber.get(c.num)?.fieldAura;
      if (!kind) continue;
      const mine = you !== undefined && sideOf(c.owner) === sideOf(you); // yours or (2v2) your teammate's: your half of the mat
      if (mine && !CONFIG.fieldAuras.showToCaster) continue; // your own fog never gets in your own way
      out.add(`${kind}|${mine ? 1 : -1}`);
    }
    return out;
  }

  /**
   * The scene an active Biome or Tome shows, and the gap between one and the next: the card's own (an Epic or
   * Mythic Tome, from abilityEffects.ambient.byCard) or its affiliation's ("aura-volcanic"…). Null: the card is
   * face-down, its turns are over (a Destroyed token is on it), or it isn't one of the cards that have one.
   */
  /** Whether a card's own scene is on right now (abilityEffects.ambient.byCard `when`). */
  private ambientHolds(obj: CardObj, when: string): boolean {
    const c = obj.data;
    if (when === 'rested') return (c.restHits ?? 0) > 0; // Marlin Knight, while it has extra hits saved up
    if (when === 'actions') return (c.actions?.total ?? 1) > 1; // Cragged Skulker, once it has gained an attacking action
    if (when === 'biome') return !!c.asBiome; // Eldritch Realm Crosser, standing as the Biome
    if (when === 'untapped') return c.rot % 2 === 0; // Primordial Incinerator, while it isn't tapped
    if (when === 'segments') return [...this.cards.values()].some((o) => o.data.owner === c.owner && o.data.faceUp && o.data.num !== null && CARD_DB.byNumber.get(o.data.num)?.name === 'Leviathan Body Segment'); // Leviathan with Body Segments out
    return true;
  }
  private ambientOf(obj: CardObj): { effect: string; every: [number, number] } | null {
    const own = this.ownAmbientOf(obj);
    // Sand Wyrm at work: the Wyrm, and any Support card of its player's with no scene of its own, shows the Duned
    // sand scene while its boosts are reaching Duned cards (a card with a scene plays the sand along with it: see below)
    if (!own && obj.data.wyrm && obj.data.faceUp && !obj.expired && this.effects.lengthOf('aura-duned') > 0) return { effect: 'aura-duned', every: CONFIG.abilityEffects.ambient.every };
    return own;
  }
  private ownAmbientOf(obj: CardObj): { effect: string; every: [number, number] } | null {
    const A = CONFIG.abilityEffects.ambient;
    const c = obj.data;
    if (!A.enabled || c.num === null || !c.faceUp || obj.expired) return null;
    // marks that last a while, each with a look of its own so both players can see them (CONFIG.abilityEffects.ambient.marks)
    for (const [flag, mark] of Object.entries(A.marks)) if (c[flag as keyof typeof c] && this.effects.lengthOf(mark.effect) > 0) return mark;
    // wrapped in vines while it lasts: Tangleweed's Lock (dark seaweed), Wingwrap holding it to the ground (bright green)
    const vines = c.vined === 'tangle' ? 'aura-vines-tangle' : c.vined === 'wing' ? 'aura-vines' : null;
    if (vines && this.effects.lengthOf(vines) > 0) return { effect: vines, every: [-1.0, -0.8] };
    // an Undead Guard: its blue ward, for as long as it stands
    if (A.bulwark && CARD_DB.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'bulwark') && this.effects.lengthOf(A.bulwark.effect) > 0) return { effect: A.bulwark.effect, every: A.bulwark.every };
    const own = A.byCard[c.num];
    if (own && own.when && !this.ambientHolds(obj, own.when)) return null;
    if (own) {
      // a Calling Pillar's beacon grows with every Pillar its owner has in play: aura-pillar-1 … aura-pillar-5
      let effect = own.effect;
      if (/-1$/.test(effect) && CARD_DB.byNumber.get(c.num)?.whileInPlay.includes('calling')) {
        const mine = [...this.cards.values()].filter((o) => o.data.num === c.num && o.data.faceUp && o.data.owner === c.owner).length;
        const grown = effect.replace(/-1$/, `-${Math.max(1, Math.min(5, mine))}`);
        if (this.effects.lengthOf(grown) > 0) effect = grown;
      }
      // Cragged Skulker: a water geyser round its edge for each attacking action it has gained (aura-skulker-1 … 8)
      if (own.when === 'actions' && /-1$/.test(effect)) {
        const grown = effect.replace(/-1$/, `-${Math.max(1, Math.min(8, (c.actions?.total ?? 1) - 1))}`);
        if (this.effects.lengthOf(grown) > 0) effect = grown;
      }
      // Marlin Knight's glow grows with each extra hit it has saved up (aura-marlin-1 … 6)
      if (own.when === 'rested' && /-1$/.test(effect)) {
        const grown = effect.replace(/-1$/, `-${Math.max(1, Math.min(6, c.restHits ?? 1))}`);
        if (this.effects.lengthOf(grown) > 0) effect = grown;
      }
      // Twilight Seedling's aura grows a step for every few points of health it has gained
      if (own.when === 'grows' && /-1$/.test(effect)) {
        const grown = effect.replace(/-1$/, `-${Math.max(1, Math.min(5, 1 + Math.floor(obj.gained / Math.max(1, CONFIG.looks.seedlingStep))))}`);
        if (this.effects.lengthOf(grown) > 0) effect = grown;
      }
      return this.effects.lengthOf(effect) > 0 ? { effect, every: own.every ?? A.every } : null;
    }
    const def = CARD_DB.byNumber.get(c.num);
    if (!def) return null;
    const allowed = def.types.map((t) => A.cards[t]).find(Boolean);
    if (!allowed || (allowed !== 'any' && !allowed.includes(def.rarity))) return null;
    const kind = def.affiliations.map((a) => A.effect + a).find((k) => this.effects.lengthOf(k) > 0);
    return kind ? { effect: kind, every: A.every } : null;
  }

  /**
   * Called each frame once it is known which cards are spent: a card whose turns have just run out (a Destroyed
   * token has just landed on it) plays its ending, if it has one (the Self-Destructing Tome explodes).
   */
  private watchEndings() {
    const A = CONFIG.abilityEffects.ambient;
    for (const obj of this.cards.values()) {
      const was = obj.wasExpired;
      obj.wasExpired = obj.expired;
      if (was !== false || !obj.expired || this.settleExpiry || !A.enabled) continue;
      const own = obj.data.num !== null && obj.data.faceUp ? A.byCard[obj.data.num] : undefined;
      if (!own?.ends) continue;
      this.effects.stop(obj.root, [own.effect]); // its last rumble gives way to the real thing
      this.playOn(obj, own.ends);
    }
    this.settleExpiry = false;
  }

  /** Does this card (if you can see its face) have an ability tag from cards.csv? */
  private hasAbility(c: CardView, ability: string) {
    return c.num !== null && c.faceUp && !!CARD_DB.byNumber.get(c.num)?.abilities.includes(ability);
  }

  /** A die was rolled or thrown: tumble it from where it is to where it landed. */
  /** Takes a token off the table for good. */
  private removeToken(id: string, obj: TokenObj) {
    this.dropClock(obj);
    this.dropDrain(obj);
    this.scene.remove(obj.mesh);
    this.pickables = this.pickables.filter((p) => p !== obj.face);
    if (obj.counter) obj.counter.tex.dispose();
    obj.fx?.dispose();
    if (obj.die) {
      obj.die.cube.geometry.dispose();
      for (const m of obj.die.cube.material as THREE.Material[]) m.dispose();
    }
    this.tokens.delete(id);
  }
  private lingering = new Set<string>(); // tokens already gone from the game that stay in view a moment longer

  /** `isNew`: the die has only just been brought in (a cure roll): it is thrown in from where the event says. */
  private rollFx(f: FxEvent, isNew = false) {
    const obj = f.token ? this.tokens.get(f.token) : undefined;
    if (!obj?.die) return;
    if (obj.data.cureDie !== undefined) this.setDieFaded(obj, false); // thrown again: back in sight
    if (isNew && f.from) {
      const start = this.toView(f.from.x, f.from.z);
      obj.mesh.position.set(start.x, obj.y, start.z);
    }
    const from = obj.mesh.position.clone();
    const dist = Math.hypot(obj.x - from.x, obj.z - from.z);
    obj.die.yaw = (Math.random() - 0.5) * 1.1;
    obj.die.shown = obj.data.value;
    obj.die.target = dieQuat(obj.data.value, obj.die.yaw);
    obj.die.anim = {
      t0: performance.now() / 1000,
      dur: 0.7 + Math.min(0.5, dist / 90),
      from,
      axis: new THREE.Vector3(Math.random() - 0.5, (Math.random() - 0.5) * 0.4, Math.random() - 0.5).normalize(),
      turns: 2 + Math.random() * 1.5 + dist / 30,
      height: 2.2 + dist * 0.07,
    };
    // a cure die goes once everyone has had a moment to see what it landed on
    if (obj.data.cureDie !== undefined) obj.die.fadeAt = obj.die.anim.t0 + obj.die.anim.dur + CONFIG.turn.cureDie.fadeAfter;
  }

  private lastFx = 0;
  /**
   * Cards (and the counters on them) a Tornado pulls into its lane stay where they stood until its damage has
   * landed and the funnel is spinning, then slide across (the rules move them in the same moment they take the hit).
   */
  private heldAt = new Map<string, { x: number; z: number; until: number }>();
  private holdPulled(view: GameView, first: boolean) {
    if (first || this.skipFx) return;
    const fresh = view.fx.filter((f) => f.id > this.lastFx);
    if (!fresh.some((f) => f.kind === 'look' && f.effect === 'tornado')) return;
    const S = CONFIG.combat.spellFx;
    const wait = S.delay + S.flightSeconds + 0.9;
    const until = performance.now() / 1000 + wait;
    for (const f of fresh) {
      if (f.kind !== 'slot' || f.effect !== 'moved') continue;
      const obj = this.cards.get(f.card);
      const c = view.field.find((x) => x.id === f.card);
      if (!obj || !c) continue;
      this.heldAt.set(c.id, { x: obj.x, z: obj.z, until });
      for (const t of tokensOnCard(c, view.tokens)) { const o = this.tokens.get(t.id); if (o) this.heldAt.set(t.id, { x: o.x, z: o.z, until }); }
    }
    setTimeout(() => {
      const now = performance.now() / 1000;
      for (const [id, h] of this.heldAt) if (h.until <= now + 0.01) this.heldAt.delete(id);
      if (!this.disposed) this.resync();
    }, wait * 1000 + 30);
  }
  /** How long after a Spell is aimed until what it sends reaches its target (its damage shows then). */
  private spellLands() {
    const S = CONFIG.combat.spellFx;
    return this.spellLead + S.flightSeconds;
  }
  private freshNow: FxEvent[] = [];
  /** When a chaining Spell's jumps start: once its first flight from the Spell card has landed, if it made one in this update. */
  private hopBase(fresh: FxEvent[], card: string) {
    return fresh.some((f) => f.kind === 'spell' && f.card === card && f.effect !== 'hop') ? this.spellLands() : this.spellLead;
  }
  /** When the Spells of this update reach this card: a jump of a chaining Spell (Chain Lightning) lands one jump after the last. */
  private spellLandsOn(fresh: FxEvent[], id: string | undefined) {
    const S = CONFIG.combat.spellFx;
    let at = 0;
    for (const f of fresh) if (f.kind === 'spell' && f.target === id) at = Math.max(at, f.effect === 'volley' ? this.spellLead + (f.hop ?? 0) * S.volleyGap + S.flightSeconds * 0.7 : f.effect === 'hop' ? this.hopBase(fresh, f.card) + ((f.hop ?? 0) + 1) * S.hopSeconds : f.effect === 'bounce' ? this.spellLands() + S.flightSeconds : this.spellLands());
    return at || this.spellLands();
  }
  /** How long after a Spell is aimed until what it sends sets off (later for one that blows apart first: Arcane Explosion). */
  private spellLead = CONFIG.combat.spellFx.delay;
  private playFx(f: FxEvent) {
    if (f.kind === 'spell') {
      // a Spell reaching a Unit or Emperor: it sets off from where the Spell card is (it may already be gone)
      const S = CONFIG.combat.spellFx;
      const target = f.target ? this.cards.get(f.target) : undefined;
      const gone = !target && f.target ? this.goneAt.get(f.target) : undefined; // (the Spell destroyed it: it still flies to where it stood)
      // (or to a lane: one a Spell marks as it is cast, or an empty one it reaches)
      const laneAt = !f.target && f.at ? this.toView(f.at.x, f.at.z) : null;
      if (!S.enabled || (!target && !gone && !laneAt)) return;
      const src = f.effect === 'hop' ? undefined : this.cards.get(f.card); // (a jump sets off from the Unit it hit last)
      const at = f.from ? this.toView(f.from.x, f.from.z) : null;
      const from = src ? new THREE.Vector3(src.x, src.root.position.y, src.z) : at ? new THREE.Vector3(at.x, f.effect === 'hop' ? 1 : 0.1, at.z) : null;
      if (!from) return;
      const to = target ? new THREE.Vector3(target.x, target.root.position.y, target.z) : gone ? gone.clone() : new THREE.Vector3(laneAt!.x, 0.1, laneAt!.z);
      const storm = f.tint === 'submerged';
      // (thrown back by the Great Hay Golem: it sets off once the Spell that hit the Golem has landed)
      if (f.effect === 'volley') { this.attackFx.launch(from, to, f.tint ?? 'neutral', this.spellLead + (f.hop ?? 0) * S.volleyGap, S.flightSeconds * 0.7, false, undefined, true); return; }
      if (f.effect === 'hop') { this.attackFx.launch(from, to, f.tint ?? 'neutral', this.hopBase(this.freshNow, f.card) + (f.hop ?? 0) * S.hopSeconds, S.hopSeconds, false, undefined, true); return; }
      this.attackFx.launch(from, to, f.tint ?? 'neutral', f.effect === 'bounce' ? this.spellLands() : this.spellLead, storm ? S.stormSeconds : S.flightSeconds, false, undefined, true);
      return;
    }
    if (f.kind === 'look') {
      this.playLook(f);
      return;
    }
    if (f.kind === 'revealCard') {
      if (f.num !== undefined) this.cb.revealed?.(f.seat ?? 0, f.num, f.text ?? '');
      return;
    }
    if (f.kind === 'abilityAsk') {
      // you just put a Lock or a timer on an Emperor: say which ability it is for
      const emperor = this.cards.get(f.card);
      if (!emperor || !f.token || this.readOnly || f.seat !== this.view?.you) return;
      const at = this.screenPoint(emperor.x, emperor.z, 0.5);
      this.cb.askAbility?.(f.token, emperor.data, f.effect === 'lock' ? 'lock' : 'cooldown', at.x, at.y);
      return;
    }
    if (f.kind === 'peek') {
      // a player is looking at the top cards of a pile: it shows on the pile, bigger the more cards
      const pile = this.piles.find((p) => p.seat === (f.seat ?? 0) && p.pile === (f.effect === 'discard' ? 'discard' : 'deck'));
      if (pile) this.looks.play(pile.group.position, pile.top.position.y, pile.group.rotation.y, f.count ?? 1);
      return;
    }
    if (f.kind === 'pileMove' && f.tint && this.effects.lengthOf(f.tint) > 0) {
      // a pile move with a look of its own (Glace Glass): a sheen wipes over the pile, then the card flies off it,
      // face-up, in a white glow
      const seat = f.seat ?? 0;
      const from: PileName = f.effect === 'discard' ? 'discard' : 'deck';
      const source = this.piles.find((p) => p.seat === seat && p.pile === from);
      if (source) this.effects.play(f.tint, source.group);
      setTimeout(() => !this.disposed && this.pileFlight(seat, from, { glow: CONFIG.playEffects.colors[f.tint!]?.[1] ?? '#ffffff', face: f.num }), PILE_SHEEN * 1000);
      return;
    }
    if (f.kind === 'pileMove') {
      this.pileFlight(f.seat ?? 0, f.effect === 'discard' ? 'discard' : 'deck');
      return;
    }
    if (f.kind === 'barFull') {
      // a gain didn't fit: the bar flashes for both players, and its owner is told nothing (or less) was gained
      this.flashBar(f.seat ?? 0, f.effect === 'dark' ? 'dark' : 'gerzite');
      if (f.text && f.seat === this.view?.you && !this.readOnly) this.cb.notice(f.text);
      return;
    }
    if (f.kind === 'toHand') {
      // (a card with a glow of its own drew it: The Remaining deep red, Occult Sorcerer purple, Cosmic Beyonder sparkling)
      const glow = f.tint === 'remaining' ? CONFIG.playEffects.colors.remainingHand?.[0] : f.tint ? CONFIG.playEffects.colors[f.tint]?.[0] : undefined;
      this.handFlight(f.seat ?? 0, f.effect === 'discard' ? 'discard' : 'deck', f.count ?? 1, glow, f.tint && f.tint !== 'remaining' && this.effects.lengthOf(f.tint) > 0 ? f.tint : undefined);
      if (f.tint === 'remaining' && f.seat === this.view?.you && !this.readOnly) this.cb.remainingToHand?.();
      return;
    }
    if (f.kind === 'attackEnd') {
      if (f.ref !== undefined) this.markers.endArrow(f.ref); // whoever drew the arrow clicked it away
      return;
    }
    if (f.kind === 'ping') {
      if (!f.at) return;
      const p = this.toView(f.at.x, f.at.z);
      this.markers.ping(new THREE.Vector3(p.x, 0, p.z), this.seatColor(f.seat ?? 0), CONFIG.pointers.pingSeconds);
      if (f.text) this.cb.notice(f.text); // a loose turn timer ran out on this spot
      return;
    }
    if (f.kind === 'cure') {
      // a die was thrown to cure a status (Burn): the die is a real one on the table (its 'roll' event plays
      // the throw). What it means is told once it has landed: a green glow and the token goes, or it stays.
      const cured = f.effect === 'cured';
      setTimeout(() => {
        if (this.disposed) return;
        const still = this.cards.get(f.card);
        const token = f.token ? this.tokens.get(f.token) : undefined;
        if (cured && still) this.effects.play('heal', still.root);
        else if (f.effect === 'ended' && still) this.effects.play('statusNotice', still.root); // a card's own roll ended it
        else if (!cured && token) {
          token.pulse = performance.now() / 1000;
          this.effects.play('statusDamage', token.mesh);
        }
        if (f.text) this.cb.notice(f.text);
      }, CURE_LAND * 1000);
      return;
    }
    if (f.kind === 'status') {
      // a status token just did its work: it swells with a ring in the color of what happened
      const token = f.token ? this.tokens.get(f.token) : undefined;
      const card = this.cards.get(f.card);
      const kind = f.effect === 'heal' ? 'statusHeal' : f.effect === 'notice' ? 'statusNotice' : 'statusDamage';
      if (token) {
        token.pulse = performance.now() / 1000;
        this.effects.play(kind, token.mesh);
      } else if (card && f.effect !== 'damage') this.effects.play(kind, card.root); // the token is already gone (it ended as it acted)
      if (card && f.effect === 'heal') this.effects.play('heal', card.root);
      if (f.text && (f.seat === undefined || f.seat === this.view?.you)) this.cb.notice(f.text); // no seat = news for both players
      return;
    }
    const obj = this.cards.get(f.card);
    if (f.kind === 'attack') {
      // (either end may be a card the attack has just destroyed, a target or — Thorns — the attacker itself: the
      // attack is still shown, to and from where they stood)
      const target = f.target ? this.cards.get(f.target) : undefined;
      const at = f.at ? this.toView(f.at.x, f.at.z) : null;
      const gone = !target && f.target ? this.goneAt.get(f.target) : undefined;
      const to = target ? new THREE.Vector3(target.x, target.root.position.y, target.z) : gone ? gone.clone() : at ? new THREE.Vector3(at.x, 0, at.z) : null;
      const from = obj ? new THREE.Vector3(obj.x, obj.root.position.y, obj.z) : this.goneAt.get(f.card)?.clone();
      if (!from) return;
      const mine = !this.readOnly && (f.seat === undefined || f.seat === this.view?.you);
      // an attack the table deals itself: the arrow shows for a moment, then the attack flies across in its attacker's affiliation
      const A = CONFIG.combat.attackFx;
      if (f.effect === 'relay') {
        // sent on by a Spatial Rift (or the like): it goes on from the card it was aimed at, once it has got there
        const via = f.from ? this.toView(f.from.x, f.from.z) : null;
        if (to && via && A.enabled) this.attackFx.launch(new THREE.Vector3(via.x, 0.1, via.z), to.clone(), f.tint ?? 'neutral', A.arrowSeconds + A.flightSeconds, A.flightSeconds, false);
        return;
      }
      if (f.effect === 'bounce') {
        // the Great Hay Golem throws the attack back the way it came, once the attack on it has landed
        if (to && A.enabled) this.attackFx.launch(from, to.clone(), f.tint ?? 'neutral', A.arrowSeconds + A.flightSeconds, A.flightSeconds, false);
        return;
      }
      const dealt = (f.effect === 'strike' || f.effect === 'miss') && A.enabled;
      if (to && obj) this.markers.arrow(...this.arrowEnds(obj, to, !!target || !!gone), this.seatColor(f.seat ?? this.sideSeat(obj.data)), dealt ? (f.effect === 'miss' ? A.missArrowSeconds : A.arrowSeconds) : CONFIG.pointers.arrowSeconds, f.id, mine && !dealt);
      if (to && dealt) this.attackFx.launch(from, to.clone(), f.tint ?? 'neutral', A.arrowSeconds, A.flightSeconds, f.effect === 'miss');
      return;
    }
    if (!obj) return;
    if (f.kind === 'link') {
      const target = f.target ? this.cards.get(f.target) : undefined;
      if (target) this.markers.link(new THREE.Vector3(obj.x, 0, obj.z), new THREE.Vector3(target.x, 0, target.z), CONFIG.playEffects.colors.link ?? ['#ffe45c'], LINK_TRAVEL);
      return;
    }
    if (f.kind === 'emerge' && f.effect === 'spirit') {
      // Undead Legion: a Spirit Soldier rises out of its Undead Soldier's blue shards (they burst first)
      obj.spiritRise = { t0: performance.now() / 1000 + SPIRIT_RISE.after };
      setTimeout(() => !this.disposed && this.cards.get(obj.data.id) === obj && this.effects.play('spirit-rise', obj.root), SPIRIT_RISE.after * 1000);
      return;
    }
    if (f.kind === 'emerge') {
      // a summoned unit rising up out of the table, in ripples of water (Leviathan Body Segment)
      // (it waits under the table until its picture is ready, so it can't rise as a blank card and miss the moment:
      // a unit's first appearance draws its face and loads its art, which online can take a moment)
      obj.emerge = { t0: performance.now() / 1000, look: f.effect === 'sand' ? 'sand' : 'water', waiting: !this.faceReady(obj) };
      if (!obj.emerge.waiting) this.emergeLook(obj);
      return;
    }
    if (f.kind === 'play' || f.kind === 'reveal') {
      this.playEffect(obj);
      // Triggers pulse purple when they are flipped face-up (never when they are first put down)
      const pulse = CONFIG.abilityEffects.revealPulse;
      const def = obj.data.num !== null ? CARD_DB.byNumber.get(obj.data.num) : undefined;
      if (f.kind === 'reveal' && def?.types.some((t) => pulse.types.includes(t))) obj.fxQueue.push(pulse.effect);
      // a Building played from a hand is built in front of you (together with its rarity effect)
      const built = CONFIG.abilityEffects.built;
      if (f.kind === 'play' && def?.types.some((t) => built.types.includes(t))) this.playOn(obj, built.effect);
      // cards tagged "entrance" come down hard: the fall starts now, alongside the rarity effect
      if (this.hasAbility(obj.data, 'entrance')) this.beginSlam(obj, f.kind === 'reveal');
    } else if (f.kind === 'damage') this.effects.play('damage', obj.root);
    else if (f.kind === 'slot' && f.effect) {
      // (one a Tornado pulls in shows it as it slides across, once the damage has landed)
      const held = this.heldAt.get(obj.data.id);
      const wait = held ? held.until - performance.now() / 1000 : 0;
      if (wait > 0) setTimeout(() => !this.disposed && this.playOn(obj, f.effect!), wait * 1000);
      else this.playOn(obj, f.effect);
    }
    else if (f.kind === 'ambush') this.playOn(obj, 'ambush');
    else if (f.kind === 'freePlay') this.effects.play('freePlay', obj.root); // played for free: a light blue beacon, alongside its rarity effect
    else if (f.kind === 'decay') this.effects.play('spDecay', obj.root); // a Building lost SP as its owner's turn ended: blue cracks
    else if (f.kind === 'ability') this.playOn(obj, 'emperorAbility'); // an Emperor used one of its abilities
    else if (f.kind === 'tier') this.playOn(obj, (f.tier ?? 2) >= 3 ? 'tier3' : 'tier2');
  }

  /**
   * Where an attack arrow starts and ends: it leaves the attacker's card a little way toward the target,
   * and stops just short of the middle of a target card (or exactly on a bare spot of the table).
   */
  private arrowEnds(from: CardObj, to: THREE.Vector3, toCard: boolean): [THREE.Vector3, THREE.Vector3] {
    const a = new THREE.Vector3(from.x, from.root.position.y + 0.7, from.z);
    const b = to.clone();
    b.y += 0.7;
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const dist = Math.hypot(dx, dz) || 1;
    const lead = Math.min(2.6, dist * 0.25);
    a.x += (dx / dist) * lead;
    a.z += (dz / dist) * lead;
    if (toCard) {
      const short = Math.min(1.6, dist * 0.15);
      b.x -= (dx / dist) * short;
      b.z -= (dz / dist) * short;
    }
    return [a, b];
  }

  /** For trying out effects from the browser console: table.testEffect('legendary') */
  testEffect(kind: string) {
    const obj = [...this.cards.values()].find((c) => c.data.owner === this.view?.you) ?? [...this.cards.values()][0];
    if (obj) this.effects.play(kind, obj.root);
  }

  private pileLabel(p: PileObj, count: number) {
    const name = p.pile === 'deck' ? 'Deck' : 'Discard';
    let status = '';
    if (p.searching) status = 'Searching…';
    else if (p.jumpT > 0) status = 'Something stirs…';
    else if (p.shuffleT > 0) status = 'Shuffling…';
    const text = `${name} · ${count}${status ? ` · ${status}` : ''}`;
    if (p.label.textContent !== text) p.label.textContent = text;
    p.label.classList.toggle('is-active', !!status);
  }

  /** Spells that show they are active for as long as they are: a screen over their side, a dome over their card. */
  private syncSpellScenes(time: number) {
    const L = CONFIG.looks;
    if (!L.enabled) return;
    const screens: { key: string; side: 1 | -1; colors: string[] }[] = [];
    const domes: { key: string; card: THREE.Object3D; color: string }[] = [];
    for (const obj of this.cards.values()) {
      const c = obj.data;
      if (!c.faceUp || c.num === null || obj.expired) continue;
      const name = CARD_DB.byNumber.get(c.num)?.name ?? '';
      if (L.sideScreen[name]) screens.push({ key: c.id, side: obj.z >= 0 ? 1 : -1, colors: L.sideScreen[name] });
      if (L.cardDome[name]) domes.push({ key: c.id, card: obj.root, color: L.cardDome[name] });
      // Buffer an aura gives it (Scorching Drifts' Duned Units): the same dome a Buffer counter puts over a card
      if (c.bufferAura) domes.push({ key: `${c.id}|buffer`, card: obj.root, color: TOKEN_RECIPES.buffer?.color ?? '#8fd3ff' });
    }
    this.spellScenes.sync(screens, domes, CONFIG.mat);
    this.spellScenes.update(time);
  }

  /** Executioner: an axe over each lane a follow-up attack will come back to (see AxeMarks). */
  private syncAxes(view: GameView) {
    this.axes.sync((view.echoes ?? []).flatMap((e, i) => {
      const slot = CONFIG.slots.find((sl) => sl.name === e.lane);
      if (!slot) return [];
      const at = this.toView(slot.x * e.side, slot.z * e.side);
      const whose = e.foe === view.you ? 'your' : `${view.players[e.foe]?.name ?? 'their'}’s`;
      return [{
        key: `${e.lane}|${e.side}|${e.by}|${i}`,
        x: at.x, z: at.z, w: slot.width, d: slot.depth,
        label: e.dueNow ? `${e.by}’s axe returns as this turn ends · ${e.amount} each` : `${e.by}’s axe returns at the end of ${whose} turn · ${e.amount} each`,
        due: e.dueNow,
      }];
    }));
  }

  private toView(x: number, z: number) {
    return { x: x * this.flip, z: z * this.flip };
  }
  private toWorld(x: number, z: number) {
    return { x: x * this.flip, z: z * this.flip };
  }

  /** Has this card's face picture loaded (so it can be shown off)? */
  private faceReady(obj: CardObj) {
    const map = obj.frontMat.map as (THREE.Texture & { image?: { width?: number } }) | null;
    return !!map?.image && (map.image.width ?? 0) > 0;
  }
  /** What a unit coming up out of the table comes up through: sand (Sand Crawlers, Undead Legion's soldiers) or water. */
  private emergeLook(obj: CardObj) {
    if (obj.emerge?.look === 'sand') this.effects.play('sand-rise', this.spotAt(obj.x, 0.1, obj.z));
    else this.attackFx.emerge(new THREE.Vector3(obj.x, 0.05, obj.z));
  }

  private makeCard(c: CardView): CardObj {
    // the units this card can summon get their pictures ready now, so they are there the moment they come (Undead
    // Legion's soldiers rising out of the ground, Sand Crawlers…)
    const name = c.num !== null && c.faceUp ? CARD_DB.byNumber.get(c.num)?.name.toLowerCase() : undefined;
    if (name) for (const u of CARD_DB.spawnOnly) if (u.summonedBy.some((n) => n.toLowerCase() === name)) this.frontTexture(u.number);
    const root = new THREE.Group();
    const flipper = new THREE.Group();
    const frontMat = new THREE.MeshLambertMaterial({ map: this.frontTexture(c.num, false, c.form ?? 0) });
    const backMat = new THREE.MeshLambertMaterial({ map: this.backTexture() });
    const front = new THREE.Mesh(this.geo.front, frontMat);
    const back = new THREE.Mesh(this.geo.back, backMat);
    front.castShadow = back.castShadow = true;
    const pick: Pick = { kind: 'card', id: c.id };
    front.userData.pick = pick;
    back.userData.pick = pick;
    flipper.add(front, back);
    root.add(flipper);
    this.scene.add(root);
    this.pickables.push(front, back);
    const p = this.toView(c.x, c.z);
    const yaw = this.cardYaw(c);
    root.position.set(p.x, 3, p.z);
    root.rotation.y = yaw;
    flipper.rotation.z = c.faceUp ? 0 : Math.PI;
    return { data: c, root, flipper, frontMat, backMat, num: c.num, x: p.x, z: p.z, y: 0, yaw, dragging: false, passiveOff: false, under: [], aura: null, flying: false, hover: 0, phase: Math.random() * 6.28, shade: null, tokenFly: false, tokenGround: false, passiveNext: 0, fxUntil: 0, fxQueue: [], armedNext: 0, ambientNext: 0, ambientUntil: 0, expired: false, wasExpired: null, damage: 0, decay: 0, overheal: 0, gained: 0, doom: null, quiet: false, slam: null };
  }

  /** Shows a tiered card as a small stack: one extra card edge per merged copy. */
  private syncStack(obj: CardObj, tier: number) {
    const want = Math.max(0, tier - 1);
    while (obj.under.length > want) {
      const m = obj.under.pop()!;
      obj.flipper.remove(m);
      this.pickables = this.pickables.filter((p) => p !== m);
    }
    while (obj.under.length < want) {
      const i = obj.under.length + 1;
      const m = new THREE.Mesh(this.geo.front, obj.frontMat);
      m.position.set(0.5 * i, -0.05 * i, 0.5 * i);
      m.castShadow = true;
      m.userData.pick = { kind: 'card', id: obj.data.id } satisfies Pick;
      obj.flipper.add(m);
      obj.under.push(m);
      this.pickables.push(m);
    }
  }

  /** The soft shadow that stays on the table under a hovering card. */
  private ensureShade(obj: CardObj) {
    if (obj.shade) return;
    const g = new THREE.PlaneGeometry(CW * 1.5, CH * 1.4);
    g.rotateX(-Math.PI / 2);
    obj.shade = new THREE.Mesh(
      g,
      new THREE.MeshBasicMaterial({ map: fxTextures().soft, color: 0x000000, transparent: true, opacity: 0, depthWrite: false }),
    );
    obj.root.add(obj.shade);
  }

  /** The cards on the table as slot limits see them (in your view), leaving one out: the card being dragged. */
  private placedCards(except?: string): Placed[] {
    return [...this.cards.values()]
      .filter((c) => c.data.id !== except)
      .map((c) => ({ x: c.x, z: c.z, types: c.data.num !== null ? (CARD_DB.byNumber.get(c.data.num)?.types ?? null) : null, shares: this.sharesLane(c.data) }));
  }
  /** A summoned unit or a clone may share a lane with another unit (and doesn't take up its room). */
  private sharesLane(c: CardView) {
    return !!c.clone || !!c.attachedTo || (c.num !== null && !!CARD_DB.byNumber.get(c.num)?.sharesLane);
  }
  /** A card's row as slot limits see it: a clone may share a lane like a summoned unit. */
  private slotDefOf(c: CardView) {
    const def = c.num !== null ? CARD_DB.byNumber.get(c.num) : undefined;
    return def && c.clone ? { ...def, sharesLane: true } : def;
  }
  /** A card's full health as it is in play: its form and tier, or a clone's own health. */
  private fullHealth(c: CardView) {
    const def = c.num !== null ? CARD_DB.byNumber.get(c.num) : undefined;
    if (!def) return null;
    return c.clone?.health ?? formOf(def, c.form, c.tier).health;
  }
  /**
   * The card a summoned unit dropped at this spot would be attached to: one whose bottom edge it was dropped on
   * (the strip of the card nearest its owner). null if it isn't a summoned unit, or wasn't dropped there.
   */
  private attachTarget(x: number, z: number, data: CardView): CardObj | null {
    if (data.num === null || !CARD_DB.byNumber.get(data.num)?.spawnOnly || this.readOnly) return null;
    let best: CardObj | null = null;
    for (const c of this.cards.values()) {
      if (c.data.id === data.id || c.data.attachedTo || c.data.attachedTo === data.id) continue;
      const box = { x: c.x, z: c.z, rot: c.data.rot, scale: c.data.scale };
      if (!onCard({ x, z }, box)) continue;
      const half = ((c.data.rot % 2 === 1 ? CW : CH) / 2) * (c.data.scale ?? 1);
      const down = this.nearSeat(this.sideSeat(c.data)) ? 1 : -1; // toward its owner, in this view
      const into = (z - c.z) * down; // how far toward the bottom edge
      if (into < half * (1 - 2 * CONFIG.sharedLanes.attachBand)) continue;
      if (!best || c.data.layer > best.data.layer) best = c;
    }
    return best;
  }

  /** The top-most card under a spot on the table, if any. */
  private cardAt(x: number, z: number): CardObj | null {
    let best: CardObj | null = null;
    for (const c of this.cards.values()) {
      if (!onCard({ x, z }, { x: c.x, z: c.z, rot: c.data.rot, scale: c.data.scale })) continue;
      if (!best || c.data.layer > best.data.layer) best = c;
    }
    return best;
  }

  private discardAuraFor(c: CardView) {
    return c.num !== null && c.faceUp && !!CARD_DB.byNumber.get(c.num)?.discardAura;
  }

  private cardYaw(c: CardView) {
    const base = this.nearSeat(this.sideSeat(c)) ? 0 : Math.PI;
    return base - (c.rot * Math.PI) / 2;
  }

  private syncCard(obj: CardObj, c: CardView, rank: number, snap: boolean) {
    // cards tagged "tap" flash when they are turned sideways (the inactive position)
    if (!snap && !obj.quiet && obj.data.rot % 2 === 0 && c.rot % 2 === 1 && this.hasAbility(c, 'tap')) this.playOn(obj, 'tap');
    obj.data = c;
    const dark = this.summonedDark(c);
    if (obj.num !== c.num || !!obj.darkFace !== dark || (obj.form ?? 0) !== (c.form ?? 0)) {
      obj.num = c.num;
      obj.darkFace = dark;
      obj.form = c.form ?? 0;
      obj.frontMat.map = this.frontTexture(c.num, dark, c.form ?? 0);
      obj.frontMat.needsUpdate = true;
    }
    // a clone (From the Mirror Realm, Spectral Clone…) is tinted a pale spectral blue
    // …and so is a Spirit Soldier (Undead Legion)
    const pale = !!c.clone || (c.num !== null && c.faceUp && !!CARD_DB.byNumber.get(c.num)?.whileInPlay.includes('intangible'));
    if (!obj.cloneTint !== !pale) {
      obj.cloneTint = pale;
      obj.frontMat.color.set(pale ? CONFIG.cardFaces.cloneTint : '#ffffff');
    }
    if (!obj.dragging) {
      const p = this.toView(c.x, c.z);
      const held = this.heldAt.get(c.id);
      obj.x = held && performance.now() / 1000 < held.until ? held.x : p.x;
      obj.z = held && performance.now() / 1000 < held.until ? held.z : p.z;
    }
    obj.y = 0.03 + rank * 0.0025 + (c.tier - 1) * 0.05;
    this.syncStack(obj, c.tier);
    obj.flying = c.num !== null && c.faceUp && CARD_DB.byNumber.get(c.num)?.movement === 'flying';
    const wantsAura = this.discardAuraFor(c);
    if (wantsAura && !obj.aura) {
      obj.aura = new OrbitAura();
      obj.root.add(obj.aura.group);
    } else if (!wantsAura && obj.aura) {
      obj.aura.dispose();
      obj.aura = null;
    }
    // turn the shortest way round
    let yaw = this.cardYaw(c);
    while (yaw - obj.root.rotation.y > Math.PI) yaw -= Math.PI * 2;
    while (yaw - obj.root.rotation.y < -Math.PI) yaw += Math.PI * 2;
    obj.yaw = yaw;
    if (snap) {
      obj.root.position.set(obj.x, obj.y, obj.z);
      obj.root.rotation.y = yaw;
    }
  }

  /** Cards that have been sacrificed and are cracking apart on the table before they vanish. */
  private dying: { obj: CardObj; t0: number; away?: boolean }[] = [];
  /** Where cards that have just been destroyed stood: what destroyed them is still shown flying there. */
  private goneAt = new Map<string, THREE.Vector3>();

  /** The card is gone from the game; here it stays put, trembles while it cracks, and is cleared away when it bursts. */
  private beginSacrifice(obj: CardObj, delay = 0, burst?: string) {
    const id = obj.data.id;
    this.goneAt.set(id, new THREE.Vector3(obj.x, obj.root.position.y, obj.z)); // (an attack or Spell that destroyed it still flies there)
    if (this.goneAt.size > 60) this.goneAt.delete(this.goneAt.keys().next().value!);
    this.cards.delete(id);
    this.pickables = this.pickables.filter((p) => p.userData.pick?.id !== id);
    obj.aura?.dispose();
    obj.aura = null;
    if (this.hovered?.kind === 'card' && this.hovered.id === id) this.hovered = null;
    this.selected.delete(id);
    const crack = () => {
      if (this.disposed) return;
      // (a card that bursts its own way: Cursed Sarcophagus in gold, Gerzite Titan, a Biome the Incinerator burns away)
      this.effects.play(burst && this.effects.lengthOf(burst) > 0 ? burst : 'sacrifice', obj.root);
      // a Calling Pillar going as the call is answered bursts in bright red on top of that, so it can't be missed
      if (obj.data.num !== null && CARD_DB.byNumber.get(obj.data.num)?.whileInPlay.includes('calling')) this.effects.play('pillarShatter', obj.root);
    };
    if (delay > 0) setTimeout(crack, delay * 1000);
    else crack();
    this.dying.push({ obj, t0: performance.now() / 1000 + delay, ...(burst === 'spirit-away' ? { away: true } : {}) });
  }

  // ======================= a card going straight from one pile to the other =======================

  // `into`: the pile it lands on (null: it goes to a hand). `arc`: how high it climbs on the way. `fade`: it
  // thins out over the last part of the flight (a card reaching your own hand slips out of sight under the hand bar).
  private flights: { mesh: THREE.Mesh; glow: THREE.Sprite; from: THREE.Vector3; to: THREE.Vector3; t0: number; into: PileObj | null; arc: number; fade: boolean; out: PileObj | null; sparkle?: string }[] = [];

  private launch(from: THREE.Vector3, to: THREE.Vector3, opts: { into: PileObj | null; out: PileObj | null; arc: number; fade: boolean; delay: number; glow?: string; sparkle?: string; face?: number }) {
    const mesh = new THREE.Mesh(this.geo.front, new THREE.MeshBasicMaterial({ map: opts.face !== undefined ? this.frontTexture(opts.face) : this.backTexture(), transparent: true }));
    const color = opts.glow ?? CONFIG.playEffects.colors.pileIn?.[0] ?? '#39d7c6';
    const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: fxTextures().dot, color, transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending }));
    glow.scale.set(CW * (opts.glow ? 3.6 : 2.6), CW * (opts.glow ? 3.6 : 2.6), 1); // The Remaining's red glow is a little larger
    mesh.add(glow);
    mesh.renderOrder = 18;
    mesh.visible = false; // until its own moment comes (several cards leave one after another)
    this.scene.add(mesh);
    this.flights.push({ mesh, glow, from, to, t0: performance.now() / 1000 + opts.delay, into: opts.into, arc: opts.arc, fade: opts.fade, out: opts.out, ...(opts.sparkle ? { sparkle: opts.sparkle } : {}) });
  }

  /**
   * Cards that have just left the table for a discard pile (a destroyed Troop once it has burst, a Spell that is
   * done, a card discarded) fly there, face-up, and the pile shows them once they land. A card that left for
   * somewhere else (back to a hand, into a deck) doesn't: only as many fly to a pile as it gained, and of those cards.
   */
  private lastDiscard: number[][] = [];
  private discardFlights(view: GameView, departures: { num: number; owner: Seat; from: THREE.Vector3; delay: number }[], first: boolean) {
    const was = this.lastDiscard;
    this.lastDiscard = view.players.map((p) => p.discard.map((c) => c.num));
    if (first || !CONFIG.discardFlight.enabled) return;
    view.players.forEach((pv, seat) => {
      const before = was[seat];
      if (!before) return;
      const gained = pv.discard.length - before.length;
      if (gained <= 0) return;
      const fresh = pv.discard.slice(0, gained).map((c) => c.num); // (the top of a pile is its first card)
      const pile = this.piles.find((p) => p.seat === seat && p.pile === 'discard');
      if (!pile) return;
      const now = performance.now() / 1000;
      // (Glace Glass: what goes to this discard pile now waits for the sheen and the card leaving it for the deck)
      const after = this.freshNow.some((f) => f.kind === 'pileMove' && !!f.tint && (f.seat ?? 0) === seat) ? PILE_SHEEN + PILE_FLIGHT * 0.7 : 0;
      for (const d0 of departures) {
        const d = after ? { ...d0, delay: d0.delay + after } : d0;
        if (d.owner !== seat) continue;
        const k = fresh.indexOf(d.num);
        if (k < 0) continue;
        fresh.splice(k, 1);
        const to = pile.group.position.clone().setY(1.5);
        this.launch(d.from.clone(), to, { into: pile, out: null, arc: 7, fade: false, delay: d.delay, face: d.num, glow: CONFIG.discardFlight.glow });
        pile.holdUntil = Math.max(pile.holdUntil, now + d.delay + PILE_FLIGHT);
      }
    });
  }
  /** The top card of one pile lifts off, arcs across the table and drops onto the other; both piles light up. */
  private pileFlight(seat: Seat, from: PileName, look: { glow?: string; face?: number } = {}) {
    const source = this.piles.find((p) => p.seat === seat && p.pile === from);
    const into = this.piles.find((p) => p.seat === seat && p.pile !== from);
    if (!source || !into) return;
    const lift = new THREE.Vector3(0, 1.5, 0);
    this.launch(source.group.position.clone().add(lift), into.group.position.clone().add(lift), { into, out: source, arc: 14, fade: false, delay: 0, ...(look.glow ? { glow: look.glow } : {}), ...(look.face !== undefined ? { face: look.face } : {}) });
  }

  /**
   * Cards going from a pile to a hand (a draw, the top discard taken back, a card taken out of a search): each
   * lifts off the pile and flies to the hand, one after another. Your own fly toward you and slip under your
   * hand bar; your opponent's fly to the row of card backs on their side. Both players see it.
   */
  private handFlight(seat: Seat, from: PileName, count: number, glow?: string, sparkle?: string) {
    const source = this.piles.find((p) => p.seat === seat && p.pile === from);
    if (!source || !this.view) return;
    const mine = seat === this.view.you;
    const row = this.oppHands.find((r) => r.seat === seat);
    const to = mine ? this.handTarget() : new THREE.Vector3(row?.group.position.x ?? 0, 0.6, (this.nearSeat(seat) ? 1 : -1) * CONFIG.zones.opponentHandRow.z);
    const start = source.group.position.clone();
    start.y = 1.5;
    for (let i = 0; i < Math.max(1, Math.min(10, count)); i++) {
      this.launch(start.clone(), to.clone(), { into: null, out: source, arc: mine ? 5 : 9, fade: mine, delay: i * 0.16, ...(glow ? { glow } : {}), ...(sparkle ? { sparkle } : {}) });
    }
  }

  /** The spot in the air, between the table and the camera, that lies behind the middle of your hand bar. */
  private handTarget() {
    const cam = this.rig.camera;
    const pt = this.cb.handPoint?.();
    if (!pt) return new THREE.Vector3(0, 8, CONFIG.table.depth / 2 + 12);
    const n = this.ndc(pt.x, pt.y);
    const dir = new THREE.Vector3(n.x, n.y, 0.5).unproject(cam).sub(cam.position).normalize();
    // about half-way from the camera to the table: there a card looks the size of the cards in the hand bar
    return cam.position.clone().addScaledVector(dir, cam.position.length() * 0.5);
  }

  private animateFlights(time: number) {
    if (!this.flights.length) return;
    this.flights = this.flights.filter((f) => {
      const k = (time - f.t0) / PILE_FLIGHT;
      if (k < 0) return true; // not its turn yet
      if (!f.mesh.visible) {
        f.mesh.visible = true;
        if (f.out) this.effects.play('pileOut', f.out.group); // it lifts off: the pile it leaves lights up
        f.out = null;
        if (f.sparkle) this.effects.play(f.sparkle, f.mesh); // sparkles that go with it all the way (Cosmic Beyonder)
      }
      if (k >= 1) {
        this.scene.remove(f.mesh);
        (f.mesh.material as THREE.Material).dispose();
        f.glow.material.dispose();
        if (f.into) this.effects.play('pileIn', f.into.group); // it lands: the pile takes it in
        return false;
      }
      const e = k * k * (3 - 2 * k);
      f.mesh.position.lerpVectors(f.from, f.to, e);
      f.mesh.position.y += Math.sin(Math.PI * k) * f.arc; // an arc, so it is seen crossing the table
      f.mesh.rotation.y = e * Math.PI * 2; // one turn on the way
      const thin = f.fade && k > 0.82 ? 1 - (k - 0.82) / 0.18 : 1;
      (f.mesh.material as THREE.MeshBasicMaterial).opacity = thin;
      f.glow.material.opacity = 0.85 * Math.sin(Math.PI * Math.min(1, k * 1.1)) * thin;
      return true;
    });
  }

  private animateDying(time: number) {
    if (!this.dying.length) return;
    this.dying = this.dying.filter(({ obj, t0, away }) => {
      if (away) {
        // a Spirit Soldier whose General has fallen: it shrinks as it drifts up and away, in its pale blue light
        const q = (time - t0) / SPIRIT_AWAY.seconds;
        if (q >= 1) {
          this.scene.remove(obj.root);
          obj.frontMat.dispose();
          obj.backMat.dispose();
          return false;
        }
        const e = q * q * (3 - 2 * q);
        const r = obj.root;
        r.position.set(obj.x + Math.sin(q * 5 + obj.phase) * 0.6 * q, obj.y + 0.3 + SPIRIT_AWAY.height * e, obj.z - 1.5 * e);
        const sc = Math.max(0.02, 1 - 0.92 * e);
        r.scale.set(sc, 1, sc);
        r.rotation.y = obj.yaw + Math.sin(q * 4) * 0.25 * q;
        return true;
      }
      const k = (time - t0) / SACRIFICE_BURST;
      if (k < 0) return true; // (the attack that destroys it is still on its way)
      if (k >= 1) {
        this.scene.remove(obj.root);
        obj.frontMat.dispose();
        obj.backMat.dispose();
        return false;
      }
      // it shudders harder and harder, lifting slightly, until it gives way
      const r = obj.root;
      const shake = 0.05 + 0.22 * k * k + (r.userData.rumble ?? 0) * 0.25; // (harder under a 'rumble': a Biome the Incinerator burns away)
      r.position.set(obj.x + (Math.random() - 0.5) * shake, obj.y + (typeof r.userData.lift === 'number' ? r.userData.lift : 0.5 * k), obj.z + (Math.random() - 0.5) * shake); // (Arcane Explosion rises high)
      r.rotation.z = (Math.random() - 0.5) * 0.05 * k;
      obj.frontMat.color.setScalar(1 - 0.45 * k); // the card darkens as the aura takes it
      return true;
    });
  }

  /**
   * A face-up card whose damage has reached its health is out: it goes dark under a web of cracks,
   * inside a slow red ring, until its owner discards it. (Nothing is discarded for you.)
   */
  private syncDoom(obj: CardObj, time: number) {
    const def = obj.data.num !== null && obj.data.faceUp ? CARD_DB.byNumber.get(obj.data.num) : undefined;
    const full = def ? this.fullHealth(obj.data) : null;
    // out: no health (or SP) left. Or it has a skull on it: once destroyed, healing doesn't bring it back.
    const out = !!full && (Math.max(0, full + obj.gained - obj.damage) + obj.overheal <= 0 || obj.expired);
    if (out && !obj.doom) {
      const t = fxTextures();
      const plane = (w: number, h: number) => {
        const g = new THREE.PlaneGeometry(w, h);
        g.rotateX(-Math.PI / 2);
        return g;
      };
      const red = CONFIG.playEffects.colors.damage?.[0] ?? '#ff4b3a';
      const group = new THREE.Group();
      const shade = new THREE.Mesh(cardFlat(CW, CH), new THREE.MeshBasicMaterial({ color: 0x14030a, transparent: true, opacity: 0.62, depthWrite: false }));
      const fit = CONFIG.playEffects.crackScale; // the cracks stop a little short of the card's edges
      const cracks = new THREE.Mesh(cardFlat(CW * fit, CH * fit), new THREE.MeshBasicMaterial({ map: t.cracks, color: 0x050102, transparent: true, opacity: 0.95, depthWrite: false }));
      const glow = new THREE.Mesh(plane(CW * 1.03 * fit, CH * 1.03 * fit), new THREE.MeshBasicMaterial({ map: t.cracks, color: red, transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending }));
      // a red outline hugging the card's edge, like the bright edge of a card selected in your hand
      const edge = new THREE.MeshBasicMaterial({ map: this.outlineTexture(), color: red, transparent: true, opacity: 0, depthWrite: false });
      const rim = new THREE.Mesh(plane(CW + OUTLINE_PAD * 2, CH + OUTLINE_PAD * 2), edge);
      shade.position.y = 0.02;
      glow.position.y = 0.024;
      cracks.position.y = 0.028;
      rim.position.y = 0.019;
      rim.renderOrder = 6; // over the glowing outline of the slot the card lies in, so it stays red
      group.add(rim, shade, glow, cracks);
      obj.root.add(group);
      obj.doom = { group, ring: edge, shade };
    } else if (!out && obj.doom) {
      obj.doom.group.removeFromParent();
      obj.doom.group.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry.dispose();
          (o.material as THREE.Material).dispose();
        }
      });
      obj.doom = null;
    }
    if (obj.doom) {
      obj.doom.ring.opacity = 0.82 + 0.18 * Math.sin(time * 2.6 + obj.phase); // steady, with a slow breath
      obj.doom.shade.visible = !obj.expired; // the skull on it already greys the card: no need to darken it twice
    }
  }

  private barFrames: { seat: Seat; bar: 'gerzite' | 'dark'; frame: THREE.Mesh; mat: THREE.MeshBasicMaterial; t0: number; holds: number; had: number }[] = [];
  /** A glowing rounded frame for something `w` by `h` cm, with `pad` cm of room round it for the glow. White, to be tinted. */
  private frameTexture(w: number, h: number, pad: number) {
    const perCm = 16;
    const c = document.createElement('canvas');
    c.width = Math.round((w + pad * 2) * perCm);
    c.height = Math.round((h + pad * 2) * perCm);
    const ctx = c.getContext('2d')!;
    ctx.strokeStyle = '#fff';
    ctx.shadowColor = '#fff';
    ctx.lineWidth = 0.22 * perCm;
    for (const blur of [0.7 * perCm, 0.3 * perCm, 0]) {
      ctx.shadowBlur = blur;
      ctx.beginPath();
      ctx.roundRect((pad - 0.25) * perCm, (pad - 0.25) * perCm, (w + 0.5) * perCm, (h + 0.5) * perCm, 0.6 * perCm);
      ctx.stroke();
    }
    return new THREE.CanvasTexture(c);
  }
  /** A bar is full: its outline flashes three times. */
  private flashBar(seat: Seat, bar: 'gerzite' | 'dark') {
    const f = this.barFrames.find((x) => x.seat === teamOf(seat) && x.bar === bar); // 2v2: the team's shared bar
    if (f) f.t0 = performance.now() / 1000;
  }
  private animateBars(time: number) {
    for (const f of this.barFrames) {
      if (f.t0 < 0) continue;
      const k = (time - f.t0) / 1.5;
      if (k >= 1) {
        f.t0 = -1;
        f.frame.visible = false;
        continue;
      }
      f.frame.visible = true;
      f.mat.opacity = Math.abs(Math.sin(Math.PI * 3 * k)) * (1 - 0.35 * k);
    }
  }

  private outlineTex: THREE.Texture | null = null;
  /** A crisp rounded outline with a soft glow outside it, drawn in white so it can be tinted any color. */
  private outlineTexture() {
    if (this.outlineTex) return this.outlineTex;
    const perCm = 40; // pixels per centimeter
    const w = Math.round((CW + OUTLINE_PAD * 2) * perCm);
    const h = Math.round((CH + OUTLINE_PAD * 2) * perCm);
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d')!;
    const gap = 0.04 * perCm; // the line sits just off the card's edge
    const line = 0.24 * perCm;
    const x = OUTLINE_PAD * perCm - gap - line / 2;
    const y = OUTLINE_PAD * perCm - gap - line / 2;
    const radius = (CONFIG.card.cornerRadius / 500) * CW * perCm + gap + line / 2;
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = line;
    ctx.shadowColor = '#fff';
    for (const blur of [0.5 * perCm, 0.22 * perCm, 0]) {
      ctx.shadowBlur = blur;
      ctx.beginPath();
      ctx.roundRect(x, y, w - x * 2, h - y * 2, radius);
      ctx.stroke();
    }
    this.outlineTex = new THREE.CanvasTexture(c);
    this.outlineTex.anisotropy = 4;
    return this.outlineTex;
  }

  private lingerCards = new Set<string>(); // Spells gone from the game that stay in view until what they set off has played out
  /** How long a Spell that just left the table stays on it, so what it set off can play out first (0 = it goes now). */
  /** Seconds until the All Stop pulse (the 'allstop-pulse' shockwave: 260 across over 1.4s, easing out) reaches a card. */
  private pulseReaches(at: { x: number; z: number }, obj: CardObj) {
    const p = this.toView(at.x, at.z);
    const r = Math.hypot(obj.x - p.x, obj.z - p.z);
    const k = 1 - Math.cbrt(Math.max(0, 1 - Math.max(0, r - 3) / 257));
    return Math.min(1.4, k * 1.4);
  }
  private holdFor(id: string, obj: CardObj, fresh: FxEvent[]): number {
    const def = obj.data.num !== null ? CARD_DB.byNumber.get(obj.data.num) : undefined;
    const recalled = (target: string | undefined) => !!target && fresh.some((f) => f.kind === 'look' && f.effect === 'recall-swirl' && f.card === target);
    // Recall Rift: the Unit it returns stays until its swirl has wound up round it (the swirl starts as the Rift's light lands)
    if (recalled(id)) return Math.min(4, this.spellLandsOn(fresh, id) + RECALL_SECONDS);
    if (!def?.types.includes('spell')) return 0;
    let hold = 0;
    for (const f of fresh) {
      if (f.card !== id) continue;
      // …and the Rift itself goes to the discard pile only after that
      if (f.kind === 'spell' && recalled(f.target)) hold = Math.max(hold, this.spellLandsOn(fresh, f.target) + RECALL_SECONDS + 0.35);
      if (f.kind === 'spell') hold = Math.max(hold, this.spellLands() + 0.35);
      else if (f.kind === 'look' && f.effect === 'gz-charge') hold = Math.max(hold, CHARGE_SECONDS + 0.9);
      else if (f.kind === 'look') hold = Math.max(hold, 1.3);
      else if (f.kind === 'play') hold = Math.max(hold, 0.9); // (cast and gone in one go: it is seen landing first)
      else if (f.kind === 'status') hold = Math.max(hold, 0.6);
      else if (f.kind === 'spent') hold = Math.max(hold, 0.9); // (seen lying there a moment, at the least)
      else if (f.kind === 'cure') hold = Math.max(hold, CURE_LAND + 1.5); // (a coin: flipped, landed, then its win shown)
    }
    return Math.min(hold, 4);
  }
  private removeCard(id: string, obj: CardObj) {
    this.scene.remove(obj.root);
    if (obj.armedNext) this.effects.stop(obj.root, [CONFIG.abilityEffects.setAura.effect, CONFIG.abilityEffects.setAura.loop]); // a set Trigger's beam goes with it
    obj.aura?.dispose();
    obj.frontMat.dispose();
    obj.backMat.dispose();
    this.pickables = this.pickables.filter((p) => p.userData.pick?.id !== id);
    this.cards.delete(id);
  }

  /** Loads a token's picture once, plus a small transparency map for cutout clicking. */
  private loadTokenArt(tokenId: string): Promise<TokenArt> {
    let p = this.tokenArt.get(tokenId);
    if (p) return p;
    p = tokenSrc(tokenId)
      .then((src) => loadImageEl(src))
      .then((img) => {
        const c = document.createElement('canvas');
        c.width = c.height = ALPHA_PX;
        const ctx = c.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(img, 0, 0, ALPHA_PX, ALPHA_PX);
        let alpha: Uint8ClampedArray | null = null;
        try {
          alpha = ctx.getImageData(0, 0, ALPHA_PX, ALPHA_PX).data;
        } catch {
          alpha = null; // picture from another site: click the whole square instead
        }
        return { img, alpha };
      })
      .catch(() => ({ img: null, alpha: null }));
    this.tokenArt.set(tokenId, p);
    return p;
  }

  /** Draws a token's top face: its picture (inside a chip for round tokens) and its number if it's a counter. */
  private paintFace(ctx: CanvasRenderingContext2D, shape: 'round' | 'cutout', art: TokenArt | null, value: number | null, badge: boolean | { from: number; fill: string } = false, prefix = '', unset = false) {
    ctx.clearRect(0, 0, 256, 256);
    if (shape === 'round') {
      ctx.save();
      ctx.beginPath();
      ctx.arc(128, 128, 127, 0, Math.PI * 2);
      ctx.clip();
      ctx.fillStyle = '#25282d';
      ctx.fillRect(0, 0, 256, 256);
      if (art?.img) ctx.drawImage(art.img, 0, 0, 256, 256);
      ctx.restore();
    } else if (art?.img) {
      ctx.drawImage(art.img, 0, 0, 256, 256);
    }
    if (value === null) return;
    // a turn timer still waiting for its number shows "?" in amber, so it stands out from the set ones
    const text = unset ? '?' : prefix + String(value);
    // a counter that keeps its picture (Entrenched) shows the picture alone at 1, the usual case;
    // from 2 up its number appears in the middle, like every other counter
    if (badge === true && value <= 1) return;
    if (badge && badge !== true) {
      // a Lock's cost: a small corner badge in the color of what it is paid with, the padlock left whole
      if (value < badge.from) return;
      ctx.fillStyle = badge.fill;
      ctx.beginPath();
      ctx.arc(176, 180, 62, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 6;
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.stroke();
      ctx.font = `700 ${text.length > 1 ? 70 : 92}px "Alegreya Sans", "Trebuchet MS", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(text, 176, 186);
      return;
    }
    ctx.fillStyle = 'rgba(0,0,0,0.38)';
    ctx.beginPath();
    ctx.arc(128, 128, shape === 'round' ? 84 : 62, 0, Math.PI * 2);
    ctx.fill();
    const big = shape === 'round' ? 128 : 100;
    ctx.font = `700 ${text.length > 2 ? big * 0.72 : big}px "Alegreya Sans", "Trebuchet MS", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 10;
    ctx.strokeStyle = 'rgba(0,0,0,0.6)';
    ctx.strokeText(text, 128, 138);
    ctx.fillStyle = unset ? '#ffc94a' : '#ffffff';
    ctx.fillText(text, 128, 138);
  }

  private faceCanvas() {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 256;
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    return { canvas, tex };
  }

  /** Plain (non-counter) tokens of one kind share a single picture. */
  private sharedFace(def: TokenDef): THREE.CanvasTexture {
    let tex = this.tokenFaces.get(def.id);
    if (tex) return tex;
    const f = this.faceCanvas();
    tex = f.tex;
    this.tokenFaces.set(def.id, tex);
    void this.loadTokenArt(def.id).then((art) => {
      this.paintFace(f.canvas.getContext('2d')!, def.shape, art, null);
      f.tex.needsUpdate = true;
    });
    return tex;
  }

  private dieFaces = new Map<string, THREE.CanvasTexture>();
  private dieTexture(color: string, value: number) {
    const key = `${color}:${value}`;
    let tex = this.dieFaces.get(key);
    if (!tex) {
      tex = new THREE.CanvasTexture(drawDieFace(color, value));
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      this.dieFaces.set(key, tex);
    }
    return tex;
  }

  /** A six-sided die: a cube with pips that lies with its current number facing up. */
  private makeDie(t: Token, def: TokenDef): TokenObj {
    const size = def.size ?? CONFIG.dice.size;
    const color = dieColor(def.id);
    const cube = new THREE.Mesh(
      new THREE.BoxGeometry(size, size, size),
      DIE_SIDES.map((v) => new THREE.MeshLambertMaterial({ map: this.dieTexture(color, v) })),
    );
    cube.position.y = size / 2;
    cube.castShadow = true;
    cube.userData.pick = { kind: 'token', id: t.id } satisfies Pick;
    const yaw = (Math.random() - 0.5) * 1.1;
    const target = dieQuat(t.value, yaw);
    cube.quaternion.copy(target);
    const mesh = new THREE.Group().add(cube);
    const p = this.toView(t.x, t.z);
    mesh.position.set(p.x, 4, p.z);
    this.scene.add(mesh);
    this.pickables.push(cube);
    return {
      data: t, def, shape: 'round', mesh, face: cube, shadow: null, counter: null, art: null, height: size,
      x: p.x, z: p.z, y: 0.34, dragging: false, offTable: false, fx: null, on: null, wait: 0, pulse: 0, clock: null, drainBadge: null,
      die: { cube, shown: t.value, yaw, target, anim: null, fadeAt: 0, faded: false },
    };
  }
  /** Hides a cure die (its roll is over), or shows it again for a new roll. */
  private setDieFaded(obj: TokenObj, faded: boolean) {
    if (!obj.die || obj.die.faded === faded) return;
    obj.die.faded = faded;
    obj.mesh.visible = !faded;
    const i = this.pickables.indexOf(obj.die.cube);
    if (faded && i >= 0) this.pickables.splice(i, 1);
    if (!faded && i < 0) this.pickables.push(obj.die.cube);
    for (const m of obj.die.cube.material as THREE.MeshLambertMaterial[]) {
      m.opacity = 1;
      m.transparent = false;
    }
  }

  private makeToken(t: Token): TokenObj {
    const def = TOKEN_DB.byId.get(t.tokenId);
    if (def?.category === 'dice') {
      const die = this.makeDie(t, def);
      if (t.cureDie !== undefined) this.setDieFaded(die, true); // a cure die lying from an old roll: out of sight (a new roll shows it)
      return die;
    }
    const shape = def?.shape ?? 'round';
    const size = def?.size ?? CONFIG.token.size;
    let counter: TokenObj['counter'] = null;
    let map: THREE.Texture;
    if (def?.counter || def?.effect === CONFIG.tokenRules.lockToken || (def?.bar && t.asCounter)) {
      // its own picture: a counter shows its number, a Lock shows what it costs to remove, Gerzite on the
      // table (a counter) its amount
      const f = this.faceCanvas();
      counter = { ...f, value: Number.NaN };
      map = f.tex;
    } else if (def) {
      map = this.sharedFace(def);
    } else {
      map = this.faceCanvas().tex;
    }

    const pick: Pick = { kind: 'token', id: t.id };
    let mesh: THREE.Object3D;
    let face: THREE.Mesh;
    let shadow: THREE.Mesh | null = null;
    let height: number;
    if (shape === 'round') {
      const geo = new THREE.CylinderGeometry(size / 2, size / 2, TOKEN_H, 48);
      const side = new THREE.MeshLambertMaterial({ color: 0x1d1f23 });
      face = new THREE.Mesh(geo, [side, new THREE.MeshLambertMaterial({ map }), side]);
      face.rotation.y = Math.PI / 2; // so the picture on top faces you
      face.position.y = TOKEN_H / 2;
      face.castShadow = true;
      mesh = new THREE.Group().add(face);
      height = TOKEN_H;
    } else {
      // a flat cutout that follows the picture's outline, with a shadow in the same shape
      const geo = new THREE.PlaneGeometry(size, size);
      geo.rotateX(-Math.PI / 2);
      face = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ map, transparent: true, alphaTest: 0.4 }));
      face.position.y = 0.06;
      shadow = new THREE.Mesh(
        geo,
        new THREE.MeshBasicMaterial({ map, color: 0x000000, transparent: true, opacity: 0.5, depthWrite: false }),
      );
      shadow.scale.set(1.05, 1, 1.05);
      mesh = new THREE.Group().add(shadow, face);
      height = 0.06;
    }
    face.userData.pick = pick;
    const p = this.toView(t.x, t.z);
    mesh.position.set(p.x, 4, p.z);
    this.scene.add(mesh);
    this.pickables.push(face);
    const obj: TokenObj = { data: t, def, shape, mesh, face, shadow, counter, art: null, height, x: p.x, z: p.z, y: 0.34, dragging: false, offTable: false, fx: null, on: null, die: null, wait: 0, pulse: 0, clock: null, drainBadge: null };
    const recipe = def ? TOKEN_RECIPES[def.effect] : undefined;
    if (recipe?.parts.length) {
      obj.fx = new TokenFx(recipe, size, face, height);
      mesh.add(obj.fx.group);
    }
    void this.loadTokenArt(t.tokenId).then((art) => {
      obj.art = art;
      if (obj.counter) this.paintCounter(obj, true);
    });
    return obj;
  }

  /**
   * The little clock on a counter that also counts turns: it rests on the counter's top right corner, so the
   * counter's own number (how much) and the clock's number (how many turns are left) can both be read.
   */
  private syncClock(obj: TokenObj) {
    const rules = CONFIG.turn.durations;
    const turns = rules.enabled && !obj.die ? (obj.data.turns ?? 0) : 0;
    if (turns <= 0) {
      this.dropClock(obj);
      return;
    }
    if (!obj.clock) {
      const size = (obj.def?.size ?? CONFIG.token.size) * rules.clock.size;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 128;
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      const side = new THREE.MeshLambertMaterial({ color: rules.clock.rim });
      const mesh = new THREE.Mesh(new THREE.CylinderGeometry(size / 2, size / 2, CLOCK_H, 32), [side, new THREE.MeshLambertMaterial({ map: tex }), side]);
      mesh.rotation.y = Math.PI / 2; // so the number on top faces you, like the counter's own
      const off = (obj.def?.size ?? CONFIG.token.size) * rules.clock.offset;
      mesh.position.set(off, obj.height + CLOCK_H / 2 + 0.005, -off);
      obj.mesh.add(mesh);
      obj.clock = { mesh, canvas, tex, shown: Number.NaN };
    }
    if (obj.clock.shown === turns) return;
    obj.clock.shown = turns;
    const look = rules.clock;
    const ctx = obj.clock.canvas.getContext('2d')!;
    ctx.clearRect(0, 0, 128, 128);
    ctx.fillStyle = look.rim;
    ctx.beginPath();
    ctx.arc(64, 64, 64, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = turns === 1 ? look.last : look.face; // its last turn: the face warms up
    ctx.beginPath();
    ctx.arc(64, 64, 54, 0, Math.PI * 2);
    ctx.fill();
    // the marks round a clock face, and two faint hands behind the number
    ctx.strokeStyle = look.rim;
    ctx.lineCap = 'round';
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const long = i % 3 === 0;
      ctx.lineWidth = long ? 5 : 3;
      ctx.beginPath();
      ctx.moveTo(64 + Math.sin(a) * (long ? 40 : 44), 64 - Math.cos(a) * (long ? 40 : 44));
      ctx.lineTo(64 + Math.sin(a) * 50, 64 - Math.cos(a) * 50);
      ctx.stroke();
    }
    ctx.globalAlpha = 0.28;
    ctx.strokeStyle = look.hand;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(64, 64);
    ctx.lineTo(64, 24);
    ctx.moveTo(64, 64);
    ctx.lineTo(92, 64);
    ctx.stroke();
    ctx.globalAlpha = 1;
    const text = String(turns);
    ctx.font = `800 ${text.length > 1 ? 58 : 76}px "Alegreya Sans", "Trebuchet MS", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 8;
    ctx.strokeStyle = turns === 1 ? look.last : look.face;
    ctx.strokeText(text, 64, 69);
    ctx.fillStyle = look.rim;
    ctx.fillText(text, 64, 69);
    obj.clock.tex.needsUpdate = true;
  }
  /**
   * The wear-off badge: a small disc on the counter's top left corner (the turn clock takes the right), with a
   * down arrow and how much of the counter's number is still to wear off.
   */
  private syncDrain(obj: TokenObj) {
    const rules = CONFIG.turn.durations;
    const drain = rules.enabled && !obj.die ? (obj.data.drain ?? 0) : 0;
    if (drain <= 0) {
      this.dropDrain(obj);
      return;
    }
    if (!obj.drainBadge) {
      const size = (obj.def?.size ?? CONFIG.token.size) * rules.clock.size;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 128;
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      const side = new THREE.MeshLambertMaterial({ color: rules.drain.rim });
      const mesh = new THREE.Mesh(new THREE.CylinderGeometry(size / 2, size / 2, CLOCK_H, 32), [side, new THREE.MeshLambertMaterial({ map: tex }), side]);
      mesh.rotation.y = Math.PI / 2;
      const off = (obj.def?.size ?? CONFIG.token.size) * rules.clock.offset;
      mesh.position.set(-off, obj.height + CLOCK_H / 2 + 0.005, -off);
      obj.mesh.add(mesh);
      obj.drainBadge = { mesh, canvas, tex, shown: Number.NaN };
    }
    if (obj.drainBadge.shown === drain) return;
    obj.drainBadge.shown = drain;
    const look = rules.drain;
    const ctx = obj.drainBadge.canvas.getContext('2d')!;
    ctx.clearRect(0, 0, 128, 128);
    ctx.fillStyle = look.rim;
    ctx.beginPath();
    ctx.arc(64, 64, 64, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = look.face;
    ctx.beginPath();
    ctx.arc(64, 64, 54, 0, Math.PI * 2);
    ctx.fill();
    // a down arrow on the left, the number on the right
    ctx.fillStyle = look.arrow;
    ctx.beginPath();
    ctx.moveTo(22, 52);
    ctx.lineTo(54, 52);
    ctx.lineTo(38, 84);
    ctx.closePath();
    ctx.fill();
    ctx.fillRect(32, 30, 12, 24);
    const text = String(drain);
    ctx.font = `800 ${text.length > 1 ? 50 : 66}px "Alegreya Sans", "Trebuchet MS", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = look.rim;
    ctx.fillText(text, 82, 69);
    obj.drainBadge.tex.needsUpdate = true;
  }
  private dropDrain(obj: TokenObj) {
    if (!obj.drainBadge) return;
    obj.mesh.remove(obj.drainBadge.mesh);
    obj.drainBadge.mesh.geometry.dispose();
    for (const m of obj.drainBadge.mesh.material as THREE.Material[]) m.dispose();
    obj.drainBadge.tex.dispose();
    obj.drainBadge = null;
  }
  private dropClock(obj: TokenObj) {
    if (!obj.clock) return;
    obj.mesh.remove(obj.clock.mesh);
    obj.clock.mesh.geometry.dispose();
    for (const m of obj.clock.mesh.material as THREE.Material[]) m.dispose();
    obj.clock.tex.dispose();
    obj.clock = null;
  }

  private paintCounter(obj: TokenObj, force = false) {
    const c = obj.counter;
    if (!c) return;
    const ctx = c.canvas.getContext('2d')!;
    if (obj.def?.bar) {
      // Gerzite on the table: its amount in a corner badge, "+2" in green if it adds to the bar each turn,
      // "−2" in red if it takes away, plain in the resource's own color otherwise
      const flow = obj.data.flow;
      const key = obj.data.value * 10 + (flow ? (flow.mode === 'add' ? 1 : 2) + (flow.when === 'end' ? 2 : 0) : 0);
      if (!force && c.value === key) return;
      c.value = key;
      const fill = flow?.mode === 'add' ? '#2f9e4f' : flow?.mode === 'remove' ? '#c0392b' : obj.def.bar === 'dark' ? '#8f1139' : '#1f6fa8';
      const sign = flow?.mode === 'add' ? '+' : flow?.mode === 'remove' ? '−' : '';
      this.paintFace(ctx, obj.shape, obj.art, Math.max(1, obj.data.value), { from: 1, fill }, sign);
      c.tex.needsUpdate = true;
      return;
    }
    if (!obj.def?.counter) {
      // a Lock: its unlock cost in a corner badge, blue for Gerzite and dark red for Dark Gerzite
      const price = obj.data.price ?? 0;
      const key = obj.data.priceDark ? -price : price;
      if (!force && c.value === key) return;
      c.value = key;
      this.paintFace(ctx, obj.shape, obj.art, price > 0 ? price : null, { from: 1, fill: obj.data.priceDark ? '#8f1139' : '#1f6fa8' });
      c.tex.needsUpdate = true;
      return;
    }
    const unset = !!obj.data.unset;
    const key = unset ? -1 : obj.data.value; // -1 = showing "?"
    if (!force && c.value === key) return;
    c.value = key;
    // the Count counter reads "×3": three of them left
    this.paintFace(ctx, obj.shape, obj.art, obj.data.value, !unset && CONFIG.tokenRules.badgeCounters.includes(obj.def?.effect ?? ''), obj.def?.effect === CONFIG.tokenRules.countToken ? '×' : '', unset);
    c.tex.needsUpdate = true;
  }

  /** For cutout tokens: is the spot under the mouse part of the picture, or a transparent gap? */
  private solidAt(obj: TokenObj, uv: THREE.Vector2 | undefined) {
    const alpha = obj.art?.alpha;
    if (obj.shape !== 'cutout' || !alpha || !uv) return true;
    const x = Math.min(ALPHA_PX - 1, Math.max(0, Math.floor(uv.x * ALPHA_PX)));
    const y = Math.min(ALPHA_PX - 1, Math.max(0, Math.floor((1 - uv.y) * ALPHA_PX)));
    return alpha[(y * ALPHA_PX + x) * 4 + 3] > 100;
  }

  // ======================= input =======================

  private bindInput() {
    const el = this.renderer.domElement;
    // two fingers on a touch screen: pinch to zoom, move both to slide the view (set up first, so a second
    // finger is known to be part of a pinch before onDown sees it)
    this.pinching = pinchable(
      el,
      (scale, _mx, _my, dx, dy) => {
        const dist = Math.max(20, this.rig.camera.position.y * 1.3);
        this.rig.zoom((-dist * (1 - 1 / scale)) / 0.06);
        this.rig.pan(dx, dy);
      },
      () => this.onCancel(), // whatever the first finger started stops
    );
    el.addEventListener('pointerdown', this.onDown);
    el.addEventListener('pointermove', this.onMove);
    el.addEventListener('pointerup', this.onUp);
    el.addEventListener('pointercancel', this.onCancel);
    el.addEventListener('pointerleave', this.onLeave);
    el.addEventListener('contextmenu', this.onContext);
    el.addEventListener('wheel', this.onWheel, { passive: false });
    el.addEventListener('auxclick', (e) => e.preventDefault());
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  private typing() {
    const a = document.activeElement;
    return a instanceof HTMLInputElement || a instanceof HTMLTextAreaElement || document.querySelector('.backdrop') !== null;
  }

  /** Is this token one of the turn timers (Start of Turn, End of Turn, Count-Up)? */
  private isTimer(t: TokenObj) {
    const L = CONFIG.turn.lifetime;
    return !!t.def && (t.def.effect === L.timer || Object.values(L.timers).includes(t.def.effect) || this.countsUp(t));
  }
  /** Is this the timer that counts up (Arcane Charge's)? */
  private countsUp(t: TokenObj) {
    return t.def?.effect === CONFIG.turn.countUp.token;
  }

  private altHeld = false; // Alt (Option) is down: kept here too, in case a mouse event arrives without its Alt flag
  private placing = false; // a card in your hand is selected: the next click on the table places it

  /** A hand card was selected (or let go of): while one is, a click on the table places it there. */
  setPlacing(on: boolean, cardId?: string) {
    if (this.readOnly) return;
    this.placing = on;
    if (!on) this.endHandDrag();
    else if (cardId) this.showSlots(this.handCardNum(cardId)); // its slots light up at once
  }

  private onKey = (e: KeyboardEvent) => {
    if (this.typing()) return;
    if (e.key === 'Alt') {
      e.preventDefault(); // Alt is the pointing key here: keep the browser's menu out of it
      this.altHeld = true;
    }
    if (['KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE', 'ShiftLeft', 'ShiftRight'].includes(e.code)) this.rig.setKey(e.code, true);
    if (e.code === 'KeyF' && !e.repeat) this.flipHovered();
    if (e.key === 'Escape') {
      this.clearSelection();
      this.cancelStrike();
    }
    // quick remove: hold X and click things, or point at something and press Delete
    if (e.code === 'KeyX' && !e.repeat) {
      this.removeKey = true;
      this.removeHint();
    }
    // quick copy: hold C and click a token or counter
    if (e.code === 'KeyC' && !e.repeat && !e.ctrlKey && !e.metaKey) {
      this.cloneKey = true;
      this.removeHint();
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && !e.repeat && !this.pointer) {
      e.preventDefault();
      if (this.selected.size) this.removeSelection();
      else this.quickRemove(this.hovered);
    }
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'Alt') {
      e.preventDefault();
      this.altHeld = false;
    }
    this.rig.setKey(e.code, false);
    if (e.code === 'KeyX') {
      this.removeKey = false;
      this.removeHint();
    }
    if (e.code === 'KeyC') {
      this.cloneKey = false;
      this.removeHint();
    }
  };
  private onBlur = () => {
    this.rig.clearKeys();
    this.altHeld = false;
    this.removeKey = false;
    this.cloneKey = false;
    this.removeHint();
  };

  // ======================= quick remove =======================

  private removeKey = false; // X is being held
  private cloneKey = false; // C is being held
  private lastMouse = { x: 0, y: 0 };

  /** What quick remove would do to the thing under the cursor. */
  private removeVerb(pick: Pick | null): 'delete' | 'discard' | null {
    if (!pick || pick.kind === 'pile' || this.readOnly) return null;
    if (pick.kind === 'token') return 'delete';
    const c = this.cards.get(pick.id);
    if (!c || !this.canControlCard(c.data)) return null;
    if (c.data.spawned) return 'delete';
    return this.isEmperorData(c.data) ? null : 'discard'; // Emperors can't be discarded
  }

  /** Tokens, counters and spawned cards are deleted; your own cards go to your discard pile. */
  private quickRemove(pick: Pick | null) {
    const verb = this.readOnly ? null : this.removeVerb(pick);
    if (!verb || !pick || pick.kind === 'pile') return;
    // one of several selected things: they all go together
    if (this.selected.size > 1 && this.selected.has(pick.id)) {
      this.removeSelection();
      this.hovered = null;
      this.removeHint();
      return;
    }
    if (verb === 'delete') this.cb.send({ type: 'delete', id: pick.id });
    else this.cb.send({ type: 'toPile', id: pick.id, pile: 'discard', where: 'top' });
    this.hovered = null;
    this.removeHint();
  }

  /** How many things are selected, if this one is among them (0 if it isn't). */
  selectionWith(id: string) {
    return this.selected.has(id) ? this.selected.size : 0;
  }
  /** Removes everything that is selected: tokens and spawned cards are deleted, your own cards are discarded. */
  removeSelected() {
    if (!this.readOnly) this.removeSelection();
  }

  /** The same for everything that's selected. */
  private removeSelection() {
    const ids = [...this.selected];
    const discard = ids.filter((id) => {
      const c = this.cards.get(id);
      return !!c && !c.data.spawned && this.canControlCard(c.data) && !this.isEmperorData(c.data);
    });
    const remove = ids.filter((id) => this.tokens.has(id) || this.cards.get(id)?.data.spawned);
    this.selected.clear();
    if (remove.length) this.cb.send({ type: 'deleteMany', ids: remove });
    for (const id of discard) this.cb.send({ type: 'toPile', id, pile: 'discard', where: 'top' });
    this.refreshSelection();
  }

  /** While X is held, a label at the cursor says what a click will do. */
  private removeHint() {
    if (this.pointer) return;
    if (this.cloneKey && !this.removeKey) {
      const ok = !this.readOnly && this.hovered?.kind === 'token';
      this.dragHint(ok ? 'Click to copy' : null, 'info', this.lastMouse.x, this.lastMouse.y);
      if (ok) this.renderer.domElement.style.cursor = 'copy';
      return;
    }
    const verb = this.removeKey ? this.removeVerb(this.hovered) : null;
    this.dragHint(verb ? `Click to ${verb}` : null, 'delete', this.lastMouse.x, this.lastMouse.y);
    if (verb) this.renderer.domElement.style.cursor = 'crosshair';
  }

  private onWheel = (e: WheelEvent) => {
    e.preventDefault();
    this.rig.zoom(e.deltaMode === 1 ? e.deltaY * 30 : e.deltaY);
  };

  private ndc(clientX: number, clientY: number) {
    const r = this.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
  }

  private pick(clientX: number, clientY: number): Pick | null {
    this.raycaster.setFromCamera(this.ndc(clientX, clientY), this.rig.camera);
    const hits = this.raycaster.intersectObjects(this.pickables, false);
    // tokens sit on top of cards, so prefer them when both are under the mouse;
    // clicks on the see-through parts of a cutout token go to whatever is underneath
    const solid = hits.filter((h) => {
      const pk = h.object.userData.pick as Pick | undefined;
      if (pk?.kind !== 'token') return true;
      const obj = this.tokens.get(pk.id);
      return !obj || this.solidAt(obj, h.uv);
    });
    const token = solid.find((h) => h.object.userData.pick?.kind === 'token');
    // (an empty pile has nothing to show, but its spot can still be clicked)
    const hit = token ?? solid.find((h) => (h.object.visible || h.object.userData.pick?.kind === 'pile') && h.object.userData.pick);
    return (hit?.object.userData.pick as Pick) ?? null;
  }

  private groundPoint(clientX: number, clientY: number): THREE.Vector3 | null {
    this.raycaster.setFromCamera(this.ndc(clientX, clientY), this.rig.camera);
    const p = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(this.ground, p) ? p : null;
  }

  /** In a game (not the solo table) the table plays the rules itself: nothing is moved, turned, flipped or changed by hand. */
  private handsOff() {
    return CONFIG.lockHandEdits && this.view?.mode !== 'solo';
  }
  /** May a card already on the table be picked up and dragged? (in a game: only a clone, or a spent card being cleared away) */
  private canDragCard(obj: CardObj) {
    return this.canControlCard(obj.data) && (!this.handsOff() || !!obj.data.clone || obj.expired);
  }
  /** May a card from the hand be dropped into this pile? (in a game: only into your discard pile, in your Overdraw Phase) */
  private handDropOk(pile: PileName) {
    if (!this.handsOff()) return true;
    return pile === 'discard' && this.view?.turn === this.view?.you && this.view?.phase === 0;
  }
  private canControlCard(c: CardView) {
    if (this.readOnly) return false;
    if (c.spawned || (this.view && this.allied(c.owner, this.view.you))) return true; // 2v2: your teammate's cards too
    // a Possession token on a card lets the other player use it
    const turned = c.rot % 2 === 1;
    const p = this.toView(c.x, c.z);
    return [...this.tokens.values()].some(
      (t) =>
        t.def?.effect === 'possession' &&
        Math.abs(t.x - p.x) <= (turned ? CH : CW) / 2 + 0.3 &&
        Math.abs(t.z - p.z) <= (turned ? CW : CH) / 2 + 0.3,
    );
  }


  private pinching: () => boolean = () => false;

  private onDown = (e: PointerEvent) => {
    this.hoverTip(false);
    if (e.button === 2 || this.pinching()) return;
    this.renderer.domElement.setPointerCapture(e.pointerId);
    const base = { id: e.pointerId, button: e.button, startX: e.clientX, startY: e.clientY, lastX: e.clientX, lastY: e.clientY };
    if (e.button === 1) {
      e.preventDefault();
      this.pointer = { ...base, pick: null, mode: 'look' };
      return;
    }
    if (e.button !== 0) return;
    this.pointer = { ...base, pick: this.pick(e.clientX, e.clientY), mode: 'pending', select: !this.placing && (e.ctrlKey || e.metaKey), alt: e.altKey || this.altHeld };
    this.hideChip();
  };

  private onMove = (e: PointerEvent) => {
    if (this.pinching()) return;
    const p = this.pointer;
    if (!p) {
      this.updateHover(e.clientX, e.clientY);
      if (this.striking) this.aimStrike(e.clientX, e.clientY);
      if (this.placing && !this.arrowHover) {
        // a selected hand card is only ever placed on the board: over a pile there is nothing to preview
        if (this.offBoard(this.hovered, e.clientX, e.clientY)) this.endHandDrag();
        else this.cb.placeHover?.(e.clientX, e.clientY, e.shiftKey, e.ctrlKey || e.metaKey);
      }
      return;
    }
    const dx = e.clientX - p.lastX;
    const dy = e.clientY - p.lastY;
    p.lastX = e.clientX;
    p.lastY = e.clientY;
    if (p.mode === 'look') this.rig.look(dx, dy);
    else if (p.mode === 'pan') this.rig.pan(dx, dy);
    else if (p.mode === 'pending') {
      if (Math.hypot(e.clientX - p.startX, e.clientY - p.startY) > 5) {
        if (p.select && !this.readOnly) this.beginMarquee(p, e);
        else if (p.alt) this.beginAim(p, e);
        else this.beginDrag(p, e);
      }
    } else if (p.mode === 'aim') this.continueAim(p, e);
    else if (p.mode === 'drag') this.continueDrag(p, e);
    else if (p.mode === 'group') this.continueGroup(p, e);
    else if (p.mode === 'marquee') this.updateMarquee(p, e);
  };

  // ======================= attack arrows and pings =======================

  /**
   * Alt + drag from one of your cards: draw an arrow from it to whatever it is attacking. Starting on a token that sits on the card counts as starting on
   * the card: cards usually carry an Entrenched, damage or tier token, and it is easy to grab one.
   */
  private beginAim(p: NonNullable<TableView['pointer']>, e: PointerEvent) {
    let obj = p.pick?.kind === 'card' ? this.cards.get(p.pick.id) : undefined;
    if (!obj) {
      const start = this.groundPoint(p.startX, p.startY);
      obj = (start && this.cardAt(start.x, start.z)) || undefined;
    }
    if (!obj || !this.canControlCard(obj.data)) {
      p.mode = 'pan';
      return;
    }
    p.mode = 'aim';
    p.aimFrom = obj.data.id;
    this.renderer.domElement.style.cursor = 'crosshair';
    this.continueAim(p, e);
  }

  private continueAim(p: NonNullable<TableView['pointer']>, e: PointerEvent) {
    const from = p.aimFrom ? this.cards.get(p.aimFrom) : undefined;
    const gp = this.groundPoint(e.clientX, e.clientY);
    if (!from || !gp) return;
    const over = this.cardAt(gp.x, gp.z);
    const overHand = this.cb.isOverHand(e.clientX, e.clientY);
    if (overHand || over === from) {
      // back on the attacker (or off the table): letting go here cancels
      p.aimAt = null;
      this.markers.setPreview(null);
      this.dragHint(null);
      return;
    }
    const target = over ?? null;
    p.aimAt = { card: target?.data.id ?? null, x: gp.x, z: gp.z };
    const to = target ? new THREE.Vector3(target.x, target.root.position.y, target.z) : new THREE.Vector3(gp.x, 0, gp.z);
    this.markers.setPreview(...this.arrowEnds(from, to, !!target), this.seatColor(this.view?.you ?? 0));
    const name = target ? this.nameOf({ kind: 'card', id: target.data.id }) : null;
    this.dragHint(name ? `Let go: attack ${name}` : 'Let go to point the attack here', 'merge', e.clientX, e.clientY);
  }

  private endAim(p: NonNullable<TableView['pointer']>) {
    this.markers.setPreview(null);
    const from = p.aimFrom ?? null;
    if (!from || !p.aimAt) return;
    if (p.aimAt.card) this.cb.send({ type: 'attack', from, to: p.aimAt.card });
    else this.cb.send({ type: 'attack', from, ...this.toWorld(p.aimAt.x, p.aimAt.z) });
  }

  /** Alt + click: flash a marker on the table that both players see. */
  private pingAt(clientX: number, clientY: number) {
    const gp = this.groundPoint(clientX, clientY);
    if (gp && !this.isOffTable(gp.x, gp.z)) this.cb.send({ type: 'ping', ...this.toWorld(gp.x, gp.z) });
  }

  // ======================= the chip over a face-up Trigger =======================

  private chipEl: HTMLElement | null = null;
  private chipCard: string | null = null;
  private chipTimer: ReturnType<typeof setTimeout> | null = null;

  /** Your own face-up Triggers get a small "Payout" chip while you point at them. */
  private wantsChip(pick: Pick | null): CardObj | null {
    if (pick?.kind !== 'card' || !this.view || this.readOnly) return null;
    const c = this.cards.get(pick.id);
    if (!c || !this.allied(c.data.owner, this.view.you) || !c.data.faceUp || c.data.num === null || c.dragging) return null;
    return CARD_DB.byNumber.get(c.data.num)?.types.includes('trigger') ? c : null;
  }

  private syncChip(pick: Pick | null) {
    const card = this.wantsChip(pick);
    if (!card) {
      // leave it up for a moment, so the cursor can travel from the card onto the chip
      if (this.chipEl && !this.chipTimer) this.chipTimer = setTimeout(() => this.hideChip(), 320);
      return;
    }
    if (this.chipTimer) clearTimeout(this.chipTimer);
    this.chipTimer = null;
    if (this.chipCard !== card.data.id) this.hideChip();
    if (!this.chipEl) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'card-chip';
      const pays = triggerPayout(card.data.num !== null ? CARD_DB.byNumber.get(card.data.num) : undefined);
      chip.textContent = `Payout · ${pays} Gerzite`;
      chip.title = `This Trigger was sprung: gain ${pays} Gerzite (its cost + ${CONFIG.trigger.payoutBonus}) and discard it`;
      chip.addEventListener('pointerenter', () => {
        if (this.chipTimer) clearTimeout(this.chipTimer);
        this.chipTimer = null;
      });
      chip.addEventListener('pointerleave', () => {
        if (!this.chipTimer) this.chipTimer = setTimeout(() => this.hideChip(), 320);
      });
      chip.addEventListener('click', () => {
        const c = this.chipCard ? this.cards.get(this.chipCard) : undefined;
        const r = chip.getBoundingClientRect();
        this.hideChip();
        if (c) this.cb.cardChip(c.data, r.left, r.bottom + 6);
      });
      document.body.append(chip);
      this.chipEl = chip;
      this.chipCard = card.data.id;
    }
    this.placeChip();
  }

  /** Keeps the chip just above its card (the camera may be moving). */
  private placeChip() {
    const c = this.chipCard ? this.cards.get(this.chipCard) : undefined;
    if (!this.chipEl) return;
    if (!c || !this.wantsChip({ kind: 'card', id: c.data.id })) {
      this.hideChip();
      return;
    }
    const half = (c.data.rot % 2 === 1 ? CW : CH) / 2;
    const p = this.screenPoint(c.root.position.x, c.root.position.z - half - 0.3, c.root.position.y);
    this.chipEl.style.transform = `translate(${p.x}px, ${p.y}px) translate(-50%, -100%)`;
  }

  private hideChip() {
    if (this.chipTimer) clearTimeout(this.chipTimer);
    this.chipTimer = null;
    this.chipEl?.remove();
    this.chipEl = null;
    this.chipCard = null;
  }

  // ======================= selecting several pieces =======================

  private refreshSelection() {
    for (const id of [...this.selected]) if (!this.cards.has(id) && !this.tokens.has(id)) this.selected.delete(id);
    for (const [id, mark] of this.selMarks) {
      if (this.selected.has(id)) continue;
      mark.removeFromParent();
      (mark.material as THREE.Material).dispose();
      mark.geometry.dispose();
      this.selMarks.delete(id);
    }
    const t = fxTextures();
    for (const id of this.selected) {
      if (this.selMarks.has(id)) continue;
      const card = this.cards.get(id);
      const token = this.tokens.get(id);
      const size = token ? (token.def?.size ?? CONFIG.token.size) * 1.9 : 0;
      const geo = new THREE.PlaneGeometry(card ? CW * 1.44 : size, card ? CH * 1.44 : size);
      geo.rotateX(-Math.PI / 2);
      const mark = new THREE.Mesh(
        geo,
        new THREE.MeshBasicMaterial({
          map: card ? t.ring : t.circle,
          color: CONFIG.colors.highlight,
          transparent: true,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      mark.position.y = card ? -0.012 : 0.012;
      (card?.root ?? token?.mesh)?.add(mark);
      this.selMarks.set(id, mark);
    }
  }

  // ======================= aiming an attack =======================

  /** One of your units is about to attack: the cards it can hit light up, and the next click picks its target. */
  beginStrike(from: string) {
    if (this.readOnly || !this.view?.combat[from]?.targets.length) return;
    this.striking = from;
    this.clearSelection();
    this.refreshStrike();
    this.dragHint('Click the card it attacks (Esc: cancel)', 'info', this.lastMouse?.x ?? 0, this.lastMouse?.y ?? 0);
  }
  /** A card effect asks you to choose one of these cards (null = nothing is being asked any more). */
  setPick(pick: { id: number; options: string[] } | null) {
    if (this.readOnly) return;
    this.picking = pick;
    if (pick) this.cancelStrike();
    this.refreshStrike();
  }
  cancelStrike() {
    if (!this.striking) return;
    this.striking = null;
    this.markers.setPreview(null);
    this.dragHint(null);
    this.refreshStrike();
  }
  /** The red rings on what the aiming card can hit: redrawn as the table changes (it ends when nothing is left to hit). */
  private refreshStrike() {
    const targets = this.striking ? (this.view?.combat[this.striking]?.targets ?? []) : [];
    if (this.striking && (!targets.length || !this.cards.has(this.striking))) {
      this.striking = null;
      this.markers.setPreview(null);
      this.dragHint(null);
    }
    const want = new Set(this.picking ? this.picking.options : this.striking ? targets : []);
    const color = this.picking ? CONFIG.colors.highlight : CONFIG.reach.attack;
    for (const [id, mark] of this.strikeMarks) {
      if (want.has(id) && this.cards.has(id) && mark.userData.color === color) continue;
      mark.removeFromParent();
      (mark.material as THREE.Material).dispose();
      mark.geometry.dispose();
      this.strikeMarks.delete(id);
    }
    for (const id of want) {
      const card = this.cards.get(id);
      if (!card || this.strikeMarks.has(id)) continue;
      const geo = new THREE.PlaneGeometry(CW * 1.5, CH * 1.5);
      geo.rotateX(-Math.PI / 2);
      const mark = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: fxTextures().ring, color, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
      mark.userData.color = color;
      mark.position.y = -0.012;
      card.root.add(mark);
      this.strikeMarks.set(id, mark);
    }
  }
  /** While aiming: an arrow from the attacker to the cursor, and what a click there would do. */
  private aimStrike(clientX: number, clientY: number) {
    const from = this.striking ? this.cards.get(this.striking) : undefined;
    const gp = this.groundPoint(clientX, clientY);
    if (!from || !gp) return;
    const over = this.cardAt(gp.x, gp.z);
    const ok = !!over && this.strikeMarks.has(over.data.id);
    const to = ok ? new THREE.Vector3(over!.x, over!.root.position.y, over!.z) : new THREE.Vector3(gp.x, 0, gp.z);
    this.markers.setPreview(...this.arrowEnds(from, to, ok), this.seatColor(this.view?.you ?? 0));
    this.renderer.domElement.style.cursor = ok ? 'crosshair' : 'not-allowed';
    const name = ok ? this.nameOf({ kind: 'card', id: over!.data.id }) : null;
    this.dragHint(ok ? `Click: attack ${name ?? 'this card'}` : 'Click a ringed card to attack it (Esc: cancel)', ok ? 'merge' : 'info', clientX, clientY);
  }
  /** A click while aiming: on a ringed card, the attack is made; anywhere else, the aim is dropped. */
  private strikeClick(clientX: number, clientY: number) {
    const from = this.striking;
    const gp = this.groundPoint(clientX, clientY);
    const over = gp ? this.cardAt(gp.x, gp.z) : null;
    if (from && over && this.strikeMarks.has(over.data.id)) {
      this.markers.setPreview(null);
      this.dragHint(null);
      this.cb.send({ type: 'strike', from, to: over.data.id }); // (a card with hits left keeps aiming: see refreshStrike)
    } else this.cancelStrike();
  }

  clearSelection() {
    if (!this.selected.size) return;
    this.selected.clear();
    this.refreshSelection();
  }

  private toggleSelect(pick: Pick | null) {
    if (!pick || pick.kind === 'pile') return;
    if (pick.kind === 'card') {
      const c = this.cards.get(pick.id);
      if (!c || !this.canControlCard(c.data)) return;
    }
    if (this.selected.has(pick.id)) this.selected.delete(pick.id);
    else this.selected.add(pick.id);
    this.refreshSelection();
  }

  private beginMarquee(p: NonNullable<TableView['pointer']>, e: PointerEvent) {
    p.mode = 'marquee';
    this.marqueeEl = document.createElement('div');
    this.marqueeEl.className = 'marquee';
    document.body.append(this.marqueeEl);
    this.updateMarquee(p, e);
  }

  private marqueeRect(p: NonNullable<TableView['pointer']>, e: PointerEvent) {
    return {
      left: Math.min(p.startX, e.clientX),
      top: Math.min(p.startY, e.clientY),
      right: Math.max(p.startX, e.clientX),
      bottom: Math.max(p.startY, e.clientY),
    };
  }

  private updateMarquee(p: NonNullable<TableView['pointer']>, e: PointerEvent) {
    if (!this.marqueeEl) return;
    const r = this.marqueeRect(p, e);
    Object.assign(this.marqueeEl.style, {
      left: `${r.left}px`,
      top: `${r.top}px`,
      width: `${r.right - r.left}px`,
      height: `${r.bottom - r.top}px`,
    });
  }

  private endMarquee(p?: NonNullable<TableView['pointer']>, e?: PointerEvent) {
    this.marqueeEl?.remove();
    this.marqueeEl = null;
    if (!p || !e) return;
    const r = this.marqueeRect(p, e);
    const inside = (id: string) => {
      const pt = this.screenOf(id);
      return !!pt && pt.x >= r.left && pt.x <= r.right && pt.y >= r.top && pt.y <= r.bottom;
    };
    for (const t of this.tokens.values()) if (inside(t.data.id)) this.selected.add(t.data.id);
    for (const c of this.cards.values()) if (this.canControlCard(c.data) && inside(c.data.id)) this.selected.add(c.data.id);
    this.refreshSelection();
  }

  /** Dragging one selected piece moves the whole selection (plus tokens sitting on selected cards). */
  private beginGroup(p: NonNullable<TableView['pointer']>, e: PointerEvent, grabbed: CardObj | TokenObj) {
    const gp = this.groundPoint(p.startX, p.startY);
    if (!gp) {
      p.mode = 'pan';
      return;
    }
    const members = new Map<string, { obj: CardObj | TokenObj; token: boolean; selected: boolean }>();
    for (const id of this.selected) {
      const card = this.cards.get(id);
      const token = this.tokens.get(id);
      if (card && this.canControlCard(card.data)) members.set(id, { obj: card, token: false, selected: true });
      else if (token) members.set(id, { obj: token, token: true, selected: true });
    }
    for (const m of [...members.values()]) {
      if (m.token) continue;
      for (const t of this.tokensOn(m.obj as CardObj)) {
        if (!members.has(t.data.id)) members.set(t.data.id, { obj: t, token: true, selected: false });
      }
    }
    p.group = [...members.values()].map((m) => ({ ...m, dx: m.obj.x - grabbed.x, dz: m.obj.z - grabbed.z }));
    for (const m of p.group) m.obj.dragging = true;
    p.offset = new THREE.Vector3(grabbed.x - gp.x, 0, grabbed.z - gp.z);
    p.mode = 'group';
    this.renderer.domElement.style.cursor = 'grabbing';
    this.continueGroup(p, e);
  }

  private continueGroup(p: NonNullable<TableView['pointer']>, e: PointerEvent) {
    const gp = this.groundPoint(e.clientX, e.clientY);
    if (!gp || !p.group || !p.offset) return;
    const x = gp.x + p.offset.x;
    const z = gp.z + p.offset.z;
    for (const m of p.group) {
      m.obj.x = x + m.dx;
      m.obj.z = z + m.dz;
    }
    const tokens = p.group.filter((m) => m.token && m.selected);
    const r = this.renderer.domElement.getBoundingClientRect();
    const atScreenEdge = e.clientX < r.left + 14 || e.clientX > r.right - 14 || e.clientY < r.top + 14;
    p.groupOff = tokens.length > 0 && (this.isOffTable(x, z) || atScreenEdge) && !this.cb.isOverHand(e.clientX, e.clientY);
    for (const m of tokens) (m.obj as TokenObj).offTable = p.groupOff;
    this.dragHint(
      p.groupOff ? `Let go to delete ${tokens.length} token${tokens.length === 1 ? '' : 's'}` : null,
      'delete',
      e.clientX,
      e.clientY,
    );
  }

  private dropGroup(p: NonNullable<TableView['pointer']>, e: PointerEvent) {
    const group = p.group ?? [];
    for (const m of group) {
      m.obj.dragging = false;
      if (m.token) (m.obj as TokenObj).offTable = false;
    }
    if (p.groupOff) {
      // only the selected tokens are thrown away; any cards in the selection go back where they were
      this.cb.send({ type: 'deleteMany', ids: group.filter((m) => m.token && m.selected).map((m) => m.obj.data.id) });
      this.resync();
      return;
    }
    if (this.cb.isOverHand(e.clientX, e.clientY)) {
      this.resync();
      return;
    }
    this.cb.send({ type: 'moveMany', items: group.map((m) => ({ id: m.obj.data.id, ...this.toWorld(m.obj.x, m.obj.z) })) });
  }

  /** Tokens sitting on a card (they ride along when it moves). */
  private tokensOn(card: CardObj): TokenObj[] {
    // the tokens on a summoned unit attached to this card are that unit's
    const kids = [...this.cards.values()].filter((c) => c.data.attachedTo === card.data.id);
    const box = (c: CardObj) => ({ x: c.x, z: c.z, rot: c.data.rot, scale: c.data.scale });
    return [...this.tokens.values()].filter((t) => onCard(t, box(card)) && !kids.some((k) => onCard(t, box(k))));
  }

  private beginDrag(p: NonNullable<TableView['pointer']>, e: PointerEvent) {
    const pick = p.pick;
    const gp = this.groundPoint(p.startX, p.startY);
    if (pick && pick.kind !== 'pile') {
      // dragging a piece that's part of a selection moves the whole selection;
      // dragging anything else drops the selection
      const grabbed = pick.kind === 'card' ? this.cards.get(pick.id) : this.tokens.get(pick.id);
      if (grabbed && this.selected.has(pick.id) && this.selected.size > 1 && !this.handsOff()) {
        this.beginGroup(p, e, grabbed);
        return;
      }
      this.clearSelection();
    }
    if (pick?.kind === 'card') {
      const obj = this.cards.get(pick.id);
      if (!obj || !this.canDragCard(obj) || !gp) {
        p.mode = 'pan';
        return;
      }
      obj.dragging = true;
      p.mode = 'drag';
      p.offset = new THREE.Vector3(obj.x - gp.x, 0, obj.z - gp.z);
      // tokens sitting on the card ride along
      p.carry = this.tokensOn(obj).map((t) => ({ obj: t, dx: t.x - obj.x, dz: t.z - obj.z }));
      for (const c of p.carry) c.obj.dragging = true;
      this.renderer.domElement.style.cursor = 'grabbing';
    } else if (pick?.kind === 'token' && !this.readOnly && !this.handsOff()) {
      const obj = this.tokens.get(pick.id);
      if (!obj || !gp) return;
      obj.dragging = true;
      p.mode = 'drag';
      p.offset = new THREE.Vector3(obj.x - gp.x, 0, obj.z - gp.z);
      this.renderer.domElement.style.cursor = 'grabbing';
    } else {
      p.mode = 'pan';
    }
    this.continueDrag(p, e);
  }

  private continueDrag(p: NonNullable<TableView['pointer']>, e: PointerEvent) {
    if (p.mode !== 'drag' || !p.pick || !p.offset) return;
    const gp = this.groundPoint(e.clientX, e.clientY);
    if (!gp) return;
    const x = gp.x + p.offset.x;
    const z = gp.z + p.offset.z;
    if (p.pick.kind === 'card') {
      const obj = this.cards.get(p.pick.id);
      if (!obj) return;
      obj.x = x;
      obj.z = z;
      for (const c of p.carry ?? []) {
        c.obj.x = x + c.dx;
        c.obj.z = z + c.dz;
      }
      const overHand = this.cb.isOverHand(e.clientX, e.clientY);
      // an Emperor can't go into a pile, so the piles don't light up for it
      const pile = overHand || this.isEmperorData(obj.data) ? null : this.pileAt(gp);
      this.cb.setHandHighlight(overHand);
      this.highlightPile(pile);
      this.showSlots(overHand || pile ? null : obj.data.num);
      const into = overHand || pile ? null : this.mergeTarget(x, z, obj.data);
      const onto = overHand || pile || into ? null : this.attachTarget(x, z, obj.data);
      const noEntry = overHand || pile || onto || obj.data.num === null ? null : slotRefusal(this.slotDefOf(obj.data), { x, z }, this.placedCards(obj.data.id), !!this.view?.wideLanes);
      if (onto) this.dragHint(`Let go to attach to ${onto.data.num !== null ? (CARD_DB.byNumber.get(onto.data.num)?.name ?? 'this card') : 'this card'}`, 'merge', e.clientX, e.clientY);
      else if (noEntry) this.dragHint(noEntry, 'delete', e.clientX, e.clientY);
      else this.dragHint(into ? `Let go to merge → Tier ${roman(into.data.tier + obj.data.tier)}` : null, 'merge', e.clientX, e.clientY);
    } else if (p.pick.kind === 'token') {
      const obj = this.tokens.get(p.pick.id);
      if (obj) {
        obj.x = x;
        obj.z = z;
        if (obj.die) {
          p.trail = [...(p.trail ?? []), { t: e.timeStamp, x, z }].slice(-8);
        }
        // thrown off the table = quick delete (but not while it's just passing over your hand bar)
        // "off the table" also covers flinging it to the left, right or top edge of the screen,
        // for views where the table's edge is out of sight
        const r = this.renderer.domElement.getBoundingClientRect();
        const atScreenEdge = e.clientX < r.left + 14 || e.clientX > r.right - 14 || e.clientY < r.top + 14;
        obj.offTable = (this.isOffTable(x, z) || atScreenEdge) && !this.cb.isOverHand(e.clientX, e.clientY);
        this.dragHint(obj.offTable ? 'Let go to delete' : null, 'delete', e.clientX, e.clientY);
      }
    }
  }

  /**
   * The card a dragged copy would merge into, if any: the same card, face-up, yours,
   * with room for more tiers, and with the dragged card's middle over it.
   */
  private mergeTarget(x: number, z: number, source: { id?: string; num: number | null; tier: number; faceUp?: boolean; clone?: unknown }): CardObj | null {
    if (source.num === null || source.faceUp === false || source.clone) return null; // (a clone — Illusionist's copy — never tiers anything up)
    const maxTier = CARD_DB.byNumber.get(source.num)?.tiers ?? 1;
    let best: CardObj | null = null;
    for (const c of this.cards.values()) {
      const d = c.data;
      if (d.id === source.id || d.num !== source.num || !d.faceUp || !this.canControlCard(d) || d.clone) continue;
      if (d.tier + source.tier > maxTier) continue;
      const turned = d.rot % 2 === 1;
      const halfW = (turned ? CH : CW) / 2;
      const halfD = (turned ? CW : CH) / 2;
      if (Math.abs(x - c.x) > halfW || Math.abs(z - c.z) > halfD) continue;
      if (!best || d.layer > best.data.layer) best = c;
    }
    return best;
  }

  private isOffTable(x: number, z: number) {
    return Math.abs(x) > CONFIG.table.width / 2 + 0.5 || Math.abs(z) > CONFIG.table.depth / 2 + 0.5;
  }

  // ======================= name label =======================

  private tipEl: HTMLElement | null = null;

  /** The name of a card or token, as the label shows it. */
  private nameOf(pick: Pick | null): string | null {
    if (!pick) return null;
    if (pick.kind === 'token') {
      const t = this.tokens.get(pick.id);
      if (!t) return null;
      const under = this.expiredUnder(t);
      if (under) return this.nameOf({ kind: 'card', id: under.data.id }); // the skull stands for its card
      if (t.data.ability) {
        // the cooldown of an Emperor ability: say which one, and how long is left
        const emperor = this.cards.get(t.data.ability.card)?.data;
        const ability = emperor && emperor.num !== null ? CARD_DB.byNumber.get(emperor.num)?.emperorAbilities.find((a) => a.slot === t.data.ability!.slot) : undefined;
        const label = ability?.name ?? `Ability ${t.data.ability.slot}`;
        if (t.def?.effect === CONFIG.tokenRules.lockToken) return `Locked: ${label}${t.data.price ? ` · unlock for ${t.data.price} ${t.data.priceDark ? 'Dark Gerzite' : 'Gerzite'}` : ''}`;
        return `${label} · cooldown: ${t.data.value} turn${t.data.value === 1 ? '' : 's'}`;
      }
      const name = t.def?.name ?? 'Token';
      // a token a card brought with it says which card: that card ends when the token is gone
      const sourceCard = t.data.from ? this.cards.get(t.data.from)?.data : undefined;
      const source = sourceCard && sourceCard.num !== null ? ` · came with ${CARD_DB.byNumber.get(sourceCard.num)?.name ?? 'a card'}` : '';
      if (t.data.price) return `${name} · unlock for ${t.data.price} ${t.data.priceDark ? 'Dark Gerzite' : 'Gerzite'}${source}`;
      if (this.countsUp(t)) return `${name} · ${t.data.value}${t.data.max !== undefined ? ` of ${t.data.max}` : ''} · goes up by 1 as each turn begins`;
      // a counter with a turn clock says both numbers: how much, and for how long
      const lasts = (t.data.turns ? ` · ${t.data.turns} turn${t.data.turns === 1 ? '' : 's'} left` : '') + (t.data.drain ? ` · ${t.data.drain} wears off, 1 a turn` : '');
      const owes = t.data.cure !== undefined ? ' · waiting for its cure roll' : '';
      if (t.data.unset) return `${name} · not set yet: click to set its turns`;
      if (t.def?.bar && t.data.asCounter) {
        const f = t.data.flow;
        const does = f ? ` · ${f.mode === 'add' ? 'adds' : 'removes'} ${t.data.value} ${f.mode === 'add' ? 'to' : 'from'} its owner's bar as each of their turns ${f.when === 'start' ? 'begins' : 'ends'}` : ' · drag it onto your bar to use it, or click to set it up';
        return `${name} counter · ${t.data.value}${does}${lasts}`;
      }
      if (t.def?.effect === CONFIG.tokenRules.splashToken) return `${name} · drop a counter or status on it to put it on every unit in this lane${source}`;
      if (t.def?.effect === CONFIG.tokenRules.linkToken) return `${name} ${t.data.value} · linked to the card under the other Link ${t.data.value}${t.data.half ? ' · this card takes half the damage' : ''}`;
      // a status whose moment its player picks (Regeneration): how much, and when
      const picked = CONFIG.turn.statusActs.find((x) => x.choose && x.effect === t.def?.effect);
      if (picked) {
        const at = t.data.actAt ?? picked.when;
        const does = picked.does === 'heal' ? 'heals' : picked.does === 'damage' ? 'deals' : 'acts';
        return `${name} · ${does} ${t.data.value}${picked.does === 'damage' ? ' damage' : ''} as each of its card owner's turns ${at === 'start' ? 'begins' : 'ends'}${lasts || ' · until removed'}${source}`;
      }
      return (t.def?.counter || t.die ? `${name} · ${t.data.value}` : name) + lasts + owes + source;
    }
    if (pick.kind !== 'card') return null;
    const c = this.cards.get(pick.id)?.data;
    if (!c) return null;
    // a face-down card you can't peek at stays a secret
    if (c.num === null) return 'Face-down card';
    const def = CARD_DB.byNumber.get(c.num);
    const name = def?.name ?? `Card ${c.num}`;
    // health left: full health, less the damage counters on the card, plus any Overheal
    const obj = this.cards.get(pick.id)!;
    const full = def ? this.fullHealth(c) : null;
    // a Building that has decayed says how its lost SP divides: damage taken, and SP decayed away
    const split = obj.decay > 0 ? ` (${obj.damage > obj.decay ? `${obj.damage - obj.decay} damage, ` : ''}${obj.decay} decay)` : '';
    const health = c.faceUp && def && full ? ` · ${Math.max(0, full + obj.gained - obj.damage) + obj.overheal}/${full + obj.gained} ${def.types.includes('building') ? 'SP' : 'health'}${split}` : '';
    // a card with two costs says which one it was summoned at
    const form = c.faceUp && def?.forms ? ` · summoned as ${formWords(formOf(def, c.form))} for ${formOf(def, c.form).cost}` : '';
    // what a click on it offers: a Biome's removal, a run-down Tome's restart
    // a Troop or Building at Tier III hits harder than its card says
    const dmg = c.faceUp && def && c.tier >= 3 && tieredDamage(def, c.tier) !== def.damage ? ` · ${tieredDamage(def, c.tier)} damage` : '';
    let offer = '';
    if (c.faceUp && def && !this.readOnly) {
      if (def.types.some((t) => CONFIG.turn.removable.includes(t))) offer = obj.expired ? ' · turns over: click to clear it away' : ` · click to remove (${tieredRemoval(def, c.tier) ?? 0} ${def.removalDark ? 'Dark Gerzite' : 'Gerzite'})`;
      else if (obj.expired && def.types.some((t) => CONFIG.turn.restartable.includes(t))) offer = c.owner === this.view?.you ? ` · turns over: click to restart or discard${CONFIG.turn.discardSpentTomes ? ' (discarded as your turn ends if not restarted)' : ''}` : ' · turns over';
      else if (CONFIG.emperorAbilities.enabled && isEmperor(def)) offer = ' · click for abilities';
    }
    const cloned = c.clone ? `: cloned from ${c.clone.by}` : '';
    // (Cragged Skulker: how many attacking actions it has a turn, and how many are left this turn)
    const acts = c.faceUp && c.actions ? ` · ${c.actions.total} attacks a turn${c.actions.left < c.actions.total ? ` (${c.actions.left} left)` : ''}` : '';
    const frenzy = c.faceUp && c.frenzy ? ' · Frenzy (2 hits an attack)' : '';
    const swarmLeft = c.faceUp && c.swarm ? (this.view?.field.filter((o) => o.swarm === c.swarm).length ?? 0) : 0;
    const swarmNote = swarmLeft ? ` · one of a swarm (${swarmLeft} left${c.num !== null && CARD_DB.byNumber.get(c.num)?.count ? ` of ${CARD_DB.byNumber.get(c.num)!.count}` : ''})` : '';
    const rested = c.faceUp && c.restHits ? ` · next attack: ${c.restHits} extra hit${c.restHits === 1 ? '' : 's'}` : '';
    return name + cloned + (c.tier > 1 ? ` · Tier ${roman(c.tier)}` : '') + form + health + dmg + acts + frenzy + rested + swarmNote + (c.faceUp ? '' : ' (face-down)') + offer;
  }

  /** Shows the name of whatever the cursor is resting on, just above the cursor. */
  private hoverTip(show = true) {
    const text = show && CONFIG.hoverNames && !this.pointer ? this.nameOf(this.hovered) : null;
    if (!text) {
      this.tipEl?.remove();
      this.tipEl = null;
      return;
    }
    if (!this.tipEl) {
      this.tipEl = document.createElement('div');
      this.tipEl.className = 'hover-tip';
      document.body.append(this.tipEl);
    }
    if (this.tipEl.textContent !== text) this.tipEl.textContent = text;
    const { x, y } = this.lastMouse;
    const left = Math.max(8, Math.min(x + 14, window.innerWidth - this.tipEl.offsetWidth - 8));
    const top = y - 34 < 8 ? y + 22 : y - 34; // drop below the cursor at the very top of the screen
    this.tipEl.style.transform = `translate(${left}px, ${top}px)`;
  }

  /** The cursor left the table (onto the hand bar, a menu…): nothing is hovered anymore. */
  private onLeave = () => {
    if (this.pointer) return;
    this.syncChip(null);
    this.hovered = null;
    this.hoverTip(false);
    this.removeHint();
  };

  private hintEl: HTMLElement | null = null;
  /** The little label that follows the cursor during a drag ("Let go to delete", "Let go to merge"). */
  private dragHint(text: string | null, kind: 'delete' | 'merge' | 'info' = 'delete', clientX = 0, clientY = 0) {
    if (!text) {
      this.hintEl?.remove();
      this.hintEl = null;
      return;
    }
    if (!this.hintEl) {
      this.hintEl = document.createElement('div');
      document.body.append(this.hintEl);
    }
    this.hintEl.className = `drag-hint drag-hint-${kind}`;
    if (this.hintEl.textContent !== text) this.hintEl.textContent = text;
    // keep the label on screen even when the cursor is at the very edge
    const x = Math.min(clientX + 16, window.innerWidth - this.hintEl.offsetWidth - 8);
    const y = Math.max(8, Math.min(clientY + 18, window.innerHeight - 40));
    this.hintEl.style.transform = `translate(${Math.max(8, x)}px, ${y}px)`;
  }

  private onUp = (e: PointerEvent) => {
    const p = this.pointer;
    if (!p || p.id !== e.pointerId) return;
    this.pointer = null;
    this.renderer.domElement.style.cursor = '';
    this.cb.setHandHighlight(false);
    this.highlightPile(null);
    this.dragHint(null);
    if (!this.placing) this.showSlots(null);
    if (p.mode === 'aim') this.endAim(p);
    else if (p.mode === 'pending') {
      if (this.readOnly) return;
      const arrow = this.myArrowAt(e.clientX, e.clientY);
      if (this.picking) {
        // a choice is being asked for: a click on a ringed card (or a token lying on it) answers it
        const gp = this.groundPoint(e.clientX, e.clientY);
        const over = gp ? this.cardAt(gp.x, gp.z) : null;
        if (over && this.picking.options.includes(over.data.id)) this.cb.send({ type: 'pick', pick: this.picking.id, card: over.data.id });
        else this.cb.notice('Choose one of the ringed cards.');
      } else if (this.striking) this.strikeClick(e.clientX, e.clientY);
      else if (p.alt) this.pingAt(e.clientX, e.clientY);
      else if (arrow !== null) this.cb.send({ type: 'endAttack', arrow }); // a click on an arrow you drew takes it away
      else if (this.placing) {
        if (this.offBoard(p.pick, e.clientX, e.clientY)) {
          // a click on a pile (or off the table) never places the card: it lets go of it, and the pile does its usual thing
          this.cb.placeCancel?.();
          if (p.pick?.kind === 'pile') this.click(p.pick, e);
        } else this.cb.placeClick?.(e.clientX, e.clientY, e.shiftKey, e.ctrlKey || e.metaKey);
      }
      else if (this.removeKey) this.quickRemove(p.pick);
      else if (this.cloneKey) {
        if (p.pick?.kind === 'token' && !this.handsOff()) this.cb.send({ type: 'cloneToken', id: p.pick.id });
      }
      else if (p.select) this.toggleSelect(p.pick);
      else if (this.selected.size) this.clearSelection(); // a plain click just lets go of the selection
      else this.click(p.pick, e);
    } else if (p.mode === 'drag') void this.drop(p, e);
    else if (p.mode === 'group') this.dropGroup(p, e);
    else if (p.mode === 'marquee') this.endMarquee(p, e);
  };

  private onCancel = () => {
    const p = this.pointer;
    this.pointer = null;
    this.dragHint(null);
    this.showSlots(null);
    this.markers.setPreview(null);
    this.endMarquee();
    for (const t of this.tokens.values()) t.dragging = t.offTable = false;
    for (const c of this.cards.values()) c.dragging = false;
    if (p?.mode === 'drag') this.resync();
  };

  private onContext = (e: MouseEvent) => {
    this.hoverTip(false);
    e.preventDefault();
    if (this.striking) {
      this.cancelStrike(); // a right-click drops the aim
      return;
    }
    const pick = this.pick(e.clientX, e.clientY);
    if (!pick || !this.view) return;
    if (pick.kind === 'pile' && this.placing) this.cb.placeCancel?.(); // using a pile lets go of a selected hand card
    if (pick.kind === 'card') {
      const c = this.cards.get(pick.id);
      if (c) this.cb.cardMenu(c.data, e.clientX, e.clientY, c.expired);
    } else if (pick.kind === 'token') {
      const t = this.tokens.get(pick.id);
      if (t) this.cb.tokenMenu(t.data, e.clientX, e.clientY);
    } else if (pick.seat === this.view.you && pick.pile === 'deck' && !this.readOnly) {
      this.cb.deckClick(e.clientX, e.clientY); // draw — or, past 7 cards or with an empty deck, a small menu of choices
    } else if (pick.seat === this.view.you && pick.pile === 'discard' && !this.readOnly) {
      this.cb.discardClick(e.clientX, e.clientY); // its top card: back to your hand, or straight to your deck
    } else this.cb.pileMenu(pick.seat, pick.pile, e.clientX, e.clientY);
  };

  private click(pick: Pick | null, e: PointerEvent) {
    if (!pick || !this.view || this.readOnly) return;
    if (pick.kind === 'card') {
      const obj = this.cards.get(pick.id);
      // a Biome (either player's) offers its removal cost; a Tome of yours whose turns are over, its restart
      // (Shift + click is always the plain click: tap it the other way)
      if (obj && !e.shiftKey && this.actsOnClick(obj) && this.cb.cardAct?.(obj.data, obj.expired, e.clientX, e.clientY)) return;
      if (!obj || !this.canControlCard(obj.data)) return;
      // the table deals attacks itself: a click on one of your units starts its attack (or asks: attack, or its
      // Tap ability?). Shift + click only turns a card, with nothing else happening: tap it, or untap it.
      if (CONFIG.combat.enabled && !e.shiftKey) {
        if (this.view.combat[pick.id]) this.cb.unitClick?.(obj.data, e.clientX, e.clientY);
        return;
      }
      if (!this.handsOff()) this.cb.send({ type: 'tap', id: pick.id, dir: CONFIG.combat.enabled && obj.data.rot % 2 === 1 ? -1 : 1 });
    } else if (pick.kind === 'token') {
      const obj = this.tokens.get(pick.id);
      // the skull on a card whose turns are over is in the way of the card: a click on it is a click on the card
      const under = obj ? this.expiredUnder(obj) : null;
      if (under && this.cb.cardAct?.(under.data, true, e.clientX, e.clientY)) return;
      if (obj?.die) this.cb.send({ type: 'roll', id: pick.id }); // click a die to roll it where it is
      else if (obj?.def?.counter && !this.handsOff()) this.cb.send({ type: 'tokenValue', id: pick.id, delta: e.shiftKey ? -1 : 1 });
    } else if (pick.kind === 'pile') this.cb.pileMenu(pick.seat, pick.pile, e.clientX, e.clientY); // Search, Shuffle, look at the top cards
  }

  /** The card under a Destroyed skull, if a click on that card would open its "clear away / restart" menu. */
  private expiredUnder(t: TokenObj): CardObj | null {
    if (t.def?.effect !== CONFIG.turn.lifetime.expiredToken || !t.on || !t.on.expired) return null;
    return this.actsOnClick(t.on) ? t.on : null;
  }

  /** Does a left-click on this card open a "pay to…" menu instead of tapping it? */
  private actsOnClick(obj: CardObj) {
    const c = obj.data;
    const def = c.num !== null && c.faceUp ? CARD_DB.byNumber.get(c.num) : undefined;
    if (!def || this.readOnly) return false;
    if (def.types.some((t) => CONFIG.turn.removable.includes(t))) return true;
    if (CONFIG.emperorAbilities.enabled && isEmperor(def)) return true; // an Emperor, yours or theirs: its abilities
    if (c.owner === this.view?.you && !obj.expired && def.effects.some((e) => e.when === 'click')) return true; // a card of yours you set off by clicking it (Moldering Tome)
    // a Tome of yours whose turns are over (restart or discard), or a Spell of yours that has ended (recast or discard)
    return obj.expired && c.owner === this.view?.you && def.types.some((t) => CONFIG.turn.restartable.includes(t) || t === 'spell');
  }

  /** The face-up card that has to be paid out of the way before this card can go down here (a Biome on a Biome). */
  private inTheWay(def: CardDef | undefined, world: { x: number; z: number }): CardObj | null {
    if (!def?.types.some((t) => CONFIG.turn.removable.includes(t))) return null;
    const here = slotAt(world);
    if (!here) return null;
    for (const c of this.cards.values()) {
      const d = c.data.num !== null && c.data.faceUp ? CARD_DB.byNumber.get(c.data.num) : undefined;
      if (!d?.types.some((t) => CONFIG.turn.removable.includes(t))) continue;
      if (slotAt({ x: c.data.x, z: c.data.z })?.slot === here.slot) return c;
    }
    return null;
  }

  /** With a hand card selected: is this spot somewhere a card is never placed by a click (a pile, or off the table)? */
  private offBoard(pick: Pick | null, clientX: number, clientY: number) {
    if (pick?.kind === 'pile') return true;
    const gp = this.groundPoint(clientX, clientY);
    return !gp || this.pileAt(gp) !== null || this.isOffTable(gp.x, gp.z);
  }

  /** The arrow of yours under the cursor, if any (its number), or null. */
  private myArrowAt(clientX: number, clientY: number): number | null {
    if (this.readOnly) return null;
    return this.markers.arrowAt(this.rig.camera, this.renderer.domElement.getBoundingClientRect(), clientX, clientY);
  }
  private arrowHover = false;

  private pileAt(gp: THREE.Vector3): PileName | null {
    if (!this.view) return null;
    for (const pile of ['deck', 'discard'] as const) {
      const z = CONFIG.zones[pile];
      if (Math.abs(gp.x - z.x) < CW / 2 + 2.5 && Math.abs(gp.z - z.z) < CH / 2 + 2.5) return pile;
    }
    return null;
  }

  private highlightPile(pile: PileName | null) {
    for (const p of this.piles) p.hover = p.seat === this.view?.you && p.pile === pile;
  }

  /** How fast (cm per second, in your view) a dragged die was moving when it was let go. */
  private flingSpeed(p: NonNullable<TableView['pointer']>, now: number) {
    const recent = (p.trail ?? []).filter((s) => now - s.t <= 140);
    const last = recent.at(-1);
    const first = recent[0];
    // held still before letting go, or no movement to speak of: not a throw
    if (!last || !first || last === first || now - last.t > 70) return { x: 0, z: 0 };
    const dt = Math.max(0.016, (last.t - first.t) / 1000);
    return { x: (last.x - first.x) / dt, z: (last.z - first.z) / dt };
  }

  private async drop(p: NonNullable<TableView['pointer']>, e: PointerEvent) {
    if (!p.pick) return;
    const gp = this.groundPoint(e.clientX, e.clientY);
    if (p.pick.kind === 'token') {
      const obj = this.tokens.get(p.pick.id);
      if (!obj) return;
      obj.dragging = false;
      if (obj.offTable) {
        this.cb.send({ type: 'delete', id: obj.data.id });
        return;
      }
      if (this.cb.isOverHand(e.clientX, e.clientY)) {
        this.resync(); // tokens can't go in your hand, and would be hidden under it: put it back
        return;
      }
      if (obj.die) {
        // let go of a die while it's still moving and it is thrown: it carries on, tumbles and lands on a random face
        const v = this.flingSpeed(p, e.timeStamp);
        const speed = Math.hypot(v.x, v.z);
        if (speed >= CONFIG.dice.throwSpeed) {
          const reach = Math.min(CONFIG.dice.maxThrow, speed * CONFIG.dice.throwTime);
          const land = this.toWorld(obj.x + (v.x / speed) * reach, obj.z + (v.z / speed) * reach);
          this.cb.send({ type: 'roll', id: obj.data.id, x: land.x, z: land.z });
          return;
        }
      }
      const w = this.toWorld(obj.x, obj.z);
      this.cb.send({ type: 'move', id: obj.data.id, x: w.x, z: w.z });
      return;
    }
    if (p.pick.kind !== 'card') return;
    const obj = this.cards.get(p.pick.id);
    if (!obj) return;
    const id = obj.data.id;
    const finish = () => {
      obj.dragging = false;
      for (const c of p.carry ?? []) c.obj.dragging = false;
    };
    if (this.cb.isOverHand(e.clientX, e.clientY)) {
      finish();
      this.cb.send({ type: 'toHand', id });
      return;
    }
    const pile = gp ? this.pileAt(gp) : null;
    if (pile && this.isEmperorData(obj.data)) {
      // the table rules say no (and explain why); put the Emperor back where it was
      finish();
      this.cb.send({ type: 'toPile', id, pile, where: 'top' });
      this.resync();
      return;
    }
    if (pile) {
      const where = pile === 'discard' && !e.shiftKey ? 'top' : await this.cb.askWhere(pile, e.clientX, e.clientY);
      finish();
      if (where) this.cb.send({ type: 'toPile', id, pile, where });
      else this.resync();
      return;
    }
    finish();
    const into = this.mergeTarget(obj.x, obj.z, obj.data);
    if (into) {
      this.cb.send({ type: 'merge', id, into: into.data.id });
      return;
    }
    const onto = this.attachTarget(obj.x, obj.z, obj.data);
    if (onto) {
      this.cb.send({ type: 'attach', id, to: onto.data.id });
      return;
    }
    const w = this.toWorld(obj.x, obj.z);
    const carry = (p.carry ?? []).map((c) => ({ id: c.obj.data.id, ...this.toWorld(c.obj.x, c.obj.z) }));
    this.cb.send({ type: 'move', id, x: w.x, z: w.z, carry });
  }

  /** A card was dragged out of the hand bar and let go over the table. */
  async dropFromHand(cardId: string, clientX: number, clientY: number, shift: boolean, free = false, dark = false) {
    const gp = this.groundPoint(clientX, clientY);
    this.highlightPile(null);
    if (!gp) return;
    const pile = this.pileAt(gp);
    if (pile && !this.handDropOk(pile)) {
      this.cb.notice(pile === 'discard' ? 'You can only discard cards from your hand during your own Overdraw Phase.' : 'Cards from your hand can’t be put back into your deck in a game.');
      return;
    }
    if (pile) {
      const where = pile === 'discard' && (!shift || this.handsOff()) ? 'top' : await this.cb.askWhere(pile, clientX, clientY);
      if (where) this.cb.send({ type: 'toPile', id: cardId, pile, where });
      return;
    }
    const into = shift ? null : this.mergeTarget(gp.x, gp.z, { num: this.handCardNum(cardId), tier: 1 });
    if (into) {
      this.cb.send({ type: 'merge', id: cardId, into: into.data.id, ...(free ? { free: true } : {}) });
      return;
    }
    const w = this.toWorld(gp.x, gp.z);
    const num = this.handCardNum(cardId);
    const def = num !== null ? CARD_DB.byNumber.get(num) : undefined;
    // a Biome onto a Biome: the one lying there has to be paid out of play first, and the player is asked
    const old = shift ? null : this.inTheWay(def, w);
    if (old && def) {
      const agreed = (await this.cb.askReplace?.(old.data, old.expired, def, clientX, clientY, { free, dark })) ?? false;
      if (!agreed) return;
    }
    const play = { type: 'play' as const, id: cardId, x: w.x, z: w.z, faceDown: shift, free, ...(dark ? { dark: true } : {}), ...(old ? { replace: old.data.id } : {}) };
    // a cost that is settled as the card is played: ask first (but not for a spot it can't go in anyway)
    const pledged = this.view?.freeUnit === cardId; // already paid for by another card: nothing to ask about its cost
    const asks = !!def && (!!def.forms || (!pledged && !!def.costFormula && !free && CONFIG.turn.payToPlay && (!!def.costFormula.choice || def.costFormula.uses.includes('unit'))));
    if (def && asks && !slotRefusal(def, { x: gp.x, z: gp.z }, this.placedCards(), !!this.view?.wideLanes)) {
      const minion = slotAt(w)?.slot.name === 'Minion'; // (dropped in the Minion slot: it can't be spread across lanes from there)
      const answer = await this.cb.askCost(cardId, def, clientX, clientY, { free, dark, minion });
      if (answer) this.cb.send({ ...play, ...answer });
      return;
    }
    this.cb.send(play);
  }

  private handCardNum(cardId: string) {
    return this.view?.hand.find((c) => c.id === cardId)?.num ?? null;
  }

  /** While a hand card is dragged over the table: light up the pile under it, or offer a merge. */
  hoverFromHand(clientX: number, clientY: number, cardId: string, shift: boolean, free = false, dark = false) {
    const gp = this.groundPoint(clientX, clientY);
    const over = gp ? this.pileAt(gp) : null;
    const pile = over && this.handDropOk(over) ? over : null; // (in a game the piles only take a card in your Overdraw Phase)
    this.highlightPile(pile);
    const into = gp && !over && !shift ? this.mergeTarget(gp.x, gp.z, { num: this.handCardNum(cardId), tier: 1 }) : null;
    const num = this.handCardNum(cardId);
    const def = num !== null ? CARD_DB.byNumber.get(num) : undefined;
    this.showSlots(num);
    // (Mystwing: your Support cards may go in your Spell slots)
    const slotDef = def && this.view?.supportInSpell && def.types.some((t) => ['biome', 'tome', 'trigger'].includes(t)) ? { ...def, extraSlots: [...def.extraSlots, 'spell'] } : def;
    const noEntry = gp && !over && !into && slotDef ? slotRefusal(slotDef, { x: gp.x, z: gp.z }, this.placedCards(), !!this.view?.wideLanes) : null;
    // the play-phase rule: every card in your Summon Phase, Spells in your Attack Phase too (a free play ignores it)
    const v = this.view;
    const pledgedHere = v?.freeUnit === cardId;
    const tooSoon = gp && !over && def && v && !free && !pledgedHere ? playWindowRefusal(def, v.turn, v.phase, v.you) : null;
    const old = gp && !over && !into && !shift && !noEntry ? this.inTheWay(def, this.toWorld(gp.x, gp.z)) : null;
    if (over && !pile) this.dragHint(over === 'discard' ? 'Cards go from your hand to your discard pile only in your Overdraw Phase' : 'Cards from your hand can’t go back into your deck', 'delete', clientX, clientY);
    else if (noEntry) this.dragHint(noEntry, 'delete', clientX, clientY);
    else if (tooSoon) this.dragHint(tooSoon.replace(/\.$/, ''), 'delete', clientX, clientY);
    else if (old) {
      // a Biome onto a Biome: what clearing the old one costs, on top of this card's own cost
      const od = CARD_DB.byNumber.get(old.data.num!)!;
      const removal = old.expired ? 0 : (tieredRemoval(od, old.data.tier) ?? 0);
      const mine = CONFIG.turn.payToPlay && !free ? (def?.cost ?? 0) : 0;
      this.dragHint(removal > 0 ? `${od.name} is in play: ${removal} ${od.removalDark ? 'Dark Gerzite' : 'Gerzite'} to remove it, then ${mine} to play this` : `${od.name}’s turns are over: it is cleared away, then this costs ${mine}`, 'info', clientX, clientY);
    }
    else if (into) this.dragHint(`Let go to merge → Tier ${roman(into.data.tier + 1)} (no extra cost)`, 'merge', clientX, clientY);
    else if (gp && !pile && this.view?.freeUnit === cardId) this.dragHint('Free: this is the Unit you chose. It goes down face-down', 'info', clientX, clientY);
    else if (gp && !pile && dark && def?.costDark) this.dragHint(`${def.name} is already summoned with Dark Gerzite: it can’t be a Darkgale summon`, 'delete', clientX, clientY);
    else if (gp && !pile && def?.forms && CONFIG.turn.payToPlay && !free) this.dragHint(`Costs ${[...new Set(def.forms.map((f) => f.cost))].join(' or ')} ${dark ? 'Dark Gerzite (Darkgale)' : 'Gerzite'}: you choose how it comes in as you put it down`, 'info', clientX, clientY);
    else if (gp && !pile && def?.costFormula && CONFIG.turn.payToPlay && !free) {
      // a cost worked out as the card is played: show it if the table already knows it
      const facts = this.view?.costFacts;
      const f = def.costFormula;
      const known = facts && !f.choice && !f.uses.includes('unit') ? priceOf(f, facts) : null;
      const usesDark = dark || (f.uses.includes('last_card') && !!facts?.lastDark);
      const coin = usesDark ? 'Dark Gerzite' : 'Gerzite';
      const me = this.view?.players[this.view.you];
      const have = (usesDark ? me?.darkGerzite : me?.gerzite) ?? 0;
      const times = dark ? darkgaleMultiplier(def) : 1;
      if (typeof known === 'string') this.dragHint(known, 'delete', clientX, clientY);
      else if (known === null) this.dragHint(`Cost: ${describeFormula(f)}. You set it as you put the card down`, 'info', clientX, clientY);
      else if (have < known * times) this.dragHint(`Costs ${known * times} ${coin} (${describeFormula(f)}): you have ${have}`, 'delete', clientX, clientY);
      else this.dragHint(`Costs ${known * times} ${coin} (${describeFormula(f)})`, 'info', clientX, clientY);
    }
    else if (gp && !pile && dark) {
      // a Darkgale summon: paid in Dark Gerzite (some card types pay more), and it gets the Darkgale token
      const me = this.view?.players[this.view.you];
      const cost = CONFIG.turn.payToPlay && !free ? (def?.cost ?? 0) * darkgaleMultiplier(def) : 0;
      const have = me?.darkGerzite ?? 0;
      const holds = CONFIG.zones.darkGerziteBar.slots;
      if (cost > holds) this.dragHint(`Too dear for a Darkgale summon: it would cost ${cost} Dark Gerzite, and the bar holds ${holds}`, 'delete', clientX, clientY);
      else if (cost <= 0) this.dragHint('Darkgale summon: no Dark Gerzite taken', 'info', clientX, clientY);
      else if (have < cost) this.dragHint(`Darkgale summon costs ${cost} Dark Gerzite: you have ${have}`, 'delete', clientX, clientY);
      else this.dragHint(`Darkgale summon: costs ${cost} Dark Gerzite`, 'info', clientX, clientY);
    } else if (gp && !pile && CONFIG.turn.payToPlay && (def?.cost ?? 0) > 0) {
      // what playing it here will cost, and whether you can afford it
      const me = this.view?.players[this.view.you];
      const coin = def!.costDark ? 'Dark Gerzite' : 'Gerzite'; // a few cards are summoned with Dark Gerzite by nature
      const have = (def!.costDark ? me?.darkGerzite : me?.gerzite) ?? 0;
      const cost = Math.max(0, def!.cost! + (this.view?.costChange[cardId] ?? 0)); // (with what the cards in play change it by)
      if (free) this.dragHint(`Free play: no ${coin} taken`, 'info', clientX, clientY);
      else if (have < cost) this.dragHint(`Costs ${cost} ${coin}: you have ${have}`, 'delete', clientX, clientY);
      else this.dragHint(`Costs ${cost} ${coin}`, 'info', clientX, clientY);
    } else this.dragHint(null);
  }

  /** The hand drag ended (dropped or cancelled): clear any highlight or label. */
  endHandDrag() {
    this.highlightPile(null);
    this.dragHint(null);
    this.showSlots(null);
  }

  private updateHover(clientX: number, clientY: number) {
    const pick = this.pick(clientX, clientY);
    this.hovered = pick;
    let cursor = '';
    if (pick?.kind === 'card') {
      const c = this.cards.get(pick.id);
      cursor = c && this.canDragCard(c) ? 'grab' : c && (this.actsOnClick(c) || (this.canControlCard(c.data) && !!this.view?.combat[c.data.id])) ? 'pointer' : 'zoom-in';
    } else if (pick?.kind === 'token') cursor = this.tokens.get(pick.id) && this.expiredUnder(this.tokens.get(pick.id)!) ? 'pointer' : this.handsOff() ? '' : 'grab';
    else if (pick?.kind === 'pile') cursor = 'pointer';
    else if (this.placing) cursor = 'copy';
    this.arrowHover = this.myArrowAt(clientX, clientY) !== null;
    if (this.arrowHover) cursor = 'pointer';
    this.renderer.domElement.style.cursor = cursor;
    this.lastMouse = { x: clientX, y: clientY };
    this.removeHint();
    this.hoverTip();
    this.syncChip(pick);
    // an arrow you drew is under the cursor: a click takes it away
    if (this.arrowHover) {
      this.hoverTip(false);
      this.dragHint('Click to take your arrow away', 'info', clientX, clientY);
    }
  }

  flipHovered() {
    const h = this.hovered;
    if (h?.kind !== 'card') return;
    const c = this.cards.get(h.id);
    if (c && this.canControlCard(c.data) && !this.handsOff()) this.cb.send({ type: 'flip', id: h.id });
  }

  private resync() {
    if (this.view) this.update(this.view);
  }

  // ======================= camera & frame loop =======================

  topDown() {
    this.rig.topDown();
  }
  resetCamera() {
    this.rig.reset();
  }

  // ---- a panel over the left of the screen (the tutorial's hints) that your side of the table is kept clear of
  private clearOf: HTMLElement | null = null;
  private inset = { shift: 0, zoom: 1 };
  private fitBox = new THREE.Box3();
  private fitPt = new THREE.Vector3();
  /** While `panel` shows, the view slides right (and steps back a little when the screen is narrow) so the
   *  piles and bars beside the table aren't hidden under it; null lets the view settle back. */
  keepClearOf(panel: HTMLElement | null) {
    this.clearOf = panel;
  }
  private fitBeside(dt: number) {
    // (on a phone the tutorial's hints sit over the table instead: there is no room beside them to slide into)
    const small = this.host.clientWidth <= 720 || this.host.clientHeight <= 500;
    const panel = !small && this.clearOf && !this.clearOf.hidden && this.clearOf.isConnected ? this.clearOf : null;
    if (!panel && this.inset.shift === 0 && this.inset.zoom === 1) return;
    const cam = this.rig.camera;
    const w = this.host.clientWidth || 1;
    const h = this.host.clientHeight || 1;
    let want = { shift: 0, zoom: 1 };
    if (panel) {
      // where everything at the sides of the table lands with the plain view
      cam.zoom = 1;
      cam.clearViewOffset();
      cam.updateProjectionMatrix();
      cam.updateMatrixWorld();
      const host = this.host.getBoundingClientRect();
      const p = panel.getBoundingClientRect();
      const right = p.right - host.left, top = p.top - host.top, bottom = p.bottom - host.top;
      let minBeside = Infinity, maxAll = -Infinity;
      const things: THREE.Object3D[] = [...this.piles.map((pl) => pl.group), ...this.barFrames.map((b) => b.frame)];
      for (const obj of things) {
        this.fitBox.setFromObject(obj);
        if (this.fitBox.isEmpty()) continue;
        let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
        for (let i = 0; i < 8; i++) {
          const v = this.fitPt.set(i & 1 ? this.fitBox.max.x : this.fitBox.min.x, i & 2 ? this.fitBox.max.y : this.fitBox.min.y, i & 4 ? this.fitBox.max.z : this.fitBox.min.z).project(cam);
          if (v.z > 1) continue; // behind the camera
          const sx = ((v.x + 1) / 2) * w, sy = ((1 - v.y) / 2) * h;
          x0 = Math.min(x0, sx); x1 = Math.max(x1, sx); y0 = Math.min(y0, sy); y1 = Math.max(y1, sy);
        }
        if (x0 === Infinity) continue;
        maxAll = Math.max(maxAll, x1);
        if (y1 > top && y0 < bottom) minBeside = Math.min(minBeside, x0);
      }
      const gap = 12;
      if (minBeside !== Infinity && minBeside < right + gap) {
        const cx = w / 2;
        // step back only as far as it takes for the far edge to stay on screen, and never much
        const zoom = Math.max(0.7, Math.min(1, (w - 2 * gap - right) / Math.max(1, maxAll - minBeside)));
        want = { zoom, shift: right + gap - (cx + (minBeside - cx) * zoom) };
      }
    }
    const k = 1 - Math.exp(-dt * 6);
    this.inset.shift += (want.shift - this.inset.shift) * k;
    this.inset.zoom += (want.zoom - this.inset.zoom) * k;
    if (Math.abs(want.shift - this.inset.shift) < 0.5) this.inset.shift = want.shift;
    if (Math.abs(want.zoom - this.inset.zoom) < 0.001) this.inset.zoom = want.zoom;
    cam.zoom = this.inset.zoom;
    this.applyViewOffset(w, h);
    cam.updateProjectionMatrix();
  }

  /** A phone held upright: the table is drawn this many pixels higher, into the empty space above it (the
   *  turn bar and the hand take the bottom of the screen). */
  private lift = 0;
  private applyViewOffset(w: number, h: number) {
    const cam = this.rig.camera;
    if (this.inset.shift || this.lift) cam.setViewOffset(w, h, -this.inset.shift, this.lift, w, h);
    else cam.clearViewOffset();
  }

  private resize() {
    const w = this.host.clientWidth || 1;
    const h = this.host.clientHeight || 1;
    this.renderer.setSize(w, h);
    this.labels.setSize(w, h);
    this.rig.camera.aspect = w / h;
    // A narrow screen (a phone held upright) would cut off the ends of the table: widen the view so it shows
    // about as much of the table's width as a computer screen does
    const REF_ASPECT = 1.5;
    const base = CONFIG.camera.fov;
    const fov = w / h >= REF_ASPECT ? base : (2 * Math.atan(Math.tan((base * Math.PI) / 360) * (REF_ASPECT / (w / h))) * 180) / Math.PI;
    this.rig.camera.fov = Math.min(112, fov);
    this.lift = w / h < 0.8 ? Math.round(h * 0.07) : h <= 500 ? Math.round(h * 0.07) : 0; // (a phone on its side too: its own front row would sit under the turn bar)
    this.applyViewOffset(w, h);
    this.rig.camera.updateProjectionMatrix();
  }

  private loop = () => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);
    const now = performance.now();
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    this.rig.update(dt);
    this.fitBeside(dt);
    this.animate(dt, now / 1000);
    this.effects.update();
    this.markers.update(now / 1000);
    this.fog.show(this.fogsWanted());
    this.fog.update(now / 1000, dt);
    this.clocks.update(now / 1000);
    this.axes.update(now / 1000);
    this.attackFx.update(now / 1000);
    this.looks.update(now / 1000);
    this.updateSlotGlows(now / 1000, dt);
    this.updateReachGlows(now / 1000, dt);
    if (this.chipEl) this.placeChip();
    this.renderer.render(this.scene, this.rig.camera);
    this.labels.render(this.scene, this.rig.camera);
  };

  private spinQ = new THREE.Quaternion();

  /** Overtime: the room warms, the table's rim and side edges glow like embers, and sparks drift up around it. */
  private animateMood(dt: number, time: number) {
    const want = this.view?.overtime ? 1 : 0;
    if (this.mood === want && want === 0) return;
    this.mood += (want - this.mood) * Math.min(1, dt * 0.9);
    if (Math.abs(want - this.mood) < 0.002) this.mood = want;
    const m = this.mood;
    const ember = this.moodEmber.set(CONFIG.overtime.ember);
    const room = this.moodRoom.copy(this.roomColor).lerp(this.moodWarm.copy(this.roomColor).lerp(ember, 0.16).multiplyScalar(0.82), m);
    (this.scene.background as THREE.Color).copy(room);
    this.scene.fog?.color.copy(room);
    const breath = 0.8 + 0.2 * Math.sin(time * 1.1);
    this.rimMat?.emissive.copy(ember).multiplyScalar(0.3 * m * breath);
    for (const band of this.emberBands) {
      (band.material as THREE.MeshBasicMaterial).opacity = 0.34 * m * breath;
      band.visible = m > 0.002;
    }
    this.markers.setMood(m, CONFIG.overtime.ember, CONFIG.table);
  }
  private moodEmber = new THREE.Color();
  private moodRoom = new THREE.Color();
  private moodWarm = new THREE.Color();

  private animate(dt: number, time: number) {
    this.animateMood(dt, time);
    this.animateMotes(time);
    this.animateDying(time);
    this.animateFlights(time);
    this.animateTrails(time);
    this.snareScenes(time);
    this.animateBars(time);
    const k = 1 - Math.exp(-dt * 16);
    const hoveredId = this.hovered?.kind === 'card' ? this.hovered.id : null;
    // which card is each token sitting on? (Flying and Ground tokens change how that card behaves)
    for (const c of this.cards.values()) {
      c.tokenFly = c.tokenGround = false;
      c.damage = c.overheal = c.decay = c.gained = 0;
      c.quiet = false;
      c.passiveOff = false;
      c.expired = false;
    }
    for (const t of this.tokens.values()) {
      t.on = this.cardAt(t.x, t.z);
      if (t.on && t.def && CONFIG.abilityEffects.quietedBy.includes(t.def.effect) && t.data.from !== t.on.data.id) t.on.quiet = true; // a Lock a card put on itself doesn't quiet it
      if (t.on && t.def && CONFIG.passiveStoppedBy.includes(t.def.effect)) t.on.passiveOff = true;
      if (t.on && t.def?.effect === CONFIG.turn.lifetime.expiredToken) t.on.expired = true;
      if (t.on && t.def && countsAgainstHealth(t.def.effect)) {
        t.on.damage += t.data.value;
        if (t.def.effect === CONFIG.tokenRules.spDecayToken) t.on.decay += t.data.value;
      }
      else if (t.on && t.def?.effect === 'overheal') t.on.overheal += t.data.value;
      else if (t.on && t.def?.effect === CONFIG.tokenRules.healthToken) t.on.gained += t.data.value;
      const recipe = t.def ? TOKEN_RECIPES[t.def.effect] : undefined;
      if (t.on && recipe?.fly) t.on.tokenFly = true;
      if (t.on && recipe?.ground) t.on.tokenGround = true;
    }
    this.watchEndings();
    this.syncSpellScenes(time);
    for (const obj of this.cards.values()) {
      const r = obj.root;
      const lift = obj.dragging ? 2.2 : obj.data.id === hoveredId ? 0.25 : 0;
      r.position.x += (obj.x - r.position.x) * k;
      r.position.z += (obj.z - r.position.z) * k;
      const flipTarget = obj.data.faceUp ? 0 : Math.PI;
      obj.flipper.rotation.z += (flipTarget - obj.flipper.rotation.z) * k;
      const flipLift = Math.sin(obj.flipper.rotation.z) * 2;
      // a summoned unit attached to a card rides on that card: up in the air with a Flying one (Skeleton Dropship),
      // never down on the table underneath it
      const parent = obj.data.attachedTo ? this.cards.get(obj.data.attachedTo) : undefined;
      // flying cards float above the table, bobbing and tilting a little (an attached one floats above its card)
      const airborne = (obj.flying || obj.tokenFly) && !obj.tokenGround && !obj.data.grounded; // (Wingwrap holds a Flying Troop down)
      if (airborne) this.ensureShade(obj);
      obj.hover += ((airborne ? 1 : 0) - obj.hover) * Math.min(1, dt * 5);
      const bob = Math.sin(time * 1.7 + obj.phase) * CONFIG.flying.bob;
      const air = obj.hover * (CONFIG.flying.height + bob);
      let lean = 0;
      if (obj.slam && !obj.dragging) {
        // Entrance: up (only for a card flipped where it lies), then down faster and faster, then one small hop
        const E = CONFIG.abilityEffects.entrance;
        const sl = obj.slam;
        const t = time - sl.t0;
        const base = obj.y + air;
        if (t < sl.rise) {
          const q = t / sl.rise;
          r.position.y = sl.y0 + (base + E.height * 0.7 - sl.y0) * (1 - (1 - q) ** 2);
        } else if (t < sl.rise + E.fall) {
          const q = (t - sl.rise) / E.fall;
          r.position.y = base + E.height * (sl.rise ? 0.7 : 1) * (1 - q * q);
          lean = E.tilt * (1 - q);
        } else {
          if (!sl.hit) {
            sl.hit = true;
            this.effects.play('entrance', r); // the impact, over whatever its rarity effect is doing
          }
          const q = (t - sl.rise - E.fall) / E.settle;
          if (q >= 1) {
            obj.slam = null;
            delete r.userData.fxY;
            r.position.y = base;
          } else r.position.y = base + E.bounce * Math.sin(Math.PI * q);
        }
      } else if (obj.spiritRise && !obj.dragging) {
        // a Spirit Soldier: small and pale, it rises out of the shards like a soul, hangs a moment, then floats back
        // down into its lane, growing to full size as it comes
        const R = SPIRIT_RISE;
        const t = time - obj.spiritRise.t0;
        const base = obj.y + air;
        let y = base, sc = 1;
        if (t < 0) { y = base; sc = 0.01; }
        else if (t < R.up) { const q = t / R.up; const e = 1 - (1 - q) ** 2; y = base + R.height * e; sc = R.small * Math.min(1, q * 3); }
        else if (t < R.up + R.hang) { y = base + R.height + Math.sin(((t - R.up) / R.hang) * Math.PI) * 0.4; sc = R.small; }
        else if (t < R.up + R.hang + R.down) { const q = (t - R.up - R.hang) / R.down; const e = q * q * (3 - 2 * q); y = base + R.height * (1 - e); sc = R.small + (1 - R.small) * e; lean = Math.sin(q * Math.PI * 2) * 0.08 * (1 - q); }
        else { delete obj.spiritRise; delete r.userData.fxY; }
        r.position.y = y;
        r.userData.fxY = obj.y;
        r.userData.spiritScale = obj.spiritRise ? sc : undefined;
      } else if (obj.emerge && !obj.dragging) {
        // up out of the table: it comes up steeply tilted, so it breaks the surface a little at a time, and levels out
        const M = CONFIG.combat.emerge;
        // (still waiting for its picture: it stays out of sight under the table, for 3 seconds at most)
        if (obj.emerge.waiting && (this.faceReady(obj) || time - obj.emerge.t0 > 3)) {
          obj.emerge.waiting = false;
          obj.emerge.t0 = time;
          this.emergeLook(obj);
        }
        const q = obj.emerge.waiting ? 0 : Math.min(1, (time - obj.emerge.t0) / M.seconds);
        const ease = 1 - (1 - q) ** 3;
        r.position.y = obj.y + air - M.depth * (1 - ease);
        lean = M.tilt * (1 - ease) + Math.sin(q * Math.PI * 3) * 0.05 * (1 - q);
        r.userData.fxY = obj.y;
        if (q >= 1) { delete obj.emerge; delete r.userData.fxY; }
      } else {
        if (obj.slam) {
          obj.slam = null; // picked up in mid-air: the drop is called off
          delete r.userData.fxY;
        }
        // stuck to its card, never under it; a Flying one hovers above it (a little lower when shrunk, still clear of it)
        if (parent) r.position.y = Math.max(obj.y, parent.root.position.y + 0.06) + lift + flipLift + air * Math.max(0.6, obj.data.scale ?? 1);
        else r.position.y += (obj.y + lift + flipLift + air - r.position.y) * k;
      }
      r.rotation.y += (obj.yaw - r.rotation.y) * k;
      // shrunk: sharing a lane, or attached to the bottom edge of a card
      const size = (obj.data.scale ?? 1) * (r.userData.spiritScale ?? 1);
      if (r.userData.spiritScale !== undefined) r.scale.set(size, 1, size); // (a Spirit rising: its size is set outright)
      else if (Math.abs(r.scale.x - size) > 1e-4) {
        const next = r.scale.x + (size - r.scale.x) * k;
        r.scale.set(next, 1, next); // flat: it shrinks across the table, not in thickness, so it still lies on top
      }
      r.rotation.x = obj.hover * Math.sin(time * 1.3 + obj.phase) * 0.05 + lean;
      r.rotation.z = obj.hover * Math.cos(time * 1.1 + obj.phase) * 0.04;
      // a 'rumble' effect playing on the card (the Self-Destructing Tome): it jolts in place, a little shove and a
      // little twist each frame, by as much as the effect says. It stays flat, so nothing under it shows through.
      const rumble: number = r.userData.rumble ?? 0;
      if (rumble > 0) {
        const R = CONFIG.abilityEffects.rumble;
        obj.flipper.position.set(Math.sin(time * 71) * R.shift * rumble, 0, Math.sin(time * 53 + 1.7) * R.shift * rumble);
        obj.flipper.rotation.y = Math.sin(time * 61 + 0.4) * R.turn * rumble;
      } else if (obj.flipper.position.x || obj.flipper.position.z || obj.flipper.rotation.y) {
        obj.flipper.position.set(0, 0, 0);
        obj.flipper.rotation.y = 0;
      }
      if (obj.shade) {
        // stays on the table while the card floats (on top of its card, for one attached to a card)
        obj.shade.position.y = (parent ? parent.root.position.y + 0.03 : 0.022) - r.position.y;
        (obj.shade.material as THREE.MeshBasicMaterial).opacity = 0.55 * obj.hover;
        obj.shade.visible = obj.hover > 0.01;
      }
      this.syncDoom(obj, time);
      // cards tagged "passive" pulse softly every few seconds, but only during their owner's turn
      // queued one-off effects (Entrance, a Trigger's reveal pulse) start once the effect before them has finished
      const gap = CONFIG.abilityEffects.entranceDelay;
      if (obj.fxQueue.length && time >= obj.fxUntil + gap) this.playOn(obj, obj.fxQueue.shift()!);
      const busy = obj.fxQueue.length > 0 || time < obj.fxUntil + gap; // a one-off effect is playing, or about to
      // (one whose Passive a status has switched off still pulses, in deep red, to show it is off)
      if (this.view?.turn === obj.data.owner && !obj.dragging && this.hasAbility(obj.data, 'passive')) {
        if (!obj.passiveNext) obj.passiveNext = Math.max(time, obj.fxUntil) + 0.8 + Math.random() * 1.5;
        else if (time >= obj.passiveNext) {
          // never over the card's rarity or Entrance effect (or a scene of its own): the pulse waits until they are done
          // (a card whose own scene never stops, like a Calling Pillar's beacon, isn't made to wait for it: its
          // Passive pulse plays over the scene)
          const steady = (this.ambientOf(obj)?.every[0] ?? 0) < 0;
          if (busy || (!steady && time < obj.ambientUntil)) obj.passiveNext = Math.max(time, obj.fxUntil, steady ? 0 : obj.ambientUntil) + 0.8;
          else {
            this.playOn(obj, obj.passiveOff ? 'passiveStopped' : 'passive');
            obj.passiveNext = time + CONFIG.abilityEffects.passiveEvery;
          }
        }
      } else obj.passiveNext = 0;
      // an active Biome or Tome: every so often, a small scene of its own or of its affiliation plays above it.
      // A scene doesn't count as busy for the next one, so a negative gap lets them overlap and never stop.
      this.bellFlashes(obj, time);
      this.laneScenes(obj, time);
      this.guardScene(obj, time);
      const scene = this.ambientOf(obj);
      if (scene && !obj.dragging) {
        const [least, most] = scene.every;
        if (!obj.ambientNext) obj.ambientNext = Math.max(time, obj.fxUntil) + 1.2 + Math.random() * 2.5; // soon after it lands
        else if (time >= obj.ambientNext) {
          if (busy) obj.ambientNext = Math.max(time, obj.fxUntil) + 1; // never over another effect on the card
          else {
            const length = this.effects.lengthOf(scene.effect);
            this.effects.play(scene.effect, obj.root);
            if (obj.data.wyrm && scene.effect !== 'aura-duned') this.effects.play('aura-duned', obj.root); // (lending to Duned cards: the sand blows along with its own scene)
            obj.ambientUntil = time + length;
            obj.ambientNext = time + Math.max(0.3, length + least + Math.random() * (most - least));
          }
        }
      } else obj.ambientNext = 0;
      // a Trigger set face-down: a beam shoots up as it lands, then a slim one stands on it while it waits
      const set = CONFIG.abilityEffects.setAura;
      if (set.enabled && obj.data.armed) {
        if (!obj.armedNext) {
          this.effects.play(set.effect, obj.root);
          obj.armedNext = time + Math.max(0.1, this.effects.lengthOf(set.effect) * 0.55); // the standing beam fades in as the first one thins out
        } else if (time >= obj.armedNext) {
          this.effects.play(set.loop, obj.root);
          obj.armedNext = time + Math.max(0.5, this.effects.lengthOf(set.loop) * (1 - BEAM_FADE));
        }
      } else if (obj.armedNext) {
        obj.armedNext = 0;
        this.effects.stop(obj.root, [set.effect, set.loop]); // flipped face-up (or gone): the beam goes out at once
      }
      if (obj.aura) {
        const pile = this.view?.players[obj.data.owner]?.discard.length ?? 0;
        obj.aura.update(time, pile, obj.data.faceUp && !obj.quiet && Math.abs(obj.flipper.rotation.z) < 0.4);
      }
    }
    // tokens sitting on a flying card float with it instead of hiding underneath
    const chains = new Map<string, TokenObj[]>();
    const pairs = new Map<number, TokenObj[]>(); // Link counters by their number
    for (const t of this.tokens.values()) {
      const m = t.mesh.position;
      if (t.wait) {
        if (time < t.wait) continue; // still inside the card that is about to burst
        t.wait = 0;
        t.mesh.visible = true;
      }
      const ride = t.on && (t.on.hover > 0.01 || t.on.data.attachedTo) ? t.on.root.position.y - 0.03 : 0;
      const roll = t.die?.anim;
      if (t.flight && !t.dragging) {
        // Gerzite on its way to the bar: an arc from where it came from, a trail of light behind it
        const fl = t.flight;
        const u = Math.max(0, Math.min(1, (time - fl.t0) / fl.dur));
        const e = u * u * (3 - 2 * u);
        const to = new THREE.Vector3(t.x, t.y, t.z);
        m.lerpVectors(fl.from, to, e);
        m.y += Math.sin(Math.PI * u) * CONFIG.looks.gainFlight.arc;
        if (fl.trail && u > 0 && u < 1 && time - fl.last > 0.015) { fl.last = time; this.trailDot(m, fl.trail); }
        if (u >= 1) t.flight = undefined;
      } else if (t.die && roll && !t.dragging) {
        // a rolled die hops to its landing spot (one big bounce, one small) while it tumbles to a stop
        const u = Math.min(1, (time - roll.t0) / roll.dur);
        const e = 1 - Math.pow(1 - u, 2.4);
        const rest = t.y + ride;
        m.x = roll.from.x + (t.x - roll.from.x) * e;
        m.z = roll.from.z + (t.z - roll.from.z) * e;
        const hop = u < 0.62 ? Math.sin((Math.PI * u) / 0.62) * roll.height : Math.sin((Math.PI * (u - 0.62)) / 0.38) * roll.height * 0.25;
        m.y = rest + (roll.from.y - rest) * (1 - e) + hop;
        t.die.cube.quaternion.copy(t.die.target).multiply(this.spinQ.setFromAxisAngle(roll.axis, (1 - e) * roll.turns * Math.PI * 2));
        if (u >= 1) t.die.anim = null;
      } else {
        if (t.die) {
          t.die.anim = null;
          t.die.cube.quaternion.slerp(t.die.target, k);
          if (t.die.fadeAt && time >= t.die.fadeAt && !t.dragging) {
            // the cure die fades out over a moment, then is out of sight (and can't be picked up) until thrown again
            const q = (time - t.die.fadeAt) / CONFIG.turn.cureDie.fadeTime;
            if (q >= 1) {
              t.die.fadeAt = 0;
              this.setDieFaded(t, true);
            } else {
              for (const mat of t.die.cube.material as THREE.MeshLambertMaterial[]) {
                mat.transparent = true;
                mat.opacity = 1 - q;
              }
            }
          }
        }
        m.x += (t.x - m.x) * k;
        m.z += (t.z - m.z) * k;
        m.y += (t.y + ride + (t.dragging ? 2.4 : 0) - m.y) * (ride > 0 ? 1 : k);
      }
      if (t.fx) {
        // an Ambush token goes still while its card is held down by one of those tokens
        t.fx.group.visible = !(t.on?.quiet && t.def?.effect === CONFIG.tokenRules.ambushToken);
        const fullAt = t.def?.fullAt ?? null;
        t.fx.update({
          time,
          value: t.data.value,
          k: fullAt ? Math.max(0, Math.min(1, t.data.value / fullAt)) : 1,
          card: t.on?.root ?? null,
        });
      }
      if (t.def?.effect === CONFIG.tokenRules.linkToken) pairs.set(t.data.value, [...(pairs.get(t.data.value) ?? []), t]);
      if (t.def && TOKEN_RECIPES[t.def.effect]?.link) {
        // a Lanespanner's tokens are joined unit to unit; any others, by whoever placed them
        const by = t.data.span ?? `seat${t.data.by ?? -1}`;
        chains.set(by, [...(chains.get(by) ?? []), t]);
      }
      // a token whose effect just happened swells for a moment
      const since = t.pulse ? time - t.pulse : 9;
      // …and one that is waiting for its cure roll (Burn) keeps breathing, so the card it is on is easy to find
      const waiting = t.data.cure !== undefined ? 1 + 0.18 * (0.5 + 0.5 * Math.sin(time * 5)) : 1;
      const swell = since < 0.7 ? 1 + 0.4 * Math.sin((Math.PI * since) / 0.7) : waiting;
      // a token on a shrunk card is shrunk to match (but full size while it is being dragged)
      const small = t.dragging ? 1 : (t.data.scale ?? 1);
      const scale = t.mesh.scale.x + ((t.offTable ? 0.55 : swell * small) - t.mesh.scale.x) * (since < 0.7 ? 1 : k);
      t.mesh.scale.setScalar(scale);
      if (t.shadow) {
        // keep the shadow on the table, drifting further off as the token lifts
        const lift = Math.max(0, m.y - ride - 0.3);
        t.shadow.position.set(0.18 + lift * 0.35, 0.33 + ride - m.y, 0.22 + lift * 0.45);
        (t.shadow.material as THREE.MeshBasicMaterial).opacity = Math.max(0.18, 0.5 - lift * 0.08);
      }
    }
    // Lanespanner: join each player's tokens in the order they were placed
    const order = (t: TokenObj) => Number(t.data.id.replace(/\D/g, ''));
    this.linkLines.update(
      // (a Lanespanner's run of units left to right, so the line doesn't double back)
      [...chains.values()].map((list) => list.sort((a, b) => (a.data.span ? a.x - b.x : order(a) - order(b))).map((t) => t.mesh.position)),
      time,
    );
    // Link counters: a green line joins the two (or more) with the same number
    this.pairLines.update([...pairs.values()].map((list) => list.sort((a, b) => order(a) - order(b)).map((t) => t.mesh.position)), time);
    // a discard pile that was waiting for a sacrificed card to burst now shows it
    const released = this.piles.filter((p) => p.holdUntil && time >= p.holdUntil);
    for (const p of released) p.holdUntil = 0;
    if (released.length) this.resync();
    for (const p of this.piles) {
      const glowMat = p.glow.material as THREE.MeshBasicMaterial;
      const target = p.searching ? 0.35 + 0.25 * Math.sin(time * 5) : p.hover ? 0.4 : 0;
      glowMat.opacity += (target - glowMat.opacity) * k;
      if (p.shuffleT > 0) {
        p.shuffleT = Math.max(0, p.shuffleT - dt);
        p.jumpT = Math.max(0, p.jumpT - dt);
        // (The Remaining's move: a slow, uneasy sway instead of the quick riffle)
        const wobble = (p.jumpT > 0 ? Math.sin(time * 9) * 0.3 : Math.sin(time * 38) * 0.18) * Math.min(1, p.shuffleT);
        p.box.rotation.y = wobble;
        p.top.rotation.y = -wobble * 1.5;
        p.top.position.x = Math.sin(time * 27) * 0.9 * Math.min(1, p.shuffleT);
        if (p.shuffleT === 0) {
          p.box.rotation.y = p.top.rotation.y = 0;
          p.top.position.x = 0;
          this.pileLabel(p, this.pileCount(p));
        }
      }
    }
  }

  private pileCount(p: PileObj) {
    const pv = this.view?.players[p.seat];
    if (!pv) return 0;
    return p.pile === 'deck' ? pv.deckCount : pv.discard.length;
  }

  dispose() {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.effects.dispose();
    this.markers.dispose();
    this.fog.dispose();
    this.axes.dispose();
    this.spellScenes.dispose();
    this.clocks.dispose();
    this.attackFx.dispose();
    this.looks.dispose();
    this.hideChip();
    this.linkLines.dispose();
    this.pairLines.dispose();
    for (const t of this.tokens.values()) t.fx?.dispose();
    for (const d of this.dying) this.scene.remove(d.obj.root);
    this.dying = [];
    this.dragHint(null);
    this.hoverTip(false);
    this.endMarquee();
    this.resizeObs.disconnect();
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.renderer.dispose();
    for (const t of this.textures.values()) t.dispose();
    this.renderer.domElement.remove();
    this.labels.domElement.remove();
  }
}
